import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { DashboardContext } from '../context/DashboardContext'
import useAuthStore from '../store/auth-store'
import { resolveIanaTz } from '../lib/tz'

// ─── Persisted sidebar selection ──────────────────────────────────────────────
// Sidebar parameter checkboxes survive page reload AND logout/login on the
// same browser.  Scoped by (userId, projectId) so different users sharing a
// browser don't see each other's selections, and switching projects doesn't
// leak selections across them.  Storage isn't cleared by sessionExpiry's
// clearAuthStorage() — only auth_token / refresh_token / auth_user are
// removed on logout, so wq_selparams_* keys persist as required.
const SELPARAMS_KEY = (userId, projectId) =>
  `wq_selparams_u${userId || 'anon'}_p${projectId || 'noproject'}`

function loadSelParams(userId, projectId) {
  if (!userId || !projectId) return null
  try {
    const raw = localStorage.getItem(SELPARAMS_KEY(userId, projectId))
    if (!raw) return null
    const v = JSON.parse(raw)
    if (!v || !Array.isArray(v.selectedParameters)) return null
    return {
      selectedParameters: v.selectedParameters,
      checkedItems: v.checkedItems && typeof v.checkedItems === 'object'
        ? v.checkedItems
        : Object.fromEntries(v.selectedParameters.map(p => [p.id, true])),
    }
  } catch { return null }
}

function saveSelParams(userId, projectId, selectedParameters, checkedItems) {
  if (!userId || !projectId) return
  try {
    localStorage.setItem(
      SELPARAMS_KEY(userId, projectId),
      JSON.stringify({ selectedParameters, checkedItems })
    )
  } catch { /* quota / private-mode — best effort */ }
}
import { ChevronDown, ChevronRight, Settings, Bell, Eye, Edit2, Search, Loader, GripVertical, X, Plus, RefreshCw } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { dashboardApi } from '../lib/dashboardApi'
import widgetApi from '../lib/widgetApi'
import { NoActiveProject } from '../components/NoActiveProject'
import { DynamicGraph } from '../components/DynamicGraph'
import { CurrentConditionsView } from '../components/CurrentConditionsView'
import { SystemPerformanceView } from '../components/SystemPerformanceView'
import { DynamicGraphCard } from '../components/DynamicGraphCard'
import { TemplateDashboardGrid } from '../components/dashboard/TemplateDashboardGrid'
import PresentationSettingsModal from '../components/dashboard/PresentationSettingsModal'
import ConfigDashboardsModal from '../components/dashboard/ConfigDashboardsModal'
import DataAnalyticsView from '../components/dashboard/DataAnalytics/DataAnalyticsView'
import { ParameterConfigModal } from '../components/dashboard/ParameterConfigModal'
import AllDataView from '../components/dashboard/AllDataView'
import DataLoggerTab from './live/DataLoggerTab'
import {
  getUnassignedDevicesStorageKey,
  getUnassignedDevicesFromStorage,
  saveUnassignedDevicesToStorage,
  createUnassignedDevice,
  extractUnassignedDevices
} from '../utils/unassignedDeviceHelpers'
import { SecureInput, SecureTextarea } from '../components/SecureInput'
import { scanInput } from '../lib/inputSecurity'

// Per-chart line colors for the Parameter Comparison view (cycled by index)
const PARAM_CHART_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#3b82f6', '#ef4444', '#06b6d4']

export function DashboardPage() {
    const { projectId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    // userId is read once at mount for the selection-persistence scope key.
    // auth-store hydrates synchronously from localStorage (`auth_user`), so
    // this is available before useState initializers run.
    const persistedUserId = useAuthStore(s => s.user?.id)
    const [projects, setProjects] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(true)
    const [currentProject, setCurrentProject] = useState(null)
    const [templates, setTemplates] = useState([])
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    // Canonical backend-defined templates for the Add Dashboard popup
    // (separate from `templates`, which is the project's full list and
    // includes customer-added clones).
    const [predefinedTemplates, setPredefinedTemplates] = useState([])
    const [loadingPredefinedTemplates, setLoadingPredefinedTemplates] = useState(false)
    
    const [expandedMenus, setExpandedMenus] = useState(['land-based', 'pond-buoy'])
    const [activeTab, setActiveTab] = useState(() => {
        try { return sessionStorage.getItem('activeTab') || 'Dashboard' } catch { return 'Dashboard' }
    })
    // Hydrate from localStorage so the sidebar checkbox state survives
    // page reload and logout/login (same browser + same user).  Scoped by
    // (userId, projectId) — see SELPARAMS_KEY at the top of file.
    const [checkedItems, setCheckedItems] = useState(
      () => loadSelParams(persistedUserId, projectId)?.checkedItems || {}
    )
    const [searchQuery, setSearchQuery] = useState('')
    const [hoveredTab, setHoveredTab] = useState(null)
    const [liveMenuFocused, setLiveMenuFocused] = useState(false)
    const [currentMenuIndex, setCurrentMenuIndex] = useState(() => {
        try {
            const saved = parseInt(sessionStorage.getItem('currentMenuIndex'), 10)
            return Number.isFinite(saved) && saved >= 0 ? saved : 0
        } catch { return 0 }
    })

    // Persist the active dashboard tab index so a page reload stays on the same
    // dashboard (Overview / Parameter Comparison / etc.) instead of resetting.
    useEffect(() => {
        try { sessionStorage.setItem('currentMenuIndex', String(currentMenuIndex)) } catch {}
    }, [currentMenuIndex])

    const [currentDataAnalyticsIndex, setCurrentDataAnalyticsIndex] = useState(0)
    const [currentReportIndex, setCurrentReportIndex] = useState(0)
    const [currentSettingsIndex, setCurrentSettingsIndex] = useState(0)
    // Settings → Users (live data from the project users API)
    const [settingsUsers, setSettingsUsers] = useState([])
    const [settingsUsersLoading, setSettingsUsersLoading] = useState(false)
    const [settingsUsersError, setSettingsUsersError]   = useState('')
    const [settingsUsersBusy, setSettingsUsersBusy]     = useState(false)
    const [newUserEmail, setNewUserEmail] = useState('')
    const [newUserAccess, setNewUserAccess] = useState('Admin')
    const [currentLiveIndex, setCurrentLiveIndex] = useState(0)
    const liveMenuItemsRefs = useRef([])
    const gridRef = useRef(null)
    const galleryFetchedProjectRef = useRef(null)
    const [gridContainerWidth, setGridContainerWidth] = useState(0)
    const gridRoRef = useRef(null)
    const gridWrapperRefCallback = useCallback(node => {
      if (gridRoRef.current) { gridRoRef.current.disconnect(); gridRoRef.current = null }
      if (!node) return
      const measure = () => {
        const w = Math.floor(node.getBoundingClientRect().width)
        console.log('[DashboardPage] gridWrapperRefCallback measured width:', w, 'node:', node.className, 'offsetWidth:', node.offsetWidth)
        setGridContainerWidth(w)
      }
      measure()
      const ro = new ResizeObserver(measure)
      ro.observe(node)
      gridRoRef.current = ro
    }, [])
    const [showCustomizeModal, setShowCustomizeModal] = useState(false)
    const [customizeTab, setCustomizeTab] = useState('Parameters')
    const [isCustomizingDashboard, setIsCustomizingDashboard] = useState(false)
    const [customDashboardWidgets, setCustomDashboardWidgets] = useState([])
    const [draggedWidgetIndex, setDraggedWidgetIndex] = useState(null)
    const [isSavingCustomization, setIsSavingCustomization] = useState(false)
    const [showAddWidgetsModal, setShowAddWidgetsModal] = useState(false)
    const [dynamicWidgetCategories, setDynamicWidgetCategories] = useState([])
    const [loadingWidgetCategories, setLoadingWidgetCategories] = useState(false)
    const [widgetCategoriesError, setWidgetCategoriesError] = useState(null)
    const [searchWidgetsQuery, setSearchWidgetsQuery] = useState('')
    const [showResetDashboardModal, setShowResetDashboardModal] = useState(false)
    const [isEditingSidebar, setIsEditingSidebar] = useState(false)
    const [isSidebarVisible, setIsSidebarVisible] = useState(true)
    // Presentation Mode. The settings modal is opened by the "Presentation"
    // button in the dashboard header (XD design). Play uses the loaded
    // config (pause + transition + rotation) to cycle through flagged
    // dashboards; Stop restores the sidebar.
    const [showPresentationSettings, setShowPresentationSettings] = useState(false)
    const [presentationConfig, setPresentationConfig] = useState({
        pauseSeconds: 30,
        transition: 'fade',
        randomOrder: false,
    })
    // Sequence of menu-indices to visit when playing. Built by matching the
    // included dashboards (from the backend) against `dashboardMenuItems` by
    // name. Empty array disables Play.
    const [presentationRotation, setPresentationRotation] = useState([])
    // Tracks the last template order we applied from presentation config so the
    // useEffect that re-runs when templates change doesn't infinite-loop.
    const presOrderSigRef = useRef(null)
    // Remembers the sidebar's pre-Play state so Stop can restore it.
    const sidebarBeforePlayRef = useRef(true)
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
    const [selectedSiteForDevice, setSelectedSiteForDevice] = useState(null)
    const [newDeviceName, setNewDeviceName] = useState('')
    const [configureModalTab, setConfigureModalTab] = useState('Devices')
    const [sensorSelectedDevice, setSensorSelectedDevice] = useState('')
    const [sensorProfileName, setSensorProfileName] = useState('Default')
    const [sensorProfiles, setSensorProfiles] = useState([{ id: 1, name: 'Default' }])
    const [selectedSensorProfile, setSelectedSensorProfile] = useState(null)
    const [showSensorProfileForm, setShowSensorProfileForm] = useState(false)
    const [sensorFormManufacturer, setSensorFormManufacturer] = useState('Sea-Bird')
    const [sensorFormModel, setSensorFormModel] = useState('HydroCAT SDI12')
    const [sensorFormAddress, setSensorFormAddress] = useState('0')
    const [sensorFormMeasCmd, setSensorFormMeasCmd] = useState('M')
    const [sensorFormPort, setSensorFormPort] = useState('P1')
    const [sensorFormPower, setSensorFormPower] = useState('Switch')
    const [sensorFormWarmup, setSensorFormWarmup] = useState('30')
    const [sensorFormTempUnit, setSensorFormTempUnit] = useState('C')
    const [sensorFormShowAll, setSensorFormShowAll] = useState(false)
    const [sensorAvailableParams, setSensorAvailableParams] = useState(['Temperature', 'ODOSat', 'Sample No'])
    const [sensorSelectedParams, setSensorSelectedParams] = useState(['Conductivity (S/m)', 'Pressure (dbar)', 'ODO (mg/L)', 'Salinity (PPT)', 'Sound Speed (m/s)', 'Sp Cond (S/m)'])
    const [draggedSensorParam, setDraggedSensorParam] = useState(null)
    const [draggedSensorParamSource, setDraggedSensorParamSource] = useState(null)
    const [unassignedDevices, setUnassignedDevices] = useState([])
    const [isCreatingDevice, setIsCreatingDevice] = useState(false)
    const [deviceError, setDeviceError] = useState('')
    const [draggedDevice, setDraggedDevice] = useState(null)
    const [dragSource, setDragSource] = useState(null) // 'unassigned' or site id
    const [draggingWidget, setDraggingWidget] = useState(null) // widget being dragged from Add Widgets popup
    const [addWidgetModalHidden, setAddWidgetModalHidden] = useState(false) // visually hide modal during drag without unmounting
    const [modalProjectSites, setModalProjectSites] = useState([]) // Working copy for modal
    const [deviceAssignments, setDeviceAssignments] = useState({}) // Track device assignments: {deviceId: siteId or null}
    const [successMessage, setSuccessMessage] = useState('')
    const [isSavingDevices, setIsSavingDevices] = useState(false)
    const [showCreateSiteModal, setShowCreateSiteModal] = useState(false)
    const [showParamConfigModal, setShowParamConfigModal] = useState(false)
    const [newSiteName, setNewSiteName] = useState('')
    const [newSiteDescription, setNewSiteDescription] = useState('')
    const [newSiteLatitude, setNewSiteLatitude] = useState('')
    const [newSiteLongitude, setNewSiteLongitude] = useState('')
    const [useDeviceGPS, setUseDeviceGPS] = useState(false)
    const [isCreatingSite, setIsCreatingSite] = useState(false)
    const [siteError, setSiteError] = useState('')
    const [siteSuccess, setSiteSuccess] = useState('')
    // Drag-and-drop for sites in edit mode
    const [draggedSiteId, setDraggedSiteId] = useState(null)
    const [dragOverSiteId, setDragOverSiteId] = useState(null)
    const [isDragOverTrash, setIsDragOverTrash] = useState(false)
    const [showDeleteSiteConfirm, setShowDeleteSiteConfirm] = useState(false)
    const [siteToDelete, setSiteToDelete] = useState(null) // { id, name }
    const [showMoveSiteConfirm, setShowMoveSiteConfirm] = useState(false)
    const [pendingSiteMove, setPendingSiteMove] = useState(null) // { fromId, toId }
    const [isDeletingSite, setIsDeletingSite] = useState(false)
    const [showConfigureDashboardsModal, setShowConfigureDashboardsModal] = useState(false)
    const [showDashboardArchive, setShowDashboardArchive] = useState(false)
    const [dashboardSearchQuery, setDashboardSearchQuery] = useState('')
    const [showAddDashboardModal, setShowAddDashboardModal] = useState(false)
    const [addDashboardSearch, setAddDashboardSearch] = useState('')
    const [showDashboardDetailsModal, setShowDashboardDetailsModal] = useState(false)
    const [dashboardName, setDashboardName] = useState('')
    const [dashboardDetails, setDashboardDetails] = useState('')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [selectedTemplateForDetails, setSelectedTemplateForDetails] = useState(null)
    const [isCreatingDashboard, setIsCreatingDashboard] = useState(false)
    const [dashboardError, setDashboardError] = useState('')
    const [dashboardSuccess, setDashboardSuccess] = useState('')
    const [currentViewingDashboard, setCurrentViewingDashboard] = useState(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const playIntervalRef = useRef(null)
    const [dashboards, setDashboards] = useState([
      { id: 1, name: 'Overview', description: 'Created from Project Map Template' },
      { id: 2, name: 'Parameter Comparison', description: 'Created from Parameter Comparison Template' },
      { id: 3, name: 'Project Map', description: 'Created from Project Map Template' },
      { id: 4, name: 'Current Conditions', description: 'Created from Current Conditions Template' },
      { id: 5, name: 'System Performance', description: 'Created from System Performance Template' }
    ])
    const [archivedDashboards, setArchivedDashboards] = useState([
      { id: 6, name: 'Custom Overview', description: 'Created from Overview Template' },
      { id: 7, name: 'DO Comparison', description: 'Created from Parameter Comparison Template' }
    ])
    // Initialize gallery state — URL params take priority over sessionStorage
    const getInitialGalleryState = () => {
        const urlParams = new URLSearchParams(window.location.search)
        const urlTab = urlParams.get('gallery_tab')
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem('galleryState')
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    if (urlTab) parsed.tab = urlTab
                    return parsed
                } catch (e) { /* ignore */ }
            }
        }
        return { tab: urlTab || 'All Photos', page: 1, search: '', selectedId: null }
    }

    const initialState = getInitialGalleryState()
    const [galleryTab, setGalleryTab] = useState(initialState.tab || 'All Photos')
    const [galleryPage, setGalleryPage] = useState(initialState.page || 1)
    const [gallerySearch, setGallerySearch] = useState(initialState.search || '')
    const [selectedPhoto, setSelectedPhoto] = useState(null)
    const [galleryState, setGalleryState] = useState(initialState)
    const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false)
    const [projectAlbums, setProjectAlbums] = useState([])
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [editAlbumModal, setEditAlbumModal] = useState(null) // { id, name }
    const [editAlbumName, setEditAlbumName] = useState('')
    const [uploadItems, setUploadItems] = useState([]) // {id, file, previewUrl, album_id, title, tags, location, description, uploading, done, error}
    const [albumPhotos, setAlbumPhotos] = useState({}) // keyed by album_id
    const [albumName, setAlbumName] = useState('')
    const [albumImages, setAlbumImages] = useState([])
    const [isCreatingAlbum, setIsCreatingAlbum] = useState(false)
    const [albumError, setAlbumError] = useState('')
    const [albumSuccess, setAlbumSuccess] = useState('')
    const [failedImageIds, setFailedImageIds] = useState(new Set())
    const [showEditMediaModal, setShowEditMediaModal] = useState(false)
    const [editingPhoto, setEditingPhoto] = useState(null)
    const [editFormData, setEditFormData] = useState({
        title: '',
        description: '',
        tags: '',
        album: '',
        location: ''
    })
    const [editPhotoFile, setEditPhotoFile] = useState(null)
    const [editPhotoPreview, setEditPhotoPreview] = useState(null)
    const [photoEditsMap, setPhotoEditsMap] = useState({})
    const [apiPhotos, setApiPhotos] = useState([])
    const [galleryLoading, setGalleryLoading] = useState(false)
    // Project settings is an inline screen (Settings → Project), not a popup.
    const [projectEditMode, setProjectEditMode] = useState(false)
    const [projectForm, setProjectForm] = useState({ name: '', description: '', latitude: '', longitude: '' })
    const [savingProject, setSavingProject] = useState(false)
    const [projectSites, setProjectSites] = useState([])
    const [loadingSites, setLoadingSites] = useState(false)
    // First device across the project's sites — drives the Live/Data Logger tab.
    const liveDeviceId = useMemo(() => {
        for (const site of (projectSites || [])) {
            const dev = (site.devices || [])[0]
            if (dev?.id) return dev.id
        }
        return null
    }, [projectSites])
    const [projectDescription, setProjectDescription] = useState('')
    const [projectImage, setProjectImage] = useState(null)
    // Paired with checkedItems — see hydration note there.
    const [selectedParameters, setSelectedParameters] = useState(
      () => loadSelParams(persistedUserId, projectId)?.selectedParameters || []
    )
    const [dateRangeGraph, setDateRangeGraph] = useState(() => {
        // Default to Day (last 24 hours) — matches the default active period below.
        const today = new Date()
        const dayAgo = new Date(today)
        dayAgo.setDate(today.getDate() - 1)

        const formatDateTime = (date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }

        return {
            from: formatDateTime(dayAgo),
            to: formatDateTime(today)
        }
    })
    const [activePeriod, setActivePeriod] = useState('D') // Day (last 24 hours) by default

    // Handle period button clicks (D, W, M, Y)
    const handlePeriodClick = (period) => {
        setActivePeriod(period)
        const today = new Date()
        const fromDate = new Date(today)
        
        switch(period) {
            case 'D': // Last 1 day
                fromDate.setDate(today.getDate() - 1)
                break
            case 'W': // Last 1 week
                fromDate.setDate(today.getDate() - 7)
                break
            case 'M': // Last 1 month
                fromDate.setMonth(today.getMonth() - 1)
                break
            case 'Y': // Last 1 year
                fromDate.setFullYear(today.getFullYear() - 1)
                break
            default:
                break
        }
        
        // Format dates as YYYY-MM-DD HH:MM:SS
        const formatDateTime = (date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
        
        setDateRangeGraph({
            from: formatDateTime(fromDate),
            to: formatDateTime(today)
        })
    }

    // Fetch projects on component mount
    useEffect(() => {
        const fetchProjectsData = async () => {
            setLoadingProjects(true)
            try {
                const response = await projectsApi.fetchProjects(1, 100)
                if (response && response.success && Array.isArray(response.data)) {
                    setProjects(response.data)
                    
                    // If projectId is provided in URL, find and set that project
                    if (projectId) {
                        const project = response.data.find(p => p.id.toString() === projectId.toString())
                        setCurrentProject(project || null)
                    } else if (response.data.length > 0) {
                        // No projectId in URL, but projects exist - set the first one as default
                        setCurrentProject(response.data[0])
                        navigate(`/dashboard/project/${response.data[0].id}`, { replace: true })
                    }
                } else {
                    console.warn('Invalid response structure:', response)
                    setProjects([])
                }
            } catch (error) {
                console.error('Error fetching projects:', error)
                setProjects([])
            } finally {
                setLoadingProjects(false)
            }
        }
        
        fetchProjectsData()
    }, [projectId, navigate])

    // Fetch project-specific data when project changes
    useEffect(() => {
        if (currentProject) {
            setProjectDescription(currentProject.description || '')
            setProjectImage(currentProject.image || null)
            
            // Fetch sites for this project dynamically
            const fetchSites = async () => {
                setLoadingSites(true)
                console.log(`[Dashboard] Fetching sites for project: ${currentProject.name} (ID: ${currentProject.id})`)
                try {
                    const sites = await projectsApi.fetchProjectSites(currentProject.id)
                    console.log(`[Dashboard] Fetched ${sites.length} sites for project ${currentProject.id}:`, sites)
                    setProjectSites(sites || [])
                    
                    // Expand all sites by default to match XD design
                    if (sites && sites.length > 0) {
                        // Expand all sites, devices, and sensors by default
                        const expandedIds = []
                        
                        for (const site of sites) {
                            expandedIds.push(site.id)
                            if (site.devices) {
                                for (const device of site.devices) {
                                    expandedIds.push(`device-${device.id}`)
                                    if (device.sensors) {
                                        for (const sensor of device.sensors) {
                                            expandedIds.push(`sensor-${sensor.id}`)
                                        }
                                    }
                                }
                            }
                        }
                        
                        setExpandedMenus(expandedIds)
                        
                        // Auto-select first parameter from first site for convenience
                        let firstParam = null
                        for (const site of sites) {
                            console.log(`[Dashboard] Checking site: ${site.name} (ID: ${site.id}), devices:`, site.devices?.length || 0)
                            if (site.devices) {
                                for (const device of site.devices) {
                                    console.log(`[Dashboard] Checking device: ${device.name} (ID: ${device.id}), sensors:`, device.sensors?.length || 0)
                                    if (device.sensors) {
                                        for (const sensor of device.sensors) {
                                            console.log(`[Dashboard] Checking sensor: ${sensor.name} (ID: ${sensor.id}), parameters:`, sensor.parameters?.length || 0)
                                            if (sensor.parameters && sensor.parameters.length > 0) {
                                                const fp = sensor.parameters[0]
                                                firstParam = {
                                                    id: fp.id,
                                                    name: fp.name,
                                                    unit: fp.unit || '',
                                                    siteName: site.name || '',
                                                    deviceName: device.name || '',
                                                    sensorName: sensor.name || '',
                                                }
                                                console.log(`[Dashboard] Found first parameter: ${firstParam.name} (ID: ${firstParam.id})`)
                                                break
                                            }
                                        }
                                        if (firstParam) break
                                    }
                                }
                                if (firstParam) break
                            }
                        }
                        
                        if (firstParam) {
                            // Auto-select first parameter ONLY if the user
                            // has no prior persisted selection for this
                            // project — otherwise this would clobber what
                            // we just hydrated from localStorage on mount.
                            setSelectedParameters(prev =>
                              prev.length > 0 ? prev : [firstParam])
                            setCheckedItems(prev =>
                              Object.keys(prev).length > 0
                                ? prev
                                : { [firstParam.id]: true })
                        }
                    }
                } catch (error) {
                    console.error('Error fetching project sites:', error)
                    setProjectSites([])
                } finally {
                    setLoadingSites(false)
                }
            }
            
            fetchSites()
        }
    }, [currentProject])

    // ── Add Dashboard popup: load the admin-assignable templates for the
    // current user/project. The backend honours TemplateAssignment rows so
    // each customer only sees what their admin allowed.
    //
    // Falls back to the client-side lowest-id-per-known-type filter on
    // `templates` if the dedicated endpoint isn't reachable (older backend,
    // or before today's restart). That keeps the popup populated rather
    // than going empty when the backend is mid-deploy.
    useEffect(() => {
        if (!currentProject?.id) {
            setPredefinedTemplates([])
            return
        }
        let cancelled = false
        setLoadingPredefinedTemplates(true)
        dashboardApi.getAdminAssignableTemplates(currentProject.id)
            .then(res => {
                if (cancelled) return
                const list = Array.isArray(res.data?.templates) ? res.data.templates : []
                setPredefinedTemplates(list)
            })
            .catch(err => {
                if (cancelled) return
                console.warn('[Add Dashboard] assignable endpoint unavailable, falling back to client-side filter', err?.response?.status)
                const PREDEFINED_TYPES = [
                    'overview', 'parameter_comparison', 'project_map',
                    'current_conditions', 'system_performance',
                ]
                const computed = PREDEFINED_TYPES
                    .map(tt => {
                        const ofType = (templates || []).filter(t => t.template_type === tt)
                        if (ofType.length === 0) return null
                        return ofType.reduce((a, b) => (a.id < b.id ? a : b))
                    })
                    .filter(Boolean)
                setPredefinedTemplates(computed)
            })
            .finally(() => { if (!cancelled) setLoadingPredefinedTemplates(false) })
        return () => { cancelled = true }
    }, [currentProject?.id, templates])

    // ── Settings → Users: load when the Settings tab opens on Users sub-page
    useEffect(() => {
        if (activeTab !== 'Settings' || currentSettingsIndex !== 0) return
        if (!currentProject?.id) {
            setSettingsUsers([])
            return
        }
        let cancelled = false
        setSettingsUsersLoading(true)
        setSettingsUsersError('')
        projectsApi.listProjectUsers(currentProject.id)
            .then(res => {
                if (cancelled) return
                const data = Array.isArray(res?.data) ? res.data : []
                setSettingsUsers(data)
            })
            .catch(err => {
                if (cancelled) return
                const detail = err.response?.data?.error || err.message || 'Failed to load users.'
                setSettingsUsersError(detail)
                setSettingsUsers([])
            })
            .finally(() => { if (!cancelled) setSettingsUsersLoading(false) })
        return () => { cancelled = true }
    }, [activeTab, currentSettingsIndex, currentProject?.id])

    // Fetch templates for the *current project* — and re-fetch when the
    // user switches projects so each project sees only its own
    // assignments + custom-created dashboards.
    //
    // Previously this called getTemplates() (the unfiltered list endpoint)
    // with []-deps, so EVERY project of EVERY user appeared in the
    // Dashboard menu of every project. The project-scoped endpoint
    // /api/dashboard/templates/available_for_project/?project=<id>
    // returns:
    //   • admin-assigned templates (global TemplateAssignment that matches
    //     this user, or project-scoped for this project)
    //   • the customer's own custom-created dashboards for this project
    //     (each has its own project_id=this_project TemplateAssignment)
    // Customer dashboards from *other* projects don't match either branch
    // of the OR, so they stay where they were created.
    useEffect(() => {
        if (!currentProject?.id) {
            setTemplates([])
            return
        }
        let cancelled = false
        const fetchTemplates = async () => {
            try {
                setLoadingTemplates(true)
                const response = await dashboardApi.getProjectTemplates(currentProject.id)
                if (cancelled) return
                const templatesData = response.data?.templates || response.data?.results || response.data || []
                const activeTemplates = templatesData.filter(t => t.is_active !== false)
                setTemplates(activeTemplates)
                setCurrentMenuIndex(prev =>
                    activeTemplates.length > 0 ? Math.min(prev, activeTemplates.length - 1) : 0
                )
            } catch (error) {
                if (cancelled) return
                console.error('Error fetching project templates:', error)
                setTemplates([])
            } finally {
                if (!cancelled) setLoadingTemplates(false)
            }
        }
        fetchTemplates()
        return () => { cancelled = true }
    }, [currentProject?.id])

    // Persist gallery state to sessionStorage whenever it changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('galleryState', JSON.stringify({
                tab: galleryTab,
                page: galleryPage,
                search: gallerySearch,
                selectedId: selectedPhoto?.id || null
            }))
        }
    }, [galleryTab, galleryPage, gallerySearch, selectedPhoto])

    // Persist active tab so refresh returns to same section
    useEffect(() => {
        try { sessionStorage.setItem('activeTab', activeTab) } catch {}
    }, [activeTab])

    // Clean gallery_tab URL param when navigating away from Gallery
    useEffect(() => {
        if (activeTab !== 'Gallery') {
            const params = new URLSearchParams(location.search)
            if (params.has('gallery_tab')) {
                params.delete('gallery_tab')
                navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true })
            }
        }
    }, [activeTab])

    // Fetch gallery photos when gallery tab is opened or project changes
    useEffect(() => {
        if (activeTab !== 'Gallery' || !currentProject?.id) return
        // Skip if we already fetched for this project and have photos
        if (galleryFetchedProjectRef.current === currentProject.id && apiPhotos.length > 0) return
        if (galleryLoading) return

        const projectId = currentProject.id
        const fetchPhotos = async () => {
            setGalleryLoading(true)
            try {
                const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
                const token = localStorage.getItem(JWT_STORAGE_KEY)
                const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
                const res = await fetch(`${API_URL}/api/photo/photo/?project=${projectId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                if (!res.ok) throw new Error('Failed to fetch photos')
                const data = await res.json()
                const photos = (data.results || data || []).map((p, i) => ({
                    id: p.id || i,
                    _fromApi: true,
                    src: p.photo_url || p.thumbnail || p.image || p.file_url || '',
                    largeSrc: p.photo_url || p.image || p.file_url || '',
                    category: p.upload_type || p.category || 'Photo',
                    title: p.name || p.title || p.original_filename || 'Untitled',
                    description: p.notes || p.description || '',
                    originalFilename: p.original_filename || '',
                    cameraSN: p.device_serial_number || 'N/A',
                    tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || 'N/A'),
                    album: '',
                    uploadedBy: p.author || p.uploaded_by || '',
                    date: p.date_uploaded || p.date || '',
                    dimensions: (p.width && p.height) ? `${p.width} x ${p.height}` : '',
                    size: p.size ? `${(p.size / 1024).toFixed(2)} KB` : '',
                    totalSize: '',
                    location: ''
                }))
                galleryFetchedProjectRef.current = projectId
                setApiPhotos(photos)
            } catch (err) {
                console.error('[Gallery] Error fetching photos:', err)
            } finally {
                setGalleryLoading(false)
            }
        }
        fetchPhotos()
    }, [activeTab, currentProject?.id, apiPhotos.length])

    // Fetch albums for the current project when Gallery tab is active
    useEffect(() => {
        if (activeTab === 'Gallery' && currentProject?.id) {
            // Reset gallery state when switching projects
            setGalleryTab('All Photos')
            setGalleryPage(1)
            setGallerySearch('')
            setSelectedPhoto(null)
            setApiPhotos([])  // Clear photos so they re-fetch for the new project
            
            const fetchAlbums = async () => {
                try {
                    const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
                    const token = localStorage.getItem(JWT_STORAGE_KEY)
                    const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
                    const res = await fetch(`${API_URL}/api/photo/album/?project=${currentProject.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                    if (!res.ok) return
                    const data = await res.json()
                    const albums = data.results || data || []
                    setProjectAlbums(albums)
                    // Auto-fetch photos for the current tab if it matches an album (handles page refresh)
                    const urlParams = new URLSearchParams(window.location.search)
                    const currentGalleryTab = urlParams.get('gallery_tab') || 'All Photos'
                    const matchedAlbum = albums.find(a => a.name === currentGalleryTab)
                    if (matchedAlbum) {
                        fetchAlbumPhotos(matchedAlbum.id)
                    }
                } catch (e) { /* silent */ }
            }
            fetchAlbums()
        }
    }, [activeTab, currentProject?.id])

    // Load unassigned devices when Add Device modal opens
    useEffect(() => {
        if (showAddDeviceModal && projectSites.length > 0 && currentProject) {
            // Create a working copy of project sites for the modal
            setModalProjectSites(JSON.parse(JSON.stringify(projectSites)))
            setDeviceAssignments({})
            
            // Clear any previous messages when modal opens
            setDeviceError('')
            setSuccessMessage('')
            
            // FIXED: Fetch unassigned devices from the backend API (Symfony approach)
            // Unassigned devices are devices where site IS NULL in the database
            const loadUnassignedDevices = async () => {
                try {
                    console.log(`[Unassigned Devices] Fetching from API for project ${currentProject.id}...`)
                    
                    // Fetch unassigned devices from backend API
                    const backendDevices = await projectsApi.fetchUnassignedDevices(currentProject.id)
                    console.log(`[Unassigned Devices] Fetched ${backendDevices.length} devices from backend API`, backendDevices)
                    
                    // Use ONLY backend devices - don't merge with stale localStorage data
                    // This ensures we always show the current state from the database
                    setUnassignedDevices(backendDevices)
                    
                    // Clear localStorage cache after successful API fetch to remove stale data
                    const storageKey = `unassigned_devices_${currentProject.id}`
                    localStorage.removeItem(storageKey)
                    console.log(`[Unassigned Devices] Cleared localStorage cache for project ${currentProject.id}`)
                    
                } catch (error) {
                    console.error(`[Unassigned Devices] Error fetching from API:`, error)
                    
                    // Fallback: Load from localStorage only if API fails
                    console.log('[Unassigned Devices] Falling back to localStorage only')
                    const localDevices = getUnassignedDevicesFromStorage(currentProject.id)
                    setUnassignedDevices(localDevices)
                    
                    // Don't show error for unassigned devices fetch - it can fail silently and load from localStorage
                    // setDeviceError('Note: Could not load unassigned devices from server')
                    // setTimeout(() => setDeviceError(''), 5000)
                }
            }
            
            loadUnassignedDevices()
            
            // Set first site as default if available
            if (!selectedSiteForDevice && projectSites.length > 0) {
                setSelectedSiteForDevice(projectSites[0].id)
            }
        }
    }, [showAddDeviceModal, projectSites, currentProject])

    // Load widget categories when Add Widgets modal opens
    useEffect(() => {
        if (showAddWidgetsModal) {
            loadWidgetCategories()
        }
    }, [showAddWidgetsModal])

    // Function to load widget categories from API
    const loadWidgetCategories = async () => {
        setLoadingWidgetCategories(true)
        setWidgetCategoriesError(null)
        try {
            console.log('[Widget Categories] Fetching from API...')
            const data = await widgetApi.getWidgetsByCategory()
            console.log('[Widget Categories] Loaded:', data)
            
            // Transform API response to match our UI structure
            const categories = data.categories || []
            setDynamicWidgetCategories(categories)
            setSearchWidgetsQuery('')
        } catch (error) {
            console.error('[Widget Categories] Error loading:', error)
            setWidgetCategoriesError('Failed to load widget categories. Please try again.')
            setDynamicWidgetCategories([])
        } finally {
            setLoadingWidgetCategories(false)
        }
    }

    // Clean up the play interval on unmount — MUST be before any early returns
    // so React hooks count stays consistent every render
    useEffect(() => {
        return () => {
            if (playIntervalRef.current) clearInterval(playIntervalRef.current)
        }
    }, [])

    // ── Persist sidebar selection (scope-aware) ──────────────────────────────
    // The naive "save on every state change" approach has a race: when the
    // user lands on `/dashboard` (no projectId yet) after login, then the
    // router resolves to `/dashboard/project/N`, the scope key changes from
    // (userId, undefined) to (userId, N) while state is still [] / {} — and
    // we'd happily save THAT empty state, wiping the user's saved selection.
    //
    // Fix: only save when the scope key matches what we've previously tracked
    // (i.e. the user is making a real change to the current project's
    // selection).  When the scope key flips to a new (userId, projectId),
    // first HYDRATE from localStorage and remember the new scope — without
    // saving on that transition.
    //
    // Hooks block lives here — above the `return <NoActiveProject />` early
    // returns — so React's hook count stays stable across all render paths.
    const persistScopeRef = useRef('')
    useEffect(() => {
      if (!persistedUserId || !projectId) return
      const scope = `${persistedUserId}:${projectId}`
      if (persistScopeRef.current !== scope) {
        // Scope just changed (login, project switch, projectId-arrives-late).
        // Re-hydrate from localStorage; skip saving for this run.
        persistScopeRef.current = scope
        const saved = loadSelParams(persistedUserId, projectId)
        if (saved) {
          setSelectedParameters(saved.selectedParameters)
          setCheckedItems(saved.checkedItems)
        }
        return
      }
      saveSelParams(persistedUserId, projectId, selectedParameters, checkedItems)
    }, [persistedUserId, projectId, selectedParameters, checkedItems])

    // ── "As of …" header clock ────────────────────────────────────────────────
    // Hooks declared near the top of the component (BEFORE any early
    // `return <NoActiveProject />`) so the hook order stays stable across
    // every render — required by the Rules of Hooks (React error #310).
    //
    // Renders the current time in the customer's configured timezone
    // (Account.timezone via auth-store).  resolveIanaTz() bridges the
    // legacy display-string profile format → IANA names that Intl accepts.
    // The clock ticks every 30 s so the label stays current without a
    // manual reload.
    const rawTz = useAuthStore(s => s.user?.timezone)
    const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])
    const [nowInstant, setNowInstant] = useState(() => new Date())
    useEffect(() => {
      const id = setInterval(() => setNowInstant(new Date()), 30 * 1000)
      return () => clearInterval(id)
    }, [])
    const nowLabel = useMemo(() => {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: customerTz,
          month: 'long', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
        }).format(nowInstant)
      } catch {
        // Final safety net — resolveIanaTz should always return something
        // Intl accepts, but if a future TZ string slips through we still
        // render *something* instead of crashing.
        return nowInstant.toLocaleString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
        })
      }
    }, [nowInstant, customerTz])

    // Load Presentation config and rebuild the rotation whenever the project or
    // its templates change. Also reorders `templates` to match the saved
    // presentation order so the nav dropdown reflects what the user configured.
    useEffect(() => {
        const pid = currentProject?.id
        if (!pid || templates.length === 0) {
            setPresentationRotation([])
            return
        }

        // Guard: if templates changed because WE reordered them (not a real
        // external change), skip re-fetching to avoid an infinite loop.
        // Include pid so a project change always triggers a fresh fetch.
        const currentSig = `${pid}:${templates.map((t) => t.id).join(',')}`
        if (presOrderSigRef.current === currentSig) return

        let cancelled = false
        dashboardApi
            .getPresentationConfig(pid)
            .then((res) => {
                if (cancelled) return
                const cfg = res.data || {}
                setPresentationConfig({
                    pauseSeconds: Math.max(30, cfg.pause_seconds || 30),
                    transition: cfg.transition || 'fade',
                    randomOrder: !!cfg.random_order,
                })

                // ── Reorder templates to match the saved presentation order ──
                // cfg.dashboards is already sorted: included first (by order),
                // then excluded in their original order.
                const orderedDashboards = cfg.dashboards || []
                const reordered = []
                orderedDashboards.forEach((d) => {
                    const t = templates.find((t) => t.name === d.name || t.id === d.id)
                    if (t) reordered.push(t)
                })
                // Append any templates not mentioned in the config (safety net)
                templates.forEach((t) => {
                    if (!reordered.find((r) => r.id === t.id)) reordered.push(t)
                })

                const reorderedSig = `${pid}:${reordered.map((t) => t.id).join(',')}`
                presOrderSigRef.current = reorderedSig // mark so next run is skipped

                // Build rotation from the NEW order (indices into reordered array)
                const included = orderedDashboards.filter((d) => d.included)
                const rotation = included
                    .map((d) => {
                        let idx = reordered.findIndex((t) => t.name === d.name)
                        if (idx < 0) idx = reordered.findIndex((t) => t.id === d.id)
                        return idx
                    })
                    .filter((idx) => idx >= 0)
                setPresentationRotation(rotation)

                if (reorderedSig !== currentSig) {
                    // Remember which template the user is currently viewing so
                    // we can restore currentMenuIndex after the reorder.
                    const currentTemplateName = templates[currentMenuIndex]?.name
                    setTemplates(reordered)
                    const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
                    if (newIdx >= 0 && newIdx !== currentMenuIndex) {
                        setCurrentMenuIndex(newIdx)
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setPresentationRotation([])
            })
        return () => {
            cancelled = true
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProject?.id, templates])

    // Show loading state while fetching projects
    if (loadingProjects) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading projects...</p>
                </div>
            </div>
        )
    }

    // Show "No Active Project" screen if user has no projects
    if (projects.length === 0) {
        return <NoActiveProject />
    }

    // If no project is set (shouldn't happen but just in case), show no active project
    if (!currentProject) {
        return <NoActiveProject />
    }


    const galleryTabs = ['All Photos', ...projectAlbums.map(a => a.name)]

    const galleryPhotos = [
        { id: 1, src: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=1200&h=800&fit=crop', category: 'Bird', title: 'Black Vulture - Coragyps atratus', description: 'Black Vulture - Coragyps atratus', originalFilename: 'BlackVulture_2024-06-27.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/27/2024 10:15 UTC-04:00', dimensions: '1620 x 1080', size: '486.28 KB', totalSize: '527.12 KB', location: 'Site - Pond Buoy' },
        { id: 2, src: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop', category: 'Deployment', title: 'XB-200 Data Buoy', description: 'XB-200 Data Buoy', originalFilename: 'XB-200_Buoy_2024-05-15.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/15/2024 14:30 UTC-04:00', dimensions: '1600 x 1200', size: '512.45 KB', totalSize: '551.22 KB', location: 'Site - Pond Buoy' },
        { id: 3, src: 'https://images.unsplash.com/photo-1585066699728-a56ef32a024a?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1585066699728-a56ef32a024a?w=1200&h=800&fit=crop', category: 'Insect', title: 'Blue Dasher - Pachydiplax longipennis', description: 'Blue Dasher - Pachydiplax longipennis', originalFilename: 'BlueDasher_2024-07-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/10/2024 09:45 UTC-04:00', dimensions: '1640 x 1230', size: '498.56 KB', totalSize: '540.15 KB', location: 'Site - Pond Buoy' },
        { id: 4, src: 'https://images.unsplash.com/photo-1480044965905-02098d419e96?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1480044965905-02098d419e96?w=1200&h=800&fit=crop', category: 'Bird', title: 'Great Blue Heron - Ardea herodias', description: 'Great Blue Heron - Ardea herodias', originalFilename: 'BlueHeron_2024-06-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/12/2024 11:20 UTC-04:00', dimensions: '1680 x 1050', size: '515.33 KB', totalSize: '558.44 KB', location: 'Site - Pond Buoy' },
        { id: 5, src: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200&h=800&fit=crop', category: 'Fish', title: 'Bluegill - Epomis Macrochirus', description: 'Bluegill - Epomis Macrochirus', originalFilename: 'Bluegill_2024-08-20.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/20/2024 13:00 UTC-04:00', dimensions: '1600 x 1200', size: '520.67 KB', totalSize: '565.21 KB', location: 'Site - Pond Buoy' },
        { id: 6, src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Weather station comparison...', description: 'Weather station comparison', originalFilename: 'WeatherStation_2024-05-20.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/20/2024 15:30 UTC-04:00', dimensions: '1620 x 1080', size: '505.44 KB', totalSize: '548.33 KB', location: 'Site - Weather Station' },
        { id: 7, src: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Diver-Link', description: 'Diver-Link', originalFilename: 'DiverLink_2024-06-05.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/05/2024 10:00 UTC-04:00', dimensions: '1600 x 1200', size: '518.22 KB', totalSize: '560.88 KB', location: 'Site - Pond Buoy' },
        { id: 8, src: 'https://images.unsplash.com/photo-1474905089749-3eaa06dc99d8?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1474905089749-3eaa06dc99d8?w=1200&h=800&fit=crop', category: 'Fungi and Lichens', title: 'Funeral Bell - Galerina marginata', description: 'Funeral Bell - Galerina marginata', originalFilename: 'FuneralBell_2024-07-25.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/25/2024 08:30 UTC-04:00', dimensions: '1640 x 1090', size: '492.15 KB', totalSize: '535.77 KB', location: 'Site - Land Based' },
        { id: 9, src: 'https://images.unsplash.com/photo-1530587191325-3db32d826c18?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1530587191325-3db32d826c18?w=1200&h=800&fit=crop', category: 'Plant', title: 'Algae growth on pond', description: 'Algae growth on pond', originalFilename: 'AlgaeGrowth_2024-08-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/10/2024 12:15 UTC-04:00', dimensions: '1620 x 1200', size: '508.88 KB', totalSize: '552.66 KB', location: 'Site - Pond Buoy' },
        { id: 10, src: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Garden Hobo Equipment', description: 'Garden Hobo Equipment', originalFilename: 'GardenHobo_2024-06-18.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/18/2024 14:45 UTC-04:00', dimensions: '1680 x 1120', size: '514.22 KB', totalSize: '557.99 KB', location: 'Site - Land Based' },
        { id: 11, src: 'https://images.unsplash.com/photo-1500829243541-74b677fecc30?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1500829243541-74b677fecc30?w=1200&h=800&fit=crop', category: 'Plant', title: 'Jimsonweed - Datura stramonium', description: 'Jimsonweed - Datura stramonium', originalFilename: 'Jimsonweed_2024-07-30.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/30/2024 09:00 UTC-04:00', dimensions: '1600 x 1200', size: '510.77 KB', totalSize: '555.21 KB', location: 'Site - Land Based' },
        { id: 12, src: 'https://images.unsplash.com/photo-1516728778615-2d590ea1855e?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1516728778615-2d590ea1855e?w=1200&h=800&fit=crop', category: 'Amphibian', title: "Blanchard's Cricket Frog - Acris blanchardi", description: "Blanchard's Cricket Frog - Acris blanchardi", originalFilename: 'CricketFrog_2024-08-05.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/05/2024 16:20 UTC-04:00', dimensions: '1640 x 1100', size: '521.44 KB', totalSize: '567.88 KB', location: 'Site - Pond Buoy' },
        { id: 13, src: 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=1200&h=800&fit=crop', category: 'Bird', title: 'Canada Goose - Goslings', description: 'Canada Goose - Goslings', originalFilename: 'CanadaGoose_2024-05-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/10/2024 07:30 UTC-04:00', dimensions: '1680 x 1120', size: '516.55 KB', totalSize: '560.22 KB', location: 'Site - Pond Buoy' },
        { id: 14, src: 'https://images.unsplash.com/photo-1551085254-e96b210db58a?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1551085254-e96b210db58a?w=1200&h=800&fit=crop', category: 'Bird', title: 'Spotted Sandpiper - Actitis macularius', description: 'Spotted Sandpiper - Actitis macularius', originalFilename: 'SpottedSandpiper_2024-06-22.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/22/2024 13:45 UTC-04:00', dimensions: '1620 x 1200', size: '509.66 KB', totalSize: '553.44 KB', location: 'Site - Pond Buoy' },
        { id: 15, src: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=1200&h=800&fit=crop', category: 'Pond', title: 'New plant growth on pond...', description: 'New plant growth on pond', originalFilename: 'PondPlants_2024-07-08.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/08/2024 10:30 UTC-04:00', dimensions: '1640 x 1100', size: '504.33 KB', totalSize: '548.99 KB', location: 'Site - Pond Buoy' },
        { id: 16, src: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1200&h=800&fit=crop', category: 'Plant', title: 'Honeysuckle - Needs Removed', description: 'Honeysuckle - Lonicera', originalFilename: 'Honeysuckle_2024-08-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/12/2024 11:50 UTC-04:00', dimensions: '1600 x 1200', size: '517.88 KB', totalSize: '562.55 KB', location: 'Site - Land Based' },
        { id: 17, src: 'https://images.unsplash.com/photo-1518173835702-3a7e5f1a3cce?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518173835702-3a7e5f1a3cce?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Barred Owl Box', description: 'Barred Owl Box', originalFilename: 'OwlBox_2024-05-25.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/25/2024 09:15 UTC-04:00', dimensions: '1680 x 1120', size: '519.22 KB', totalSize: '564.11 KB', location: 'Site - Land Based' },
        { id: 18, src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&h=800&fit=crop', category: 'Plant', title: 'American Pokeweed - Phytolacca americana', description: 'American Pokeweed - Phytolacca americana', originalFilename: 'Pokeweed_2024-08-18.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/18/2024 14:00 UTC-04:00', dimensions: '1620 x 1080', size: '506.44 KB', totalSize: '550.22 KB', location: 'Site - Land Based' },
        { id: 19, src: 'https://images.unsplash.com/photo-1470058869958-2a77ade41c02?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1470058869958-2a77ade41c02?w=1200&h=800&fit=crop', category: 'Arachnid', title: 'Arabesque Orbweaver - Neoscona arabesca', description: 'Arabesque Orbweaver - Neoscona arabesca', originalFilename: 'Orbweaver_2024-07-15.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/15/2024 15:30 UTC-04:00', dimensions: '1640 x 1090', size: '512.77 KB', totalSize: '557.66 KB', location: 'Site - Land Based' },
        { id: 20, src: 'https://images.unsplash.com/photo-1497752531616-c3afd9760a11?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1497752531616-c3afd9760a11?w=1200&h=800&fit=crop', category: 'Amphibian', title: 'Northern Leopard Frog - Lithobates pipiens', description: 'Northern Leopard Frog - Lithobates pipiens', originalFilename: 'LeopardFrog_2024-06-28.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/28/2024 12:45 UTC-04:00', dimensions: '1680 x 1120', size: '515.33 KB', totalSize: '559.88 KB', location: 'Site - Pond Buoy' },
        { id: 21, src: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop', category: 'Deployment', title: 'miniDOT Loggers', description: 'miniDOT Loggers', originalFilename: 'miniDOT_2024-05-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/12/2024 08:00 UTC-04:00', dimensions: '1600 x 1200', size: '520.11 KB', totalSize: '564.33 KB', location: 'Site - Pond Buoy' },
        { id: 22, src: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=1200&h=800&fit=crop', category: 'Arachnid', title: 'Dark Fishing Spider in nest b...', description: 'Dark Fishing Spider in nest', originalFilename: 'FishingSpider_2024-07-22.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/22/2024 16:00 UTC-04:00', dimensions: '1620 x 1080', size: '508.55 KB', totalSize: '552.77 KB', location: 'Site - Pond Buoy' },
        { id: 23, src: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&h=800&fit=crop', category: 'Deployment', title: 'EWC', description: 'Environmental Water Collection', originalFilename: 'EWC_2024-06-08.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/08/2024 13:30 UTC-04:00', dimensions: '1640 x 1100', size: '513.44 KB', totalSize: '558.99 KB', location: 'Site - Pond Buoy' },
        { id: 24, src: 'https://images.unsplash.com/photo-1453748866136-b1dd97284f49?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1453748866136-b1dd97284f49?w=1200&h=800&fit=crop', category: 'Plant', title: 'Watercress - Nasturtium', description: 'Watercress - Nasturtium officinale', originalFilename: 'Watercress_2024-08-03.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/03/2024 10:20 UTC-04:00', dimensions: '1600 x 1200', size: '511.22 KB', totalSize: '556.44 KB', location: 'Site - Land Based' },
    ]

    const photosPerPage = 12
    const matchedAlbum = galleryTab !== 'All Photos' ? projectAlbums.find(a => a.name === galleryTab) : null
    const activeGalleryPhotos = matchedAlbum && albumPhotos[matchedAlbum.id]?.length > 0
        ? albumPhotos[matchedAlbum.id]
        : (apiPhotos.length > 0 && apiPhotos.some(p => p.src && p.src.startsWith('http'))) ? apiPhotos : galleryPhotos
    const albumFilteredPhotos = matchedAlbum
        ? (albumPhotos[matchedAlbum.id] || []).filter(p => p.src && p.src.length > 0)
        : galleryTab !== 'All Photos'
            ? activeGalleryPhotos.filter(p => (p.album || '') === galleryTab)
            : activeGalleryPhotos
    const filteredGalleryPhotos = gallerySearch.trim()
        ? albumFilteredPhotos.filter(p =>
            (p.title || '').toLowerCase().includes(gallerySearch.toLowerCase()) ||
            (p.category || '').toLowerCase().includes(gallerySearch.toLowerCase()) ||
            (p.description || '').toLowerCase().includes(gallerySearch.toLowerCase())
          )
        : albumFilteredPhotos
    const totalGalleryPages = Math.ceil(filteredGalleryPhotos.length / photosPerPage)
    const currentPhotos = filteredGalleryPhotos
        .slice((galleryPage - 1) * photosPerPage, galleryPage * photosPerPage)
        .map(p => photoEditsMap[p.id] ? { ...p, ...photoEditsMap[p.id] } : p)

    const dashboardMenuItems = [
        ...templates.map(t => t.name),
        'Config Dashboards'
    ]

    const dashboardCyclableItems = templates.length // Only cycle through template names

    const dataAnalyticsMenuItems = [
        'Visualize',
        'Validate',
        'Formulas'
    ]

    const reportMenuItems = [
        'Field Station Turbidity',
        'Select Report'
    ]

    const settingsMenuItems = [
        'Users',
        'Project'
    ]

    const liveMenuItems = [
        'Data Logger',
        'Communications',
        'Sensor Readings'
    ]

    const toggleMenu = (menu) => {
        setExpandedMenus(prev =>
            prev.includes(menu) ?
            prev.filter(m => m !== menu) :
            [...prev, menu]
        )
    }

    const toggleCheckbox = (id) => {
        console.log(`[Dashboard] toggleCheckbox called for ID:`, id)
        setCheckedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }))
        
        // Track selected parameters for graph
        // Find the parameter from projectSites — capture site/sensor/device/unit
        // so the Parameter Comparison view can title each chart "Site | Param (unit)".
        let parameterFound = null
        for (const site of projectSites) {
            if (site.devices) {
                for (const device of site.devices) {
                    if (device.sensors) {
                        for (const sensor of device.sensors) {
                            if (sensor.parameters) {
                                const param = sensor.parameters.find(p => p.id === id)
                                if (param) {
                                    parameterFound = {
                                        id: param.id,
                                        name: param.name,
                                        unit: param.unit || '',
                                        siteName: site.name || '',
                                        deviceName: device.name || '',
                                        sensorName: sensor.name || '',
                                    }
                                    console.log(`[Dashboard] Found parameter:`, parameterFound)
                                    break
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // Add or remove from selected parameters
        if (parameterFound) {
            setSelectedParameters(prev => {
                const isSelected = prev.some(p => p.id === parameterFound.id)
                const newParams = isSelected 
                    ? prev.filter(p => p.id !== parameterFound.id)
                    : [...prev, parameterFound]
                console.log(`[Dashboard] Updated selectedParameters:`, newParams)
                return newParams
            })
        }
    }

    // Filter sites, devices, and parameters based on search query
    const getFilteredSites = () => {
        if (!searchQuery.trim()) {
            return projectSites
        }

        const query = searchQuery.toLowerCase()
        const filtered = []

        projectSites.forEach(site => {
            let matchingSite = {
                ...site,
                devices: []
            }

            if (site.devices) {
                site.devices.forEach(device => {
                    let matchingDevice = {
                        ...device,
                        sensors: []
                    }

                    if (device.sensors) {
                        device.sensors.forEach(sensor => {
                            let matchingSensor = {
                                ...sensor,
                                parameters: []
                            }

                            if (sensor.parameters) {
                                matchingSensor.parameters = sensor.parameters.filter(param =>
                                    param.name.toLowerCase().includes(query)
                                )
                            }

                            if (matchingSensor.parameters.length > 0 || sensor.name.toLowerCase().includes(query)) {
                                matchingDevice.sensors.push(matchingSensor)
                            }
                        })
                    }

                    if (matchingDevice.sensors.length > 0 || device.name.toLowerCase().includes(query)) {
                        matchingSite.devices.push(matchingDevice)
                    }
                })
            }

            if (matchingSite.devices.length > 0 || site.name.toLowerCase().includes(query)) {
                filtered.push(matchingSite)
            }
        })

        return filtered
    }

    // Site drag-and-drop handlers (edit mode)
    const handleSiteDragStart = (e, siteId) => {
        setDraggedSiteId(siteId)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', siteId)
    }

    const handleSiteDragOver = (e, targetSiteId) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (targetSiteId !== draggedSiteId) {
            setDragOverSiteId(targetSiteId)
        }
    }

    const handleSiteDrop = (e, targetSiteId) => {
        e.preventDefault()
        setDragOverSiteId(null)
        if (!draggedSiteId || draggedSiteId === targetSiteId) {
            setDraggedSiteId(null)
            return
        }
        const fromSite = projectSites.find(s => s.id === draggedSiteId)
        const toSite = projectSites.find(s => s.id === targetSiteId)
        setPendingSiteMove({ fromId: draggedSiteId, toId: targetSiteId, fromName: fromSite?.name, toName: toSite?.name })
        setShowMoveSiteConfirm(true)
        setDraggedSiteId(null)
    }

    const handleSiteDragEnd = () => {
        setDraggedSiteId(null)
        setDragOverSiteId(null)
        setIsDragOverTrash(false)
    }

    const handleTrashDragOver = (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setIsDragOverTrash(true)
    }

    const handleTrashDragLeave = () => {
        setIsDragOverTrash(false)
    }

    const handleTrashDrop = (e) => {
        e.preventDefault()
        setIsDragOverTrash(false)
        if (!draggedSiteId) return
        const site = projectSites.find(s => s.id === draggedSiteId)
        setSiteToDelete({ id: draggedSiteId, name: site?.name || 'this site' })
        setShowDeleteSiteConfirm(true)
        setDraggedSiteId(null)
    }

    const confirmSiteMove = () => {
        if (!pendingSiteMove) return
        const { fromId, toId } = pendingSiteMove
        const sites = [...projectSites]
        const fromIdx = sites.findIndex(s => s.id === fromId)
        const toIdx = sites.findIndex(s => s.id === toId)
        if (fromIdx === -1 || toIdx === -1) { setShowMoveSiteConfirm(false); return }
        const [moved] = sites.splice(fromIdx, 1)
        sites.splice(toIdx, 0, moved)
        setProjectSites(sites)
        setShowMoveSiteConfirm(false)
        setPendingSiteMove(null)
    }

    const confirmSiteDelete = async () => {
        if (!siteToDelete) return
        setIsDeletingSite(true)
        try {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = localStorage.getItem(JWT_STORAGE_KEY)
            const response = await fetch(`${API_URL}/api/project/site/${siteToDelete.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            if (!response.ok && response.status !== 204) {
                throw new Error('Failed to delete site')
            }
            setProjectSites(prev => prev.filter(s => s.id !== siteToDelete.id))
        } catch (err) {
            console.error('Delete site error:', err)
            setSiteError('Failed to delete site. Please try again.')
            setTimeout(() => setSiteError(''), 3000)
        } finally {
            setIsDeletingSite(false)
            setShowDeleteSiteConfirm(false)
            setSiteToDelete(null)
        }
    }

    // Stop play mode helper (used by manual navigation too). Also restores
    // the sidebar — Play collapses it; Stop / Prev / Next put it back.
    const stopPlay = () => {
        if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current)
            playIntervalRef.current = null
        }
        if (isPlaying) {
            setIsSidebarVisible(sidebarBeforePlayRef.current)
        }
        setIsPlaying(false)
    }

    const handlePrevious = () => {
        stopPlay() // manual navigation cancels auto-play
        if (currentMenuIndex === dashboardCyclableItems) {
            setCurrentMenuIndex(dashboardCyclableItems - 1)
        } else {
            setCurrentMenuIndex(prev =>
                prev === 0 ? dashboardCyclableItems - 1 : prev - 1
            )
        }
    }

    const handleNext = () => {
        stopPlay() // manual navigation cancels auto-play
        setCurrentMenuIndex(prev =>
            prev >= dashboardCyclableItems - 1 ? 0 : prev + 1
        )
    }

    // Build a shuffled copy if random order is on. Sets persist for the
    // duration of the run — we don't re-shuffle every cycle, which would be
    // disorienting. Plain function (no deps) — safe to call anywhere.
    const buildPlaybackOrder = (rotation, random) => {
        if (!random || rotation.length <= 1) return rotation.slice()
        const out = rotation.slice()
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[out[i], out[j]] = [out[j], out[i]]
        }
        return out
    }

    // Play: cycle through dashboards flagged for presentation, at the cadence
    // configured in Presentation Settings. Clicking Play again (or Stop)
    // stops the cycle. Manual navigation (Previous/Next) also stops it via
    // the existing stopPlay() helper.
    const handlePlay = () => {
        if (isPlaying) {
            clearInterval(playIntervalRef.current)
            playIntervalRef.current = null
            setIsPlaying(false)
            // Restore sidebar to whatever it was before Play.
            setIsSidebarVisible(sidebarBeforePlayRef.current)
            return
        }

        if (presentationRotation.length === 0) {
            window.alert(
                'No dashboards are selected for presentation.\n\nClick "Presentation" to choose which dashboards to include.'
            )
            return
        }

        // Snapshot sidebar state so Stop restores it.
        sidebarBeforePlayRef.current = isSidebarVisible
        setIsSidebarVisible(false)

        const order = buildPlaybackOrder(presentationRotation, presentationConfig.randomOrder)
        let pos = 0
        setCurrentMenuIndex(order[pos])
        setIsPlaying(true)

        // If only one dashboard is flagged, there's nothing to cycle — just
        // show it and leave isPlaying=true so the Stop button stays available.
        if (order.length > 1) {
            playIntervalRef.current = setInterval(() => {
                pos = (pos + 1) % order.length
                setCurrentMenuIndex(order[pos])
            }, presentationConfig.pauseSeconds * 1000)
        }
    }

    const selectMenuItem = (index) => {
        const menuItem = dashboardMenuItems[index]
        
        // Handle special menu items
        if (menuItem === 'Config Dashboards' || menuItem === 'Configure Dashboards') {
            setShowConfigureDashboardsModal(true)
            return
        }
        
        // Find the template with this name
        const template = templates.find(t => t.name === menuItem)
        if (template) {
            // Store selected template for creating a dashboard
            setSelectedTemplate(template)
            console.log('[Dashboard] Selected template:', template)
        }
        
        setCurrentMenuIndex(index)
        setActiveTab('Dashboard')
    }

    // Create dashboard from template with default name
    const createDashboardFromTemplate = async (template, isBlank = false) => {
        if (!currentProject) {
            setDashboardError('Please select a project first')
            return
        }

        setIsCreatingDashboard(true)
        setDashboardError('')

        try {
            // Generate dashboard name
            let dashboardName
            if (isBlank) {
                // For blank template, use "New Dashboard" with timestamp
                const timestamp = new Date().toLocaleTimeString()
                dashboardName = `New Dashboard (${timestamp})`
            } else {
                // For API templates, use template name
                dashboardName = template.name || 'New Dashboard'
            }

            console.log('[Create Dashboard] Auto-creating dashboard:', {
                name: dashboardName,
                template: template.id || 'blank',
                isBlank: isBlank
            })

            // Call backend API to create dashboard
            const response = await projectsApi.createDashboard(currentProject.id, {
                name: dashboardName,
                template: template.id || 'blank',
                description: template.description || `Created from ${template.name || 'Blank'} Template`
            })

            console.log('[Create Dashboard] Dashboard created successfully:', response)

            // Add the new dashboard to the list
            const newDashboard = response.dashboard || response
            setDashboards([...dashboards, newDashboard])

            // Show success message
            setDashboardSuccess(`Dashboard "${dashboardName}" created successfully!`)

            // Close modal and reset state
            setTimeout(() => {
                setShowAddDashboardModal(false)
                setShowDashboardDetailsModal(false)
                setSelectedTemplateForDetails(null)
                setDashboardName('')
                setDashboardDetails('')
                setDashboardSuccess('')
            }, 1500)
        } catch (error) {
            console.error('[Create Dashboard] Error:', error)
            setDashboardError(error.response?.data?.message || error.message || 'Error creating dashboard')
        } finally {
            setIsCreatingDashboard(false)
        }
    }

    // Dashboard archive/reactivate/delete handlers with backend persistence
    const handleArchiveDashboard = async (dashboardId) => {
        const dashboard = dashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to archive dashboard
                await projectsApi.archiveDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setDashboards(dashboards.filter(d => d.id !== dashboardId))
                setArchivedDashboards([...archivedDashboards, dashboard])
                setDashboardSuccess(`Dashboard "${dashboard.name}" archived successfully!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error archiving dashboard:', error)
                setDashboardError(`Failed to archive dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    const handleReactivateDashboard = async (dashboardId) => {
        const dashboard = archivedDashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to reactivate dashboard
                await projectsApi.reactivateDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setArchivedDashboards(archivedDashboards.filter(d => d.id !== dashboardId))
                setDashboards([...dashboards, dashboard])
                setDashboardSuccess(`Dashboard "${dashboard.name}" reactivated successfully!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error reactivating dashboard:', error)
                setDashboardError(`Failed to reactivate dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    const handlePermanentlyDeleteDashboard = async (dashboardId) => {
        const dashboard = archivedDashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to delete dashboard permanently
                await projectsApi.deleteDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setArchivedDashboards(archivedDashboards.filter(d => d.id !== dashboardId))
                setDashboardSuccess(`Dashboard "${dashboard.name}" permanently deleted!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error deleting dashboard:', error)
                setDashboardError(`Failed to delete dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    // Fetch photos for a specific album and store in albumPhotos state
    const fetchAlbumPhotos = async (albumId) => {
        try {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = localStorage.getItem(JWT_STORAGE_KEY)
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
            const res = await fetch(`${API_URL}/api/photo/photo/?album=${albumId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            if (!res.ok) return
            const data = await res.json()
            const photos = (data.results || data || []).map((p, i) => ({
                id: p.id || i,
                _fromApi: true,
                src: p.photo_url || '',
                largeSrc: p.photo_url || '',
                category: p.upload_type || 'Photo',
                title: p.name || p.original_filename || 'Untitled',
                description: p.notes || '',
                originalFilename: p.original_filename || '',
                cameraSN: p.device_serial_number || 'N/A',
                tags: 'N/A',
                album: '',
                uploadedBy: p.author || '',
                date: p.date_uploaded || '',
                dimensions: (p.width && p.height) ? `${p.width} x ${p.height}` : '',
                size: p.size ? `${(p.size / 1024).toFixed(2)} KB` : '',
                totalSize: '',
                location: ''
            }))
            setAlbumPhotos(prev => ({ ...prev, [albumId]: photos }))
        } catch (e) {
            console.error('[Gallery] Error fetching album photos:', e)
        }
    }

    // Handle gallery tab click — sync URL param and fetch album photos on demand
    const handleGalleryTabClick = (tab) => {
        setGalleryTab(tab)
        setGalleryPage(1)
        // Sync to URL
        const params = new URLSearchParams(location.search)
        if (tab === 'All Photos') {
            params.delete('gallery_tab')
        } else {
            params.set('gallery_tab', tab)
        }
        const newSearch = params.toString()
        navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true })
        // Fetch album photos if this is an API album tab
        const album = projectAlbums.find(a => a.name === tab)
        if (album && !albumPhotos[album.id]?.length) {
            fetchAlbumPhotos(album.id)
        }
    }

    // Handle album creation
    const handleCreateAlbum = async () => {
        if (!albumName.trim()) {
            setAlbumError('Album name is required')
            return
        }
        const _albumSecErr = scanInput(albumName)
        if (_albumSecErr) { setAlbumError(_albumSecErr); return }

        if (albumImages.length === 0) {
            setAlbumError('Please upload at least one image')
            return
        }

        setIsCreatingAlbum(true)
        setAlbumError('')
        setAlbumSuccess('')

        try {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = localStorage.getItem(JWT_STORAGE_KEY)
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

            // Create FormData for multipart upload
            const formData = new FormData()
            formData.append('name', albumName)
            formData.append('project', currentProject.id)
            formData.append('public', 1)

            // Add images with keys file_0, file_1, …
            albumImages.forEach((file, index) => {
                formData.append(`file_${index}`, file)
            })

            // POST to the combined upload endpoint
            const albumRes = await fetch(`${API_URL}/api/photo/album/upload/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            })

            if (!albumRes.ok) {
                const errData = await albumRes.json().catch(() => ({}))
                throw new Error(errData.detail || 'Failed to create album')
            }

            const result = await albumRes.json()
            console.log('[Create Album] Created:', result)

            const newAlbum = {
                id: result.album.id,
                name: result.album.name,
                project: result.album.project_id,
                public: result.album.public,
            }

            // Add new album to list and store its photos
            setProjectAlbums(prev => [...prev, newAlbum])
            if (result.photos?.length > 0) {
                const mappedPhotos = result.photos.map((p, i) => ({
                    id: p.id || i,
                    src: p.photo_url || '',
                    largeSrc: p.photo_url || '',
                    category: 'Photo',
                    title: p.name || 'Untitled',
                    description: '',
                    originalFilename: p.name || '',
                    cameraSN: 'N/A',
                    tags: 'N/A',
                    album: newAlbum.name,
                    uploadedBy: '',
                    date: '',
                    dimensions: '',
                    size: '',
                    totalSize: '',
                    location: ''
                }))
                setAlbumPhotos(prev => ({ ...prev, [newAlbum.id]: mappedPhotos }))
            }

            setAlbumSuccess('Album created successfully!')
            setTimeout(() => {
                setShowCreateAlbumModal(false)
                setAlbumName('')
                setAlbumImages([])
                setAlbumError('')
                setAlbumSuccess('')
                setGalleryTab(newAlbum.name)
                setGalleryPage(1)
            }, 1000)
        } catch (error) {
            console.error('[Create Album] Error:', error)
            setAlbumError(error.message || 'Failed to create album')
        } finally {
            setIsCreatingAlbum(false)
        }
    }

    // Handle album image upload
    const handleAlbumImageUpload = (e) => {
        const files = Array.from(e.target.files || [])
        setAlbumImages(prev => [...prev, ...files])
        setAlbumError('')
    }

    // Remove image from album
    const removeAlbumImage = (index) => {
        setAlbumImages(prev => prev.filter((_, i) => i !== index))
    }

    // Use dynamic projectSites instead of hardcoded sites
    // (nowLabel + customerTz are declared near the top of the component, above
    //  the early `return <NoActiveProject />` paths — see the hooks block there.)

    // ── Upload to Gallery handlers ────────────────────────────────────────
    const handleUploadFilesSelect = (files) => {
        const newItems = Array.from(files).map(file => ({
            id: Math.random().toString(36).slice(2),
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            album_id: '',
            title: '',
            tags: '',
            location: '',
            description: '',
            uploading: false,
            done: false,
            error: ''
        }))
        setUploadItems(prev => [...prev, ...newItems])
    }

    const uploadSingleItem = async (item) => {
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = localStorage.getItem(JWT_STORAGE_KEY)
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

        let resolvedAlbumId = item.album_id
        // If album value is a name string (not a numeric DB id), find or create the album
        if (item.album_id && isNaN(parseInt(item.album_id, 10))) {
            const albumName = item.album_id
            const existing = projectAlbums.find(a => a.name === albumName)
            if (existing) {
                resolvedAlbumId = existing.id
            } else {
                const albumRes = await fetch(`${API_URL}/api/photo/album/`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: albumName, project: currentProject.id, public: 1 })
                })
                if (albumRes.ok) {
                    const albumData = await albumRes.json()
                    resolvedAlbumId = albumData.id
                    setProjectAlbums(prev => [...prev, albumData])
                } else {
                    resolvedAlbumId = ''
                }
            }
        }

        const formData = new FormData()
        formData.append('project', currentProject.id)
        formData.append('file_0', item.file)
        formData.append('title_0', item.title)
        formData.append('description_0', item.description)
        formData.append('tags_0', item.tags)
        if (resolvedAlbumId) formData.append('album_id_0', resolvedAlbumId)
        const res = await fetch(`${API_URL}/api/photo/photo/upload/`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        })
        if (!res.ok) throw new Error('Upload failed')
        return res.json()
    }

    const handleUploadItem = async (itemId) => {
        setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: true, error: '' } : i))
        try {
            const item = uploadItems.find(i => i.id === itemId)
            await uploadSingleItem(item)
            setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: false, done: true } : i))
        } catch (e) {
            setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: false, error: e.message } : i))
        }
    }

    const handleStartUpload = async () => {
        const pending = uploadItems.filter(i => !i.done)
        for (const item of pending) {
            await handleUploadItem(item.id)
        }
        // Refresh album photos if on an album tab
        setTimeout(() => {
            setShowUploadModal(false)
            setUploadItems([])
            // Refresh gallery
            setApiPhotos([])
            if (galleryTab !== 'All Photos') {
                const album = projectAlbums.find(a => a.name === galleryTab)
                if (album) {
                    setAlbumPhotos(prev => ({ ...prev, [album.id]: [] }))
                    fetchAlbumPhotos(album.id)
                }
            }
        }, 800)
    }
    // ── End Upload to Gallery handlers ───────────────────────────────────

    // ── Album edit / delete ───────────────────────────────────────────────
    const handleEditAlbumSave = async () => {
        if (!editAlbumModal || !editAlbumName.trim()) return
        const secErr = scanInput(editAlbumName)
        if (secErr) { alert(secErr); return }
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = localStorage.getItem(JWT_STORAGE_KEY)
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
        try {
            const res = await fetch(`${API_URL}/api/photo/album/${editAlbumModal.id}/`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editAlbumName.trim() })
            })
            if (!res.ok) throw new Error('Failed to rename album')
            setProjectAlbums(prev => prev.map(a => a.id === editAlbumModal.id ? { ...a, name: editAlbumName.trim() } : a))
            if (galleryTab === editAlbumModal.name) {
                setGalleryTab(editAlbumName.trim())
            }
            setEditAlbumModal(null)
        } catch (e) {
            alert(e.message)
        }
    }

    const handleDeleteAlbum = async (albumId, albumName) => {
        if (!window.confirm(`Delete album "${albumName}"? The album will be removed but photos will remain in All Photos.`)) return
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = localStorage.getItem(JWT_STORAGE_KEY)
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
        try {
            const res = await fetch(`${API_URL}/api/photo/album/${albumId}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            if (!res.ok) throw new Error('Failed to delete album')
            setProjectAlbums(prev => prev.filter(a => a.id !== albumId))
            if (galleryTab === albumName) {
                setGalleryTab('All Photos')
                navigate(location.pathname, { replace: true })
            }
        } catch (e) {
            alert(e.message)
        }
    }
    // ── End Album edit / delete ───────────────────────────────────────────

    // ── Photo save / delete ───────────────────────────────────────────────
    const handleSavePhotoInfo = async () => {
        if (!editingPhoto) return
        const secErr = scanInput(editFormData.title) || scanInput(editFormData.description) || scanInput(editFormData.tags)
        if (secErr) { alert(secErr); return }
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = localStorage.getItem(JWT_STORAGE_KEY)
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

        let newSrc = editingPhoto.src

        if (editingPhoto._fromApi) {
            try {
                // If a new image file was chosen, upload it as a replacement
                if (editPhotoFile) {
                    const fd = new FormData()
                    fd.append('project', currentProject.id)
                    fd.append('file_0', editPhotoFile)
                    fd.append('title_0', editFormData.title)
                    fd.append('description_0', editFormData.description)
                    const uploadRes = await fetch(`${API_URL}/api/photo/photo/upload/`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: fd
                    })
                    if (uploadRes.ok) {
                        const uploadData = await uploadRes.json()
                        const newPhoto = uploadData.photos?.[0]
                        if (newPhoto?.photo_url) newSrc = newPhoto.photo_url
                        // Delete old photo from backend
                        await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/`, {
                            method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                        })
                    }
                } else {
                    // Update metadata (title + notes)
                    await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/`, {
                        method: 'PATCH',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: editFormData.title, notes: editFormData.description })
                    })
                }

                // Update album assignment if album changed
                const selectedAlbumObj = projectAlbums.find(a => a.name === editFormData.album)
                const prevAlbumObj = projectAlbums.find(a => a.name === editingPhoto.album)
                if (editFormData.album !== (editingPhoto.album || '')) {
                    await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/assign-album/`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ album_id: selectedAlbumObj ? selectedAlbumObj.id : null })
                    })
                    // Invalidate cached album photos so they refresh on next tab click
                    if (prevAlbumObj) {
                        setAlbumPhotos(prev => { const n = { ...prev }; delete n[prevAlbumObj.id]; return n })
                    }
                    if (selectedAlbumObj) {
                        setAlbumPhotos(prev => { const n = { ...prev }; delete n[selectedAlbumObj.id]; return n })
                    }
                }
            } catch (e) {
                console.error('Failed to save photo info:', e)
            }
        }

        const updated = { ...editingPhoto, ...editFormData, src: newSrc, largeSrc: newSrc }
        setPhotoEditsMap(prev => ({ ...prev, [editingPhoto.id]: updated }))
        setSelectedPhoto(updated)
        setEditPhotoFile(null)
        setEditPhotoPreview(null)
        setShowEditMediaModal(false)
    }

    const handleDeletePhoto = async (photo) => {
        if (!window.confirm(`Delete "${photo.title}"? This cannot be undone.`)) return
        if (photo._fromApi) {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = localStorage.getItem(JWT_STORAGE_KEY)
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
            try {
                await fetch(`${API_URL}/api/photo/photo/${photo.id}/`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            } catch (e) {
                console.error('Failed to delete photo:', e)
            }
        }
        setApiPhotos(prev => prev.filter(p => p.id !== photo.id))
        setAlbumPhotos(prev => {
            const next = { ...prev }
            Object.keys(next).forEach(k => { next[k] = next[k].filter(p => p.id !== photo.id) })
            return next
        })
        setPhotoEditsMap(prev => { const n = { ...prev }; delete n[photo.id]; return n })
        setSelectedPhoto(null)
    }
    // ── End Photo save / delete ───────────────────────────────────────────

    // Create Site form validation (computed fresh every render)
    const _siteNameVal = newSiteName.trim()
    const _siteNamePattern = /^[A-Za-z][A-Za-z0-9 \-_]*$/
    const _siteNameInvalid = _siteNameVal !== '' && !_siteNamePattern.test(_siteNameVal)
    const _siteNameFieldError = _siteNameInvalid ? 'Must begin with a letter; may contain letters, digits, spaces, dashes, and underscores.' : ''
    const _siteLatVal = newSiteLatitude.trim()
    const _siteLonVal = newSiteLongitude.trim()
    const _siteLatError = _siteLatVal !== '' && isNaN(parseFloat(_siteLatVal)) ? 'Enter a valid decimal number, e.g. 39.782875' : ''
    const _siteLonError = _siteLonVal !== '' && isNaN(parseFloat(_siteLonVal)) ? 'Enter a valid decimal number, e.g. -83.993666' : ''
    const _isSiteFormValid = _siteNameVal !== '' && !_siteNameInvalid && !_siteLatError && !_siteLonError

    return (
    <DashboardContext.Provider value={{
      selectedParameters,
      // Project-scoped data exposed to widgets so they can render without
      // every render site having to plumb it through props. Used by the
      // GPS widget (Project Map dashboard) which needs both the site list
      // and the active project id to render markers + persist per-project
      // map config.
      sites: projectSites,
      projectId: currentProject?.id,
      dateRange: dateRangeGraph,
      activePeriod,
      handlePeriodClick,
      setDateRange: setDateRangeGraph,
      isCustomizing: isCustomizingDashboard,
      // Widgets call this to deep-link into the Gallery tab. Optional
      // `albumName` lands the user inside that album's sub-tab (galleryTab)
      // rather than the default "All Photos" view — used by the Gallery
      // widget's "View All" so the user lands directly on the album the
      // widget is currently displaying.
      goToGallery: (albumName) => {
        setActiveTab('Gallery')
        setGalleryTab(albumName || 'All Photos')
        setGalleryPage(1)
        setSelectedPhoto(null)
        navigate(location.pathname, { replace: true })
      },
    }}>
        <div className="flex flex-col bg-gray-100 min-h-screen w-full">
      {/* White Header with Logo and Navigation — matches XD design */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 h-[60px]">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img src="/images/dashboardlogo.png" alt="Fondriest" className="h-10 w-auto" />
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-end gap-8 flex-1 justify-center self-stretch">
            {['Dashboard', 'Data Analytics', 'Report', 'Gallery', 'Settings', 'Live'].map((tab) => (
              <div
                key={tab}
                className="relative h-full flex items-end"
                onMouseEnter={() => setHoveredTab(tab)}
                onMouseLeave={() => {
                  setHoveredTab(null)
                  if (tab === 'Live') setLiveMenuFocused(false)
                }}
              >
                <button
                  onClick={() => {
                    setActiveTab(tab)
                    if (tab === 'Gallery') {
                      // Always start fresh on Gallery — reset to All Photos
                      setGalleryTab('All Photos')
                      setGalleryPage(1)
                      setSelectedPhoto(null)
                      navigate(location.pathname, { replace: true })
                    }
                  }}
                  data-tab-name={tab}
                  onFocus={() => { if (tab === 'Live') setLiveMenuFocused(true) }}
                  onKeyDown={(e) => {
                    if (tab === 'Live' && (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter' || e.key === 'Tab')) {
                      if (e.key === 'Tab') {
                        e.preventDefault()
                        setLiveMenuFocused(true)
                        setTimeout(() => { liveMenuItemsRefs.current[0]?.focus() }, 0)
                      } else {
                        e.preventDefault()
                        setLiveMenuFocused(true)
                        setTimeout(() => { liveMenuItemsRefs.current[0]?.focus() }, 0)
                      }
                    }
                  }}
                  className={`text-sm font-semibold pb-3 border-b-2 transition-colors flex items-center gap-1 whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-[#358ECD] text-[#358ECD]'
                      : 'border-transparent text-[#4D4D4D] hover:text-[#358ECD] hover:border-[#358ECD]'
                  }`}
                >
                  {tab}
                  {['Dashboard', 'Data Analytics', 'Report', 'Settings', 'Live'].includes(tab) && (
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${hoveredTab === tab ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {/* Dashboard Dropdown */}
                {tab === 'Dashboard' && hoveredTab === tab && (
                  <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[200px] py-1.5 overflow-hidden">
                    {dashboardMenuItems.map((item, index) => {
                      const isConfigItem = item === 'Config Dashboards' || item === 'Configure Dashboards'
                      const isFirstConfig = isConfigItem && index > 0
                      return (
                        <div key={index}>
                          {isFirstConfig && <div className="border-t border-gray-200 my-1.5" />}
                          <button
                            onClick={() => selectMenuItem(index)}
                            className={`w-full text-left px-4 py-2 text-sm transition ${
                              index === currentMenuIndex && !isConfigItem
                                ? 'bg-[#EBF5FB] text-[#358ECD] font-medium'
                                : 'text-[#4D4D4D] hover:bg-gray-50'
                            }`}
                          >
                            {item}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Data Analytics Dropdown */}
                {tab === 'Data Analytics' && hoveredTab === tab && (
                  <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[180px] py-1.5">
                    {dataAnalyticsMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => { setCurrentDataAnalyticsIndex(index); setActiveTab('Data Analytics') }}
                        className={`w-full text-left px-4 py-2 text-sm transition ${
                          index === currentDataAnalyticsIndex
                            ? 'bg-[#EBF5FB] text-[#358ECD] font-medium'
                            : 'text-[#4D4D4D] hover:bg-gray-50'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}

                {/* Report Dropdown */}
                {tab === 'Report' && hoveredTab === tab && (
                  <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[200px] py-1.5">
                    {reportMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => { setCurrentReportIndex(index); setActiveTab('Report') }}
                        className="w-full text-left px-4 py-2 text-sm text-[#4D4D4D] hover:bg-gray-50 transition"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}

                {/* Settings Dropdown */}
                {tab === 'Settings' && hoveredTab === tab && (
                  <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[180px] py-1.5">
                    {settingsMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => { setCurrentSettingsIndex(index); setActiveTab('Settings'); if (item === 'Project') setProjectEditMode(false) }}
                        className="w-full text-left px-4 py-2 text-sm text-[#4D4D4D] hover:bg-gray-50 transition"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}

                {/* Live Dropdown */}
                {tab === 'Live' && (hoveredTab === tab || liveMenuFocused) && (
                  <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[180px] py-1.5">
                    {liveMenuItems.map((item, index) => (
                      <button
                        key={index}
                        ref={(el) => { if (el) liveMenuItemsRefs.current[index] = el }}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab') {
                            e.preventDefault()
                            if (e.shiftKey) {
                              index > 0 ? liveMenuItemsRefs.current[index - 1]?.focus() : document.querySelector('[data-tab-name="Live"]')?.focus()
                            } else {
                              index < liveMenuItems.length - 1 ? liveMenuItemsRefs.current[index + 1]?.focus() : liveMenuItemsRefs.current[0]?.focus()
                            }
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setLiveMenuFocused(false)
                            document.querySelector('[data-tab-name="Live"]')?.focus()
                          } else if (e.key === 'ArrowDown') {
                            e.preventDefault()
                            index < liveMenuItems.length - 1 ? liveMenuItemsRefs.current[index + 1]?.focus() : liveMenuItemsRefs.current[0]?.focus()
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault()
                            index > 0 ? liveMenuItemsRefs.current[index - 1]?.focus() : document.querySelector('[data-tab-name="Live"]')?.focus()
                          }
                        }}
                        onFocus={() => setLiveMenuFocused(true)}
                        onClick={() => { setCurrentLiveIndex(index); setActiveTab('Live'); setLiveMenuFocused(false) }}
                        className={`w-full text-left px-4 py-2 text-sm transition ${
                          index === currentLiveIndex
                            ? 'bg-[#EBF5FB] text-[#358ECD] font-medium'
                            : 'text-[#4D4D4D] hover:bg-gray-50'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right: As of date + refresh */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-6">
            <span className="text-sm text-[#4D4D4D] whitespace-nowrap">As of {nowLabel}</span>
            <button onClick={() => window.location.reload()} className="hover:opacity-70 transition p-1" title="Refresh">
              <img src="/images/ICON_Refresh.png" alt="Refresh" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden mt-4 px-4">
        {/* Toggle Sidebar Button - hidden on Gallery tab */}
        {!isSidebarVisible && activeTab !== 'Gallery' && (
          <button
            onClick={() => setIsSidebarVisible(true)}
            className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 rounded hover:bg-gray-50 transition mr-2"
            title="Open Sidebar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Left Sidebar - hidden on Gallery tab */}
        <div className={`bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300 ease-in-out ${
          activeTab === 'Gallery' ? 'max-w-0 flex-0 opacity-0 pointer-events-none' :
          isSidebarVisible ? 'max-w-[17%] flex-[17%] opacity-100' : 'max-w-0 flex-0 opacity-0'
        }`}>
          {/* Sidebar Header */}
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#4D4D4D]">All Sites</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setIsEditingSidebar(!isEditingSidebar)
                }}
                className="p-1 hover:bg-gray-100 rounded transition"
                title="Edit Sites"
              >
                <img src="/images/ICON_EditAllSitesBar.png" alt="Edit" className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsSidebarVisible(false)}
                className="p-1 hover:bg-gray-100 rounded transition"
                title="Close Sidebar"
              >
                <img src="/images/left_panel_close_24dp_AFAFAF_FILL1_wght400_GRAD0_opsz24.png" alt="Close" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search Parameters */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search Parameters"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
              <Search className="absolute right-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>

          {/* Sidebar Menu */}
          <div className="p-3 space-y-0.5">
            {loadingSites ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="ml-2 text-sm text-gray-600">Loading sites...</span>
              </div>
            ) : projectSites.length === 0 ? (
              <div className="py-4 px-2 text-center text-sm text-gray-500">
                No sites available for this project
              </div>
            ) : (
              <>
                {/* Edit Mode View */}
                {isEditingSidebar ? (
                  <div className="space-y-1">
                    {getFilteredSites().map((site) => (
                      <div
                        key={site.id}
                        className={`space-y-1 rounded transition ${
                          dragOverSiteId === site.id ? 'border-2 border-blue-400 bg-blue-50' : ''
                        } ${draggedSiteId === site.id ? 'opacity-40' : ''}`}
                        draggable
                        onDragStart={(e) => handleSiteDragStart(e, site.id)}
                        onDragOver={(e) => handleSiteDragOver(e, site.id)}
                        onDrop={(e) => handleSiteDrop(e, site.id)}
                        onDragEnd={handleSiteDragEnd}
                      >
                        {/* Site Name - Edit Mode */}
                        <div className="w-full flex items-center px-2 py-1.5 hover:bg-gray-50 rounded transition">
                          <span className="text-gray-400 text-xs flex-shrink-0 mr-1.5 cursor-grab active:cursor-grabbing">☰</span>
                          <button
                            onClick={() => toggleMenu(site.id)}
                            className="flex items-center gap-1 flex-1 min-w-0"
                          >
                            <span className="flex-1 text-left font-medium text-gray-700 text-xs truncate">{site.name}</span>
                            {site.devices && site.devices.length > 0 && (
                              <ChevronDown
                                className={`w-3 h-3 text-gray-500 transition-transform flex-shrink-0 ${
                                  expandedMenus.includes(site.id) ? 'rotate-180' : ''
                                }`}
                              />
                            )}
                          </button>
                          {/* Configure icon — always visible in edit mode */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedSiteForDevice(site.id)
                              setConfigureModalTab('Devices')
                              setNewDeviceName('')
                              setDeviceError('')
                              setSuccessMessage('')
                              setModalProjectSites(JSON.parse(JSON.stringify(projectSites)))
                              setSensorSelectedDevice('')
                              setSensorProfileName('Default')
                              setSensorProfiles([{ id: 1, name: 'Default' }])
                              setSelectedSensorProfile(null)
                              setShowSensorProfileForm(false)
                              setShowAddDeviceModal(true)
                            }}
                            className="ml-1 flex-shrink-0 p-1 rounded hover:bg-gray-200 transition"
                            title={`Configure ${site.name}`}
                          >
                            <Edit2 className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>

                        {/* Devices for this site - Edit Mode */}
                        {site.devices && expandedMenus.includes(site.id) && (
                          <div className="ml-2 space-y-1">
                            {site.devices.map((device) => (
                              <div key={device.id}>
                                <button
                                  onClick={() => toggleMenu(`device-${device.id}`)}
                                  className="w-full flex items-center gap-2 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 transition"
                                >
                                  <span className="text-gray-500 text-xs">☰</span>
                                  <span className="flex-1 text-left truncate font-semibold">{device.name}</span>
                                  {device.sensors && device.sensors.length > 0 && (
                                    <ChevronDown 
                                      className={`w-2.5 h-2.5 text-gray-400 transition-transform flex-shrink-0 ${
                                        expandedMenus.includes(`device-${device.id}`) ? 'rotate-180' : ''
                                      }`}
                                    />
                                  )}
                                </button>

                                {/* Parameters for this device - Edit Mode */}
                                {device.sensors && expandedMenus.includes(`device-${device.id}`) && (
                                  <div className="ml-3 bg-gray-50 rounded py-0.5 mt-0.5 space-y-0">
                                    {device.sensors.map((sensor) => (
                                      <div key={sensor.id}>
                                        {sensor.parameters && sensor.parameters.length > 0 && (
                                          <>
                                            <div className="px-2 py-1 text-xs text-gray-500 font-medium">{sensor.name}</div>
                                            {sensor.parameters.map((param) => (
                                              <div
                                                key={param.id}
                                                className="px-2 py-0.5 text-xs text-gray-600"
                                              >
                                                {param.name}
                                              </div>
                                            ))}
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Trash Drop Zone */}
                    <div
                      onDragOver={handleTrashDragOver}
                      onDragLeave={handleTrashDragLeave}
                      onDrop={handleTrashDrop}
                      className={`my-3 py-3 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed text-xs transition-all ${
                        isDragOverTrash
                          ? 'border-red-400 bg-red-50 text-red-600'
                          : 'border-gray-300 text-gray-400'
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                      {isDragOverTrash ? 'Drop to delete' : 'Drag here to delete'}
                    </div>

                    {/* Add Site, Parameter Configuration, Link External Site */}
                    <div className="space-y-1 mt-1">
                      <button
                        onClick={() => {
                          setShowCreateSiteModal(true)
                          setNewSiteName('')
                          setNewSiteDescription('')
                          setNewSiteLatitude('')
                          setNewSiteLongitude('')
                          setUseDeviceGPS(false)
                        }}
                        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition font-medium">
                        <span className="font-bold text-sm leading-none">+</span>
                        Add Site
                      </button>
                      <button
                        onClick={() => setShowParamConfigModal(true)}
                        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition font-medium"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"/>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                        Parameter Configuration
                      </button>
                      <button className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition font-medium">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                        Link External Site
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal View — matches XD All Sites sidebar:
                       Site      bold black,   no checkbox            left edge 0
                       Device    semibold,     no checkbox            left edge 0, smaller
                       Sensor    regular,      checkbox + chevron     left edge 0 (checkbox at edge)
                       Parameter regular,      checkbox               indented ~20px from sensor
                  */
                  getFilteredSites().map((site) => (
                    <div key={site.id} className="space-y-1.5">
                      {/* Site — bold heading at the very left edge */}
                      <h3 className="pt-2 text-[15px] font-bold text-gray-900 leading-tight">
                        {site.name}
                      </h3>

                      {/* Devices for this site — always rendered. No
                          collapse trigger on site / device per XD. */}
                      {(site.devices || []).map((device) => (
                        <div key={device.id} className="space-y-1">
                          {/* Device — bold sub-heading per XD (slightly
                              smaller than Site, but same weight + dark
                              colour). XD shows "<uid> Data Logger" — we
                              append the suffix when the device name
                              doesn't already carry it. */}
                          <h4 className="pt-1 text-[12px] font-bold text-gray-800 leading-tight">
                            {/Data Logger$/i.test(device.name || '')
                              ? device.name
                              : `${device.name || `Device ${device.id}`} Data Logger`}
                          </h4>

                          {/* Sensors — checkbox sits at the same left edge
                              as the device heading (no ml-* on the wrapper) */}
                          {(device.sensors || []).map((sensor) => (
                            <div key={sensor.id}>
                              <button
                                onClick={() => toggleMenu(`sensor-${sensor.id}`)}
                                className="w-full flex items-center gap-2 py-1 text-[13px] text-gray-700 hover:bg-gray-50 rounded transition"
                              >
                                <input
                                  type="checkbox"
                                  checked={checkedItems[sensor.id] || false}
                                  onChange={() => toggleCheckbox(sensor.id)}
                                  className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="flex-1 text-left truncate font-medium">{sensor.name}</span>
                                {sensor.parameters && sensor.parameters.length > 0 && (
                                  <ChevronDown
                                    className={`w-3.5 h-3.5 text-gray-500 transition-transform flex-shrink-0 mr-1 ${
                                      expandedMenus.includes(`sensor-${sensor.id}`) ? 'rotate-180' : ''
                                    }`}
                                  />
                                )}
                              </button>

                              {/* Parameters — indented ONE step from the
                                  sensor's checkbox column (≈ ml-6 = 24px). */}
                              {sensor.parameters && expandedMenus.includes(`sensor-${sensor.id}`) && (
                                <div className="ml-6 space-y-0">
                                  {sensor.parameters.map((param) => (
                                    <button
                                      key={param.id}
                                      onClick={() => toggleCheckbox(param.id)}
                                      className="w-full flex items-center gap-2 py-1 text-[13px] text-gray-600 hover:bg-gray-50 rounded transition"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checkedItems[param.id] || false}
                                        onChange={() => toggleCheckbox(param.id)}
                                        className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="truncate text-left flex-1 font-normal">{param.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </>
            )}
            {searchQuery.trim() && getFilteredSites().length === 0 && (
              <div className="py-4 px-2 text-center text-sm text-gray-500">
                No results found for "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 pl-4 pr-4">
          {/* Current Project Title */}
          {currentProject && (
            <div className="py-2 px-4 mb-3">
              <h1 className="text-2xl font-bold text-gray-800">{currentProject.name}</h1>
              {projectDescription && <p className="text-sm text-gray-600 mt-1">{projectDescription}</p>}
            </div>
          )}
          
          {/* Filter Bar - Only for Dashboard */}
          {activeTab === 'Dashboard' && (
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
              {/* Customize mode toolbar */}
              {isCustomizingDashboard ? (
                <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
                  {/* Active dashboard view tab (kept visible in customize mode to match XD) */}
                  <div className="flex items-center gap-1.5 border-b-2 border-[#358ECD] pb-1.5 mr-1">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M0 8.88892V0H8.88889V8.88892H0ZM0 20V11.1111H8.88889V20H0ZM11.1111 8.88892V0H20V8.88892H11.1111ZM11.1111 20V11.1111H20V20H11.1111Z" fill="#358ECD"/>
                    </svg>
                    <span className="text-sm font-semibold text-[#358ECD]">{dashboardMenuItems[currentMenuIndex]}</span>
                  </div>
                  <button
                    onClick={() => setShowPresentationSettings(true)}
                    title="Configure which dashboards play in Presentation mode"
                    className="flex items-center gap-1.5 border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Presentation
                  </button>
                  <span className="flex items-center gap-1.5 text-xs text-gray-400 ml-1">
                    <GripVertical className="w-3.5 h-3.5" />
                    Drag Widgets to Reorder
                  </span>
                  <div className="flex-1" />
                  <button
                    onClick={() => setShowAddWidgetsModal(true)}
                    className="flex items-center gap-1.5 border border-[#358ECD] text-[#358ECD] px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#EBF5FB] transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Widgets
                  </button>
                  <button
                    onClick={() => setShowResetDashboardModal(true)}
                    className="flex items-center gap-1.5 border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reset Dashboard
                  </button>
                  <button
                    onClick={async () => {
                      setIsSavingCustomization(true)
                      try { await gridRef.current?.save() } catch {}
                      setIsSavingCustomization(false)
                      setIsCustomizingDashboard(false)
                    }}
                    disabled={isSavingCustomization}
                    className="flex items-center gap-1.5 bg-[#358ECD] text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-[#2d7ab5] disabled:opacity-60 transition"
                  >
                    {isSavingCustomization ? 'Saving…' : 'Save Customization'}
                  </button>
                  <button
                    onClick={() => setIsCustomizingDashboard(false)}
                    className="border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                /* Normal view mode */
                <div className="flex items-center justify-between px-4 py-2.5">
                  {/* Left: Overview tab + navigation */}
                  <div className="flex items-center gap-3">
                    {/* Active dashboard name as tab */}
                    <div className="flex items-center gap-1.5 border-b-2 border-[#358ECD] pb-1.5 mr-1">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M0 8.88892V0H8.88889V8.88892H0ZM0 20V11.1111H8.88889V20H0ZM11.1111 8.88892V0H20V8.88892H11.1111ZM11.1111 20V11.1111H20V20H11.1111Z" fill="#358ECD"/>
                      </svg>
                      <span className="text-sm font-semibold text-[#358ECD]">{dashboardMenuItems[currentMenuIndex]}</span>
                    </div>
                    {/* Previous / Next / Play */}
                    <div className="flex items-center rounded-md border border-gray-200 divide-x divide-gray-200 bg-white shadow-sm overflow-hidden">
                      <button onClick={handlePrevious} className="px-4 py-1.5 text-sm text-[#4D4D4D] hover:bg-gray-50 transition">Previous</button>
                      <button onClick={handleNext}     className="px-4 py-1.5 text-sm text-[#4D4D4D] hover:bg-gray-50 transition">Next</button>
                      <button
                        onClick={handlePlay}
                        title={
                          isPlaying
                            ? 'Stop presentation'
                            : presentationRotation.length === 0
                              ? 'No dashboards selected — open Presentation to configure'
                              : `Play presentation (${presentationRotation.length} dashboard${presentationRotation.length === 1 ? '' : 's'}, ${presentationConfig.pauseSeconds}s each)`
                        }
                        className={`flex items-center gap-1.5 px-4 py-1.5 text-sm transition ${
                          isPlaying
                            ? 'bg-[#358ECD] text-white hover:bg-[#2d7ab5]'
                            : 'text-[#4D4D4D] hover:bg-gray-50'
                        }`}
                      >
                        {isPlaying ? (
                          /* Pause icon */
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <rect x="3" y="3" width="5" height="14" rx="1" fill="currentColor"/>
                            <rect x="12" y="3" width="5" height="14" rx="1" fill="currentColor"/>
                          </svg>
                        ) : (
                          /* Play icon */
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 14.5L14.5 10L7.5 5.5V14.5ZM10 20C8.617 20 7.317 19.738 6.1 19.213C4.883 18.688 3.825 17.975 2.925 17.075C2.025 16.175 1.313 15.117 0.788 13.9C0.263 12.683 0 11.383 0 10C0 8.617 0.263 7.317 0.788 6.1C1.313 4.883 2.025 3.825 2.925 2.925C3.825 2.025 4.883 1.313 6.1 0.788C7.317 0.263 8.617 0 10 0C11.383 0 12.683 0.263 13.9 0.788C15.117 1.313 16.175 2.025 17.075 2.925C17.975 3.825 18.688 4.883 19.213 6.1C19.738 7.317 20 8.617 20 10C20 11.383 19.738 12.683 19.213 13.9C18.688 15.117 17.975 16.175 17.075 17.075C16.175 17.975 15.117 18.688 13.9 19.213C12.683 19.738 11.383 20 10 20ZM10 18C12.233 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.233 18 10C18 7.767 17.225 5.875 15.675 4.325C14.125 2.775 12.233 2 10 2C7.767 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.767 2 10C2 12.233 2.775 14.125 4.325 15.675C5.875 17.225 7.767 18 10 18Z" fill="#888888"/>
                          </svg>
                        )}
                        {isPlaying ? 'Stop' : 'Play'}
                      </button>
                    </div>
                  </div>

                  {/* Right: Customize */}
                  <button
                    onClick={() => setIsCustomizingDashboard(true)}
                    className="flex items-center gap-2 bg-[#358ECD] text-white py-1.5 px-4 rounded-md text-sm font-semibold hover:bg-[#2d7ab5] transition shadow-sm"
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                      <path d="M0 8.88892V0H8.88889V8.88892H0ZM0 20V11.1111H8.88889V20H0ZM11.1111 8.88892V0H20V8.88892H11.1111ZM11.1111 20V11.1111H20V20H11.1111Z" fill="white"/>
                    </svg>
                    Customize
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Main Content Area */}
          <div ref={gridWrapperRefCallback} className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
            {/* Data Analytics View */}
            {activeTab === 'Data Analytics' && (
              <DataAnalyticsView
                currentDataAnalyticsIndex={currentDataAnalyticsIndex}
                setCurrentDataAnalyticsIndex={setCurrentDataAnalyticsIndex}
                dateRangeGraph={dateRangeGraph}
                setDateRangeGraph={setDateRangeGraph}
                activePeriod={activePeriod}
                handlePeriodClick={handlePeriodClick}
                selectedParameters={selectedParameters}
              />
            )}
            {/* Report View */}
            {activeTab === 'Report' && (
              <div className="w-full h-full flex flex-col bg-white">
                {currentReportIndex === 0 && (
                  <div className="flex flex-col h-full overflow-y-auto p-6 space-y-4">
                    {/* Header Section */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-800">Field Station Turbidity Report</h2>
                        <p className="text-sm text-gray-600 mt-1">Pond Buoy | X3-4G-01078</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-gray-600">January 5, 2026 - January 9, 2026</div>
                        {/* Download Dropdown */}
                        <div className="relative group">
                          <button className="text-white px-4 py-2 rounded hover:opacity-90 flex items-center gap-2" style={{ backgroundColor: '#358ECD' }}>
                            ↓ Download
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">CSV</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">PDF</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">PDF and CSV</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">CSV RAIN</button>
                          </div>
                        </div>
                        
                        {/* Share Dropdown */}
                        <div className="relative group">
                          <button className="text-white px-4 py-2 rounded hover:opacity-90 flex items-center gap-2" style={{ backgroundColor: '#358ECD' }}>
                            ⤴ Share
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">Email</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700">FTP/SFTP</button>
                          </div>
                        </div>

                        {/* Edit Button */}
                        <button 
                          onClick={() => setShowCustomizeModal(true)}
                          className="text-gray-600 hover:text-gray-900 p-2 hover:bg-gray-100 rounded transition"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Stats Table */}
                    <div className="bg-white rounded shadow-sm p-4">
                      <h3 className="font-bold text-[#4D4D4D] text-base mb-4">Stats Table</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs text-gray-700 border-collapse">
                          <thead className="bg-gray-50">
                            <tr className="border-b border-gray-300">
                              <th className="text-left p-3 font-semibold border-r border-gray-200">Site</th>
                              <th className="text-left p-3 font-semibold border-r border-gray-200">Device</th>
                              <th className="text-left p-3 font-semibold border-r border-gray-200">Sensor</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Parameter</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Samples</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Min</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Max</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Mean</th>
                              <th className="text-center p-3 font-semibold border-r border-gray-200">Median</th>
                              <th className="text-center p-3 font-semibold">Std</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-200 hover:bg-blue-50">
                              <td className="p-3 border-r border-gray-200">Pond Buoy</td>
                              <td className="p-3 border-r border-gray-200">X3-4G-01078</td>
                              <td className="p-3 border-r border-gray-200">NX260 Turbidity</td>
                              <td className="text-center p-3 border-r border-gray-200">Turbidity (NTU)</td>
                              <td className="text-center p-3 border-r border-gray-200">48</td>
                              <td className="text-center p-3 border-r border-gray-200">3.01</td>
                              <td className="text-center p-3 border-r border-gray-200">7.95</td>
                              <td className="text-center p-3 border-r border-gray-200">3.72</td>
                              <td className="text-center p-3 border-r border-gray-200">3.58</td>
                              <td className="text-center p-3">0.76</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Data Plot */}
                    <div className="bg-white rounded shadow-sm p-4">
                      <h3 className="font-bold text-[#4D4D4D] text-base mb-4">Data Plot</h3>
                      <svg className="w-full" viewBox="0 0 900 250" preserveAspectRatio="xMidYMid meet">
                        <defs>
                          <pattern id="gridReport" width="45" height="25" patternUnits="userSpaceOnUse">
                            <path d="M 45 0 L 0 0 0 25" fill="none" stroke="#f0f0f0" strokeWidth="0.5"/>
                          </pattern>
                        </defs>
                        <rect width="900" height="250" fill="url(#gridReport)" />
                        
                        {/* Y Axis Labels */}
                        <text x="15" y="15" fontSize="11" fill="#666" textAnchor="end">9</text>
                        <text x="15" y="55" fontSize="11" fill="#666" textAnchor="end">6</text>
                        <text x="15" y="95" fontSize="11" fill="#666" textAnchor="end">3</text>
                        <text x="15" y="135" fontSize="11" fill="#666" textAnchor="end">0 -</text>
                        
                        {/* Y Axis */}
                        <line x1="50" y1="10" x2="50" y2="200" stroke="#999" strokeWidth="1" />
                        
                        {/* X Axis */}
                        <line x1="50" y1="200" x2="900" y2="200" stroke="#999" strokeWidth="1" />
                        
                        {/* X Axis Labels */}
                        <text x="120" y="220" fontSize="11" fill="#666" textAnchor="middle">01/05</text>
                        <text x="240" y="220" fontSize="11" fill="#666" textAnchor="middle">01/06</text>
                        <text x="360" y="220" fontSize="11" fill="#666" textAnchor="middle">01/07</text>
                        <text x="480" y="220" fontSize="11" fill="#666" textAnchor="middle">01/08</text>
                        <text x="600" y="220" fontSize="11" fill="#666" textAnchor="middle">01/09</text>

                        {/* Blue Line (Turbidity Data) */}
                        <polyline points="60,160 140,150 220,130 300,110 380,100 460,90 540,80 620,75 700,90 780,105 860,130" 
                                  stroke="#06b6d4" strokeWidth="2.5" fill="none" />
                        
                        {/* Data Points */}
                        <circle cx="60" cy="160" r="3" fill="#06b6d4" />
                        <circle cx="140" cy="150" r="3" fill="#06b6d4" />
                        <circle cx="220" cy="130" r="3" fill="#06b6d4" />
                        <circle cx="300" cy="110" r="3" fill="#06b6d4" />
                        <circle cx="380" cy="100" r="3" fill="#06b6d4" />
                        <circle cx="460" cy="90" r="3" fill="#06b6d4" />
                        <circle cx="540" cy="80" r="3" fill="#06b6d4" />
                        <circle cx="620" cy="75" r="3" fill="#06b6d4" />
                        <circle cx="700" cy="90" r="3" fill="#06b6d4" />
                        <circle cx="780" cy="105" r="3" fill="#06b6d4" />
                        <circle cx="860" cy="130" r="3" fill="#06b6d4" />
                      </svg>
                      <div className="text-center text-sm text-gray-600 mt-2">Turbidity Levels Over 30 Days</div>
                    </div>

                    {/* Data Table */}
                    <div className="bg-white rounded shadow-sm">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="font-bold text-[#4D4D4D] text-base">Report: Field Station Turbidity</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-gray-700 border-collapse">
                          <tbody>
                            {/* Header Information Section */}
                            <tr className="border-b border-gray-200">
                              <td className="p-3 font-semibold bg-gray-50 border-r border-gray-200 w-1/3">Site</td>
                              <td className="p-3">Pond Buoy</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="p-3 font-semibold bg-gray-50 border-r border-gray-200 w-1/3">Device</td>
                              <td className="p-3">X3-4G-01078</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="p-3 font-semibold bg-gray-50 border-r border-gray-200 w-1/3">Sensor</td>
                              <td className="p-3">NX260 Turbidity</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="p-3 font-semibold bg-gray-50 border-r border-gray-200 w-1/3">Parameter / Timestamp</td>
                              <td className="p-3 flex items-center justify-between">
                                <span>Turbidity (NTU)</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </td>
                            </tr>
                            
                            {/* Data Rows */}
                            {[
                              { date: '01/05/2026 23:00:00 (UTC-05:00)', value: '3.36' },
                              { date: '01/05/2026 22:00:00 (UTC-05:00)', value: '3.22' },
                              { date: '01/06/2026 21:00:00 (UTC-05:00)', value: '3.19' },
                              { date: '01/06/2026 20:00:00 (UTC-05:00)', value: '3.52' },
                              { date: '01/06/2026 19:00:00 (UTC-05:00)', value: '3.01' },
                              { date: '01/06/2026 18:00:00 (UTC-05:00)', value: '6.32' },
                              { date: '01/06/2026 17:00:00 (UTC-05:00)', value: '4.04' },
                              { date: '01/06/2026 16:00:00 (UTC-05:00)', value: '3.12' },
                              { date: '01/05/2026 15:00:00 (UTC-05:00)', value: '3.73' },
                              { date: '01/05/2026 14:00:00 (UTC-05:00)', value: '3.51' },
                              { date: '01/05/2026 13:00:00 (UTC-05:00)', value: '4.07' },
                              { date: '01/05/2026 12:00:00 (UTC-05:00)', value: '3.59' },
                              { date: '01/05/2026 11:00:00 (UTC-05:00)', value: '3.59' },
                            ].map((row, idx) => (
                              <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="p-3 border-r border-gray-200">{row.date}</td>
                                <td className="p-3 text-left">{row.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                {currentReportIndex === 1 && (
                  <div className="space-y-4 p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Select Report</h2>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="border border-gray-300 rounded p-4 cursor-pointer hover:bg-blue-50 transition">
                        <h3 className="font-semibold text-gray-700 mb-2">Daily Report</h3>
                        <p className="text-sm text-gray-600">View daily monitoring data and statistics</p>
                      </div>
                      <div className="border border-gray-300 rounded p-4 cursor-pointer hover:bg-blue-50 transition">
                        <h3 className="font-semibold text-gray-700 mb-2">Weekly Report</h3>
                        <p className="text-sm text-gray-600">View weekly trend analysis and alerts</p>
                      </div>
                      <div className="border border-gray-300 rounded p-4 cursor-pointer hover:bg-blue-50 transition">
                        <h3 className="font-semibold text-gray-700 mb-2">Monthly Report</h3>
                        <p className="text-sm text-gray-600">View monthly summary and performance metrics</p>
                      </div>
                      <div className="border border-gray-300 rounded p-4 cursor-pointer hover:bg-blue-50 transition">
                        <h3 className="font-semibold text-gray-700 mb-2">Custom Report</h3>
                        <p className="text-sm text-gray-600">Create custom report with date range</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Project settings now render inline in the Settings tab (no popup). */}

            {/* Settings View */}
            {activeTab === 'Settings' && (
              <div className="w-full h-full flex flex-col p-6">
                {currentSettingsIndex === 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl">
                    <h3 className="text-base font-bold text-gray-800 mb-4">Users</h3>

                    {settingsUsersError && (
                      <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        {settingsUsersError}
                      </div>
                    )}

                    {/* Users table */}
                    <table className="w-full text-sm text-gray-700">
                      <thead>
                        <tr className="text-gray-500 text-xs font-semibold">
                          <th className="text-left py-2 pr-3 font-semibold">Name</th>
                          <th className="text-left py-2 px-3 font-semibold">Email Address</th>
                          <th className="text-left py-2 px-3 font-semibold">Access</th>
                          <th className="w-20" />
                        </tr>
                      </thead>
                      <tbody>
                        {settingsUsersLoading ? (
                          <tr><td colSpan="4" className="py-6 text-center text-gray-400 text-sm">
                            <Loader className="w-4 h-4 animate-spin inline-block mr-2" />
                            Loading users…
                          </td></tr>
                        ) : settingsUsers.length === 0 ? (
                          <tr><td colSpan="4" className="py-6 text-center text-gray-400 text-sm">
                            {currentProject?.id ? 'No users yet — add one below.' : 'Open a project to manage users.'}
                          </td></tr>
                        ) : settingsUsers.map(u => (
                          <tr key={u.id} className="border-t border-gray-100">
                            <td className="py-2.5 pr-3 text-gray-800">{u.name}</td>
                            <td className="py-2.5 px-3 text-gray-700">{u.email}</td>
                            <td className="py-2.5 px-3 text-gray-700">{u.access}</td>
                            <td className="py-2.5 text-right">
                              {u.access === 'Owner' ? (
                                <span className="text-gray-300 text-sm cursor-not-allowed" title="Cannot remove the project owner">Remove</span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={settingsUsersBusy}
                                  onClick={async () => {
                                    if (!currentProject?.id) return
                                    if (!window.confirm(`Remove ${u.name || u.email} from this project?`)) return
                                    setSettingsUsersBusy(true)
                                    setSettingsUsersError('')
                                    try {
                                      await projectsApi.removeProjectUser(currentProject.id, u.id)
                                      setSettingsUsers(prev => prev.filter(x => x.id !== u.id))
                                    } catch (err) {
                                      setSettingsUsersError(err.response?.data?.error || err.message || 'Failed to remove user.')
                                    } finally {
                                      setSettingsUsersBusy(false)
                                    }
                                  }}
                                  className="text-[#358ECD] hover:text-[#2c7ab2] text-sm underline-offset-2 hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Add new user row */}
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-gray-700 w-20 flex-shrink-0">New User</span>
                        <input
                          type="email"
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                          placeholder="Email"
                          disabled={settingsUsersBusy || !currentProject?.id}
                          className="flex-1 min-w-[180px] px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] disabled:bg-gray-50"
                        />
                        <select
                          value={newUserAccess}
                          onChange={(e) => setNewUserAccess(e.target.value)}
                          disabled={settingsUsersBusy || !currentProject?.id}
                          className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] bg-white disabled:bg-gray-50"
                        >
                          <option value="Admin">Admin</option>
                          <option value="Collaborator">Collaborator</option>
                        </select>
                        <button
                          type="button"
                          onClick={async () => {
                            const email = newUserEmail.trim()
                            if (!email || !currentProject?.id) return
                            setSettingsUsersBusy(true)
                            setSettingsUsersError('')
                            try {
                              const res = await projectsApi.addProjectUser(currentProject.id, { email, access: newUserAccess })
                              if (res?.data) {
                                setSettingsUsers(prev => [...prev, res.data])
                              }
                              setNewUserEmail('')
                              setNewUserAccess('Admin')
                            } catch (err) {
                              setSettingsUsersError(err.response?.data?.error || err.message || 'Failed to add user.')
                            } finally {
                              setSettingsUsersBusy(false)
                            }
                          }}
                          disabled={!newUserEmail.trim() || settingsUsersBusy || !currentProject?.id}
                          className="px-5 py-1.5 border-2 border-[#358ECD] text-[#358ECD] hover:bg-[#358ECD] hover:text-white rounded text-sm font-semibold tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#358ECD]"
                        >
                          {settingsUsersBusy ? '…' : 'ADD'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 italic mt-2 ml-[92px]">
                        *User must have an active WQData LIVE account
                      </p>
                    </div>
                  </div>
                )}
                {currentSettingsIndex === 1 && (
                  <div className="space-y-4">
                    <div className="bg-white rounded shadow-sm max-w-xl">
                      {/* Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-bold text-gray-800">Project</h2>
                        {!projectEditMode && (
                          <button
                            onClick={() => {
                              setProjectForm({
                                name: currentProject?.name || '',
                                description: currentProject?.description || '',
                                latitude: currentProject?.latitude || '',
                                longitude: currentProject?.longitude || '',
                              })
                              setProjectEditMode(true)
                            }}
                            title="Edit project"
                            className="text-gray-400 hover:text-gray-600 transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {!projectEditMode ? (
                        /* Read-only screen (dynamic) */
                        <div className="px-6 py-4 space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Project Name</h4>
                            <p className="text-sm text-gray-600">{currentProject?.name || 'N/A'}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Project Description</h4>
                            {currentProject?.description
                              ? <p className="text-sm text-gray-600">{currentProject.description}</p>
                              : <p className="text-sm text-gray-400 italic">Edit to add project description.</p>}
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Default Dashboard</h4>
                            <p className="text-sm text-gray-600">{dashboardMenuItems[0] || 'Overview'}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Default Map Type</h4>
                            <p className="text-sm text-gray-600">Imagery</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Project Logo</h4>
                            {currentProject?.image
                              ? <p className="text-sm text-gray-600">{String(currentProject.image).split('/').pop()}</p>
                              : <p className="text-sm text-gray-400 italic">No logo set.</p>}
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">Disclaimer</h4>
                            <p className="text-sm text-gray-400 italic">Edit to add disclaimer.</p>
                          </div>
                          {(currentProject?.latitude || currentProject?.longitude) && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-800">Coordinates</h4>
                              <p className="text-sm text-gray-600">{currentProject.latitude}, {currentProject.longitude}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Inline edit screen (no popup) */
                        <div className="px-6 py-5 space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Project Name</label>
                            <input type="text" value={projectForm.name} onChange={(e) => setProjectForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Project Description</label>
                            <textarea value={projectForm.description} onChange={(e) => setProjectForm(f => ({ ...f, description: e.target.value }))} rows="3" placeholder="Project Description" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] resize-none" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-1">Default Dashboard</label>
                              <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"><option>Overview</option><option>Parameter Comparison</option><option>Project Map</option><option>Current Conditions</option><option>System Performance</option></select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-1">Default Map Type</label>
                              <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"><option>Imagery</option><option>Street</option><option>Terrain</option><option>Hybrid</option></select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Project Logo (232 x 70 pixels)</label>
                            <div className="border border-gray-300 rounded p-4 text-center text-sm text-gray-600">Drag and Drop Files or <span className="text-[#358ECD] cursor-pointer hover:underline">Browse</span></div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Coordinates</label>
                            <div className="grid grid-cols-2 gap-4">
                              <input type="text" value={projectForm.latitude} onChange={(e) => setProjectForm(f => ({ ...f, latitude: e.target.value }))} placeholder="Latitude" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                              <input type="text" value={projectForm.longitude} onChange={(e) => setProjectForm(f => ({ ...f, longitude: e.target.value }))} placeholder="Longitude" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                            </div>
                          </div>
                          <button
                            disabled={savingProject || !currentProject?.id}
                            onClick={async () => {
                              if (!currentProject?.id) return
                              setSavingProject(true)
                              try {
                                await projectsApi.updateProject(currentProject.id, { name: projectForm.name, description: projectForm.description, latitude: projectForm.latitude, longitude: projectForm.longitude })
                                setCurrentProject(prev => ({ ...prev, ...projectForm }))
                                setProjectEditMode(false)
                              } catch (err) {
                                alert('Failed to save project: ' + (err.response?.data?.error || err.message))
                              } finally { setSavingProject(false) }
                            }}
                            className="w-full py-2.5 text-white rounded text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
                            style={{ backgroundColor: '#358ECD' }}
                          >
                            {savingProject ? 'SAVING…' : 'SAVE'}
                          </button>
                          <button onClick={() => setProjectEditMode(false)} className="w-full text-center text-sm text-[#358ECD] hover:underline">Cancel</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Live View */}
            {activeTab === 'Live' && (
              <div className="w-full h-full flex flex-col bg-white p-6">
                {currentLiveIndex === 0 && (
                  <DataLoggerTab
                    projectId={currentProject?.id}
                    deviceId={liveDeviceId}
                    currentLiveIndex={currentLiveIndex}
                    setCurrentLiveIndex={setCurrentLiveIndex}
                  />
                )}
                {currentLiveIndex === 1 && (
                  <div className="w-full h-full flex flex-col bg-white p-6 space-y-6 overflow-y-auto">
                    {/* Device Icons */}
                    <div className="flex gap-6 pb-6 border-b border-gray-200">
                      <button
                        onClick={() => setCurrentLiveIndex(0)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 0
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Data Logger"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 0 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 5H5m10 0h4M9 11h.01M15 11h.01M9 15h.01M15 15h.01M6 17v2m12-2v2M5 20h14a2 2 0 002-2V4a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentLiveIndex(1)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 1
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Communications"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 1 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3v-6" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentLiveIndex(2)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 2
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Sensor Readings"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 2 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </button>
                    </div>

                    {/* Cellular Section */}
                    <div className="pb-6 border-b border-gray-200">
                      <h3 className="text-sm font-bold text-gray-800 mb-4">Cellular</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <label className="text-xs text-gray-600 font-medium block mb-2">IMEI</label>
                            <input type="text" placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-600 font-medium block mb-2">IMEI (SIM ID)</label>
                            <input type="text" placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                          </div>
                          <button className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition self-end">
                            Read
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Modem Info Section */}
                    <div className="pb-6 border-b border-gray-200">
                      <h3 className="text-sm font-bold text-gray-800 mb-4">Modem Info</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <label className="text-xs text-gray-600 font-medium block mb-2">IMEI</label>
                            <input type="text" placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                          </div>
                          <button className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition self-end">
                            Read
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Signal Quality Section */}
                    <div className="pb-6">
                      <h3 className="text-sm font-bold text-gray-800 mb-4">Signal Quality</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <label className="text-xs text-gray-600 font-medium block mb-2">RSSI (dBm)</label>
                            <input type="text" placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-600 font-medium block mb-2">BER (%)</label>
                            <input type="text" placeholder="" className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                          </div>
                          <button className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition self-end">
                            Read
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {currentLiveIndex === 2 && (
                  <div className="w-full h-full flex flex-col bg-white p-6 overflow-y-auto">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Sensor Readings</h2>
                    
                    {/* Device Icons */}
                    <div className="flex gap-6 pb-6 border-b border-gray-200">
                      <button
                        onClick={() => setCurrentLiveIndex(0)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 0
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Data Logger"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 0 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 5H5m10 0h4M9 11h.01M15 11h.01M9 15h.01M15 15h.01M6 17v2m12-2v2M5 20h14a2 2 0 002-2V4a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentLiveIndex(1)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 1
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Communications"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 1 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3v-6" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentLiveIndex(2)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' && currentLiveIndex < 2) {
                            setCurrentLiveIndex(currentLiveIndex + 1)
                          } else if (e.key === 'ArrowLeft' && currentLiveIndex > 0) {
                            setCurrentLiveIndex(currentLiveIndex - 1)
                          }
                        }}
                        className={`flex items-center justify-center w-16 h-16 rounded transition-all ${
                          currentLiveIndex === 2
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 border-2 border-gray-300 hover:border-blue-300'
                        }`}
                        title="Sensor Readings"
                        tabIndex={0}
                      >
                        <svg className={`w-10 h-10 ${currentLiveIndex === 2 ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="mb-8">
                      <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        YSI EXOZ Sonde
                      </h3>
                      <div className="grid grid-cols-3 gap-6 mb-6">
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Temperature (C)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">24.5</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Sp Cond (µS/cm)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">397.6</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Turbidity (NTU)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">2.35</p>
                        </div>
                      </div>
                      
                      {/* Mini Chart */}
                      <div className="bg-gray-50 border border-gray-200 rounded p-4 h-64 flex items-center justify-center">
                        <svg viewBox="0 0 600 200" className="w-full h-full">
                          {/* Grid */}
                          <defs>
                            <pattern id="grid2" width="60" height="40" patternUnits="userSpaceOnUse">
                              <path d="M 60 0 L 0 0 0 40" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                            </pattern>
                          </defs>
                          <rect width="600" height="200" fill="url(#grid2)" />
                          {/* Y Axis */}
                          <line x1="40" y1="20" x2="40" y2="180" stroke="#999" strokeWidth="1" />
                          {/* X Axis */}
                          <line x1="40" y1="180" x2="600" y2="180" stroke="#999" strokeWidth="1" />
                          {/* Line Chart - Orange trend */}
                          <polyline points="50,80 110,75 170,70 230,65 290,62 350,65 410,70 470,75 530,85" stroke="#f97316" strokeWidth="2" fill="none" />
                        </svg>
                      </div>
                    </div>

                    {/* NexSens NX260 Section */}
                    <div className="mb-8 pt-8 border-t border-gray-200">
                      <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        NexSens NX260
                      </h3>
                      <div className="grid grid-cols-4 gap-6 mb-6">
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Temperature (C)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">24.5</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Dissolved Oxygen (mg/L)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">8.47</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12a5 5 0 1010 0A5 5 0 017 12z" />
                            </svg>
                            Turbidity (NTU)
                          </p>
                          <p className="text-4xl font-bold text-gray-900">2.48</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-2">Status</p>
                          <p className="text-sm font-bold text-green-600">Active</p>
                        </div>
                      </div>

                      {/* Mini Charts */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 border border-gray-200 rounded p-4 h-48 flex items-center justify-center">
                          <svg viewBox="0 0 400 150" className="w-full h-full">
                            <defs>
                              <pattern id="grid3" width="40" height="30" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                              </pattern>
                            </defs>
                            <rect width="400" height="150" fill="url(#grid3)" />
                            <line x1="30" y1="20" x2="30" y2="140" stroke="#999" strokeWidth="1" />
                            <line x1="30" y1="140" x2="400" y2="140" stroke="#999" strokeWidth="1" />
                            <polyline points="40,70 90,68 140,66 190,64 240,63 290,65 340,70" stroke="#3b82f6" strokeWidth="2" fill="none" />
                          </svg>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded p-4 h-48 flex items-center justify-center">
                          <svg viewBox="0 0 400 150" className="w-full h-full">
                            <defs>
                              <pattern id="grid4" width="40" height="30" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                              </pattern>
                            </defs>
                            <rect width="400" height="150" fill="url(#grid4)" />
                            <line x1="30" y1="20" x2="30" y2="140" stroke="#999" strokeWidth="1" />
                            <line x1="30" y1="140" x2="400" y2="140" stroke="#999" strokeWidth="1" />
                            <polyline points="40,100 90,90 140,80 190,75 240,78 290,88 340,100" stroke="#8b5cf6" strokeWidth="2" fill="none" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Gallery View */}
            {activeTab === 'Gallery' && (
              <div className="w-full h-full flex flex-col bg-white">
                {/* Gallery Header Bar */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-sm font-medium text-gray-700">Gallery: Environmental Field Station</h2>
                  <div className="flex items-center gap-3">
                    {/* Album edit/delete — shown only when a DB album tab is active */}
                    {(() => {
                      const activeDbAlbum = projectAlbums.find(a => a.name === galleryTab)
                      if (!activeDbAlbum) return null
                      return (
                        <>
                          <button
                            onClick={() => { setEditAlbumModal({ id: activeDbAlbum.id, name: activeDbAlbum.name }); setEditAlbumName(activeDbAlbum.name) }}
                            className="flex items-center gap-1.5 text-sm text-[#358ECD] hover:text-blue-700 px-3 py-1.5 border border-[#358ECD] rounded hover:bg-blue-50 transition"
                            title="Rename this album"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>
                            Edit Album
                          </button>
                          <button
                            onClick={() => handleDeleteAlbum(activeDbAlbum.id, activeDbAlbum.name)}
                            className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 px-3 py-1.5 border border-red-300 rounded hover:bg-red-50 transition"
                            title="Delete this album"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete Album
                          </button>
                          <div className="w-px h-5 bg-gray-300" />
                        </>
                      )
                    })()}
                    <button
                      onClick={() => {
                        setShowCreateAlbumModal(true)
                        setAlbumName('')
                        setAlbumImages([])
                        setAlbumError('')
                        setAlbumSuccess('')
                      }}
                      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 transition">
                      <span>+</span> Album
                    </button>
                    <button
                      onClick={() => { setUploadItems([]); setShowUploadModal(true) }}
                      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 transition">
                      <span>↑</span> Upload
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 transition">
                      <span>☰</span> Filter
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 transition">
                      <span>↕</span> Sort By
                    </button>
                  </div>
                </div>

                {/* Category Tabs + Search */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                  <div className="flex gap-6 items-center">
                    {galleryTabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => { handleGalleryTabClick(tab); setSelectedPhoto(null); }}
                        className={`text-sm font-medium pb-1 border-b-2 transition ${
                          galleryTab === tab ? 'border-gray-800 text-gray-800' : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search Photos"
                      value={gallerySearch}
                      onChange={(e) => setGallerySearch(e.target.value)}
                      className="pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 w-48"
                    />
                    <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>

                {/* Detail View when photo is selected */}
                {selectedPhoto ? (
                  <div className="flex flex-1 overflow-hidden">
                    {/* Left Panel: Thumbnails + Info */}
                    <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                      {/* Thumbnail Section */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-baseline justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-800">
                            {galleryTab === 'All Photos' ? 'All Media' : `All ${galleryTab} Media`}
                          </h3>
                          <span className="text-xs text-gray-500">(Page {galleryPage}/{totalGalleryPages || 1})</span>
                        </div>
                        {/* 4-column thumbnail grid */}
                        <div className="grid grid-cols-4 gap-1 mb-3">
                          {currentPhotos.map((photo) => {
                            const imageUrl = photo.src || ''
                            const imageHasError = failedImageIds.has(photo.id)
                            return (
                              <div
                                key={photo.id}
                                onClick={() => setSelectedPhoto(photoEditsMap[photo.id] ? { ...photo, ...photoEditsMap[photo.id] } : photo)}
                                className={`cursor-pointer rounded overflow-hidden border-2 transition-all ${
                                  selectedPhoto.id === photo.id
                                    ? 'border-blue-500'
                                    : 'border-transparent hover:border-gray-300'
                                }`}
                              >
                                {imageHasError || !imageUrl ? (
                                  <div className="w-full h-14 bg-gray-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <img
                                    src={imageUrl}
                                    alt={photo.title}
                                    className="w-full h-14 object-cover"
                                    onError={() => setFailedImageIds(prev => new Set(prev).add(photo.id))}
                                    loading="lazy"
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                        {/* Pagination arrows */}
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => setGalleryPage(p => Math.max(1, p - 1))}
                            disabled={galleryPage <= 1}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition"
                          >▲</button>
                          <button
                            onClick={() => setGalleryPage(p => Math.min(totalGalleryPages, p + 1))}
                            disabled={galleryPage >= totalGalleryPages}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition"
                          >▼</button>
                        </div>
                      </div>

                      {/* Information Panel */}
                      <div className="flex-1 overflow-y-auto">
                        {/* Info header with edit button */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                          <h2 className="text-sm font-semibold text-gray-800">Information</h2>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingPhoto(selectedPhoto)
                                setEditPhotoFile(null)
                                setEditPhotoPreview(null)
                                const currentAlbum = selectedPhoto.album || (galleryTab !== 'All Photos' ? galleryTab : '')
                                setEditFormData({
                                  title: selectedPhoto.title,
                                  description: selectedPhoto.description,
                                  tags: selectedPhoto.tags,
                                  album: currentAlbum,
                                  location: selectedPhoto.location
                                })
                                setShowEditMediaModal(true)
                              }}
                              className="p-1 text-gray-400 hover:text-[#358ECD] transition"
                              title="Edit information"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeletePhoto(selectedPhoto)}
                              className="p-1 text-gray-400 hover:text-red-500 transition"
                              title="Delete photo"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="px-4 py-3 space-y-3">
                          {[
                            { label: 'Title', value: selectedPhoto.title },
                            { label: 'Description', value: selectedPhoto.description },
                            { label: 'Original Filename', value: selectedPhoto.originalFilename },
                            { label: 'Camera S/N', value: selectedPhoto.cameraSN },
                            { label: 'Tags', value: selectedPhoto.tags },
                            { label: 'Album', value: selectedPhoto.album },
                            { label: 'Uploaded By', value: selectedPhoto.uploadedBy },
                            { label: 'Date', value: selectedPhoto.date },
                            { label: 'Dimensions', value: selectedPhoto.dimensions },
                            { label: 'Size', value: selectedPhoto.size },
                            { label: 'Total Size', value: selectedPhoto.totalSize },
                            { label: 'Location', value: selectedPhoto.location },
                          ].map(({ label, value }) => (
                            <div key={label}>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
                              <p className="text-sm text-gray-800 mt-0.5">{value || '—'}</p>
                            </div>
                          ))}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Map</p>
                            <button className="text-sm text-[#358ECD] hover:underline mt-0.5">Show Map</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel: Large Photo Only */}
                    <div className="flex-1 bg-gray-50 flex items-center justify-center overflow-hidden">
                      {failedImageIds.has(selectedPhoto.id) || (!selectedPhoto.largeSrc && !selectedPhoto.src) ? (
                        <div className="flex flex-col items-center justify-center text-gray-400 gap-3">
                          <svg className="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">Unable to load image</p>
                        </div>
                      ) : (
                        <img
                          src={selectedPhoto.largeSrc || selectedPhoto.src}
                          alt={selectedPhoto.title}
                          className="max-w-full max-h-full object-contain"
                          onError={() => setFailedImageIds(prev => new Set(prev).add(selectedPhoto.id))}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  /* Grid View when no photo selected */
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {galleryLoading && apiPhotos.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                        <span className="text-sm text-gray-600">Loading photos...</span>
                      </div>
                    ) : currentPhotos.length === 0 && !galleryLoading ? (
                      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                        <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm">No photos found for this project</p>
                      </div>
                    ) : (
                    <div className="grid grid-cols-6 gap-4">
                      {currentPhotos.map((photo) => {
                        const imageUrl = photo.src || photo.thumbnail || photo.image || photo.file_url || photo.url || ''
                        const imageHasError = failedImageIds.has(photo.id)
                        
                        return (
                          <div
                            key={photo.id}
                            className="cursor-pointer group"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <div className="aspect-[4/3] rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                              {imageHasError || !imageUrl ? (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              ) : (
                                <img
                                  src={imageUrl}
                                  alt={photo.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  onError={() => setFailedImageIds(prev => new Set(prev).add(photo.id))}
                                  loading="lazy"
                                />
                              )}
                            </div>
                            <div className="mt-2 text-center">
                              <p className="text-xs font-semibold text-gray-800">{photo.category}</p>
                              <p className="text-xs text-gray-500 italic truncate">{photo.title}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    )}

                    {/* Pagination */}
                    <div className="flex items-center justify-center gap-1 mt-6 pb-4">
                      <button 
                        onClick={() => setGalleryPage(prev => Math.max(1, prev - 1))}
                        disabled={galleryPage === 1}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Back
                      </button>
                      {Array.from({ length: totalGalleryPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setGalleryPage(page)}
                          className={`w-8 h-8 text-sm rounded border transition ${
                            galleryPage === page
                              ? 'bg-white border-blue-500 text-blue-600 font-medium'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button 
                        onClick={() => setGalleryPage(prev => Math.min(totalGalleryPages, prev + 1))}
                        disabled={galleryPage === totalGalleryPages}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {/* Create Album Modal */}
                {showCreateAlbumModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateAlbumModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                      {/* Modal Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-bold text-gray-800">Create New Album</h2>
                        <button 
                          onClick={() => setShowCreateAlbumModal(false)}
                          className="text-gray-400 hover:text-gray-600 transition"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Modal Content */}
                      <div className="px-6 py-4 space-y-4">
                        {/* Album Name Input */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Album Name</label>
                          <SecureInput
                            type="text"
                            value={albumName}
                            onChange={(e) => {
                              setAlbumName(e.target.value)
                              setAlbumError('')
                            }}
                            placeholder="Enter album name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Image Upload Section */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Upload Images</label>
                          
                          {/* Upload Area */}
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition cursor-pointer">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleAlbumImageUpload}
                              className="hidden"
                              id="album-image-upload"
                            />
                            <label htmlFor="album-image-upload" className="cursor-pointer">
                              <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                              </svg>
                              <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                            </label>
                          </div>

                          {/* Uploaded Images Preview */}
                          {albumImages.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Images ({albumImages.length})</p>
                              <div className="grid grid-cols-4 gap-2">
                                {albumImages.map((file, index) => (
                                  <div key={index} className="relative group">
                                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={`Preview ${index}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                    <button
                                      onClick={() => removeAlbumImage(index)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                      title="Remove image"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Error Message */}
                        {albumError && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-700">{albumError}</p>
                          </div>
                        )}

                        {/* Success Message */}
                        {albumSuccess && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-sm text-green-700">{albumSuccess}</p>
                          </div>
                        )}
                      </div>

                      {/* Modal Footer */}
                      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <button
                          onClick={() => setShowCreateAlbumModal(false)}
                          disabled={isCreatingAlbum}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateAlbum}
                          disabled={isCreatingAlbum || !albumName.trim() || albumImages.length === 0}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center gap-2"
                        >
                          {isCreatingAlbum ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            'Create Album'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dashboard View */}
            {activeTab === 'Dashboard' && (
              <>
                {/* Overview Content */}
                {(templates[currentMenuIndex]?.template_type === 'overview' || templates[currentMenuIndex]?.name === 'Overview') && (
                  <>
                    {currentProject?.id ? (
                      <TemplateDashboardGrid
                        key={templates[currentMenuIndex]?.id}
                        ref={gridRef}
                        projectId={currentProject.id}
                        templateId={templates[currentMenuIndex]?.id}
                        isEditing={isCustomizingDashboard}
                        droppingWidget={draggingWidget}
                        onDropComplete={() => { setDraggingWidget(null); setAddWidgetModalHidden(false); setShowAddWidgetsModal(false) }}
                        containerWidth={gridContainerWidth}
                      />
                    ) : (
                      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
                        Select a project to view the dashboard
                      </div>
                    )}
                  </>
                )}

                {/* ── HIDDEN LEGACY OVERVIEW (kept for reference) ── */}
                {false && (
                  <>
            {/* Graph Section with Time Controls */}
            <div className="bg-white rounded shadow-sm mb-4">
              <div className="flex border-b border-gray-100 items-center justify-between p-4">
                <h3 className="font-bold text-[#4D4D4D] text-lg">
                  {projectSites.length > 0 ? projectSites[0].name : 'Project Site'}
                </h3>
              </div>
              
              {/* Time Period Controls */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 font-medium">Time Range:</span>
                  <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
                    <button 
                      onClick={() => handlePeriodClick('D')}
                      className={`px-2 py-1 text-xs font-medium rounded transition ${
                        activePeriod === 'D' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      D
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('W')}
                      className={`px-2 py-1 text-xs font-medium rounded transition ${
                        activePeriod === 'W' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      W
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('M')}
                      className={`px-2 py-1 text-xs font-medium rounded transition ${
                        activePeriod === 'M' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      M
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('Y')}
                      className={`px-2 py-1 text-xs font-medium rounded transition ${
                        activePeriod === 'Y' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Y
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedParameters.length > 0 
                    ? `${selectedParameters.length} parameter${selectedParameters.length > 1 ? 's' : ''} selected`
                    : 'Select parameters to view data'}
                </div>
              </div>

              {/* Dynamic Graph Component */}
              <div className="p-4">
                <DynamicGraph 
                  selectedParameters={selectedParameters}
                  dateRange={dateRangeGraph}
                  timezone="UTC"
                  timePeriod={activePeriod}
                />
              </div>
            </div>

            {/* Second Row with Wind Rose and other content */}
            <div className="flex gap-4 mb-4 items-start">
              {/* Wind Rose */}
            <div className="w-[68%] flex flex-wrap gap-4">
              <div className="w-[49%]">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="flex border-b border-gray-100 items-center justify-between p-4">
                    <h3 className="font-bold text-[#4D4D4D] text-lg">Wind Rose</h3>
                    <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                    </button>
                  </div>
                  <div className=" flex items-center justify-center p-[25px] pb-[38px]">
                    <img alt="Project" src="/images/windrose.png"></img>
                  </div>
                </div>
                {/* Environmental News */}
                <div className="bg-white rounded-lg shadow-sm mt-4">
                  <div className="flex border-b border-gray-100 items-center justify-between p-4">
                    <h3 className="font-bold text-[#4D4D4D] text-lg">Environmental News</h3>
                    <button className="text-[#4D4D4D] flex justify-between items-center gap-4 font-medium text-base bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg> View All</button>
                  </div>
                  <div className="space-y-2 p-[25px]">
                    <div className="mb-4">
                      <p className="text-base font-medium text-gray-900 line-clamp-2">From Source to Sea: Monitoring and Protecting...</p>
                      <button className="text-gray-600 font-medium text-base mt-1 hover:underline">Read Article</button>
                    </div>
                    <div className="mb-4 border-t border-gray-200 my-4 py-2">
                      <p className="text-base font-medium text-gray-900 line-clamp-2">From Source to Sea: Monitoring and Protecting...</p>
                      <button className="text-gray-600 font-medium text-base mt-1 hover:underline">Read Article</button>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <p className="text-base font-medium text-gray-900 line-clamp-2">Using Traditional Ghanaian Fishing Canoes to Monitor...</p>
                      <button className="text-gray-600 font-medium text-base mt-1 hover:underline">Read Article</button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-[49%]">
              {/* Project Photo */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Project Photo</h3>
                  <button className="text-[#4D4D4D] flex justify-between items-center gap-4 font-medium text-base bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg> View All</button>
                </div>
                <div className=" flex items-center justify-center overflow-hidden p-[25px]">
                  <img src="/images/projectphoto.jpg" alt="Project" />
                </div>
              </div>
              {/* Field Notes */}
              <div className="bg-white rounded-lg shadow-sm mt-4">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Field Notes</h3>
                  <div className="flex items-center gap-[20px]">
                  <button className="text-[#4D4D4D] font-medium text-base bg-gray-200 py-2 px-6 rounded-lg">Save Note</button>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg>
                  </div>
                </div>
                <div className="text-base p-[25px]">
                 <button className="text-gray-600 block ml-auto font-medium text-base">12/29/2025 8:00 am EST</button>
                 <div className="text-center py-[25px]">
                  <p className="text-base text-gray-600">Enter note by typing</p>
                  <p className="text-gray-600 text-base">or</p>
                  <p className="text-gray-600 text-base">Drag and drop file, images, or photos</p>
                  <p className="text-gray-600 text-base">or</p>
                  <button className="text-gray-600 font-medium text-base">Browse and Select</button>
                  </div>
                </div>
              </div>
             
            </div>
             {/* Event Monitor */}
            <div className="bg-white w-full rounded-lg shadow-sm mb-4">
              <div className="flex border-b border-gray-100 items-center justify-between p-4">
                <h3 className="font-bold text-[#4D4D4D] text-lg">Event Monitor</h3>
                <div className="flex items-center gap-[20px]">
                  <button className="text-lg flex justify-between items-center gap-6 text-[#4D4D4D] font-medium bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.88889 20V13.3333H11.1111V15.5555H20V17.7778H11.1111V20H8.88889ZM0 17.7778V15.5555H6.66666V17.7778H0ZM4.44444 13.3333V11.1111H0V8.88885H4.44444V6.66663H6.66666V13.3333H4.44444ZM8.88889 11.1111V8.88885H20V11.1111H8.88889ZM13.3333 6.66663V0H15.5556V2.22223H20V4.4444H15.5556V6.66663H13.3333ZM0 4.4444V2.22223H11.1111V4.4444H0Z" fill="#4D4D4D"/>
</svg>
 Filter</button>
                  <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-[25px]">
              <table className="w-full text-base text-gray-600">
                <thead className="border-b border-gray-100 text-gray-600">
                  <tr>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">#</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Type</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Event Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">1</td>
                    <td className="py-3 px-3">Low Power</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-red-600">●</span> Trigger</td>
                    <td className="py-3 px-3">2026-01-08 19:00:00</td>
                  </tr>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">2</td>
                    <td className="py-3 px-3">Low Dissolved Oxygen</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-[#56B565]">● </span> Trigger</td>
                    <td className="py-3 px-3">2025-07-29 14:19:16</td>
                  </tr>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">3</td>
                    <td className="py-3 px-3">High Turbidity</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-[#56B565]">● </span> Normal</td>
                    <td className="py-3 px-3">2025-12-29 03:00:00</td>
                  </tr>
                </tbody>
              </table>
              </div>
              </div>
              </div>
              {/* Support our Project */}
              <div className="supportour w-[32%]">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Support our Project</h3>
                </div>
                <div className=" flex items-center flex-wrap justify-center overflow-hidden p-[25px]">
                <button className="text-[#0866AB] block mr-auto font-medium text-base underline mb-6">Donate Now</button>
                  <img src="/images/supportour.png" alt="Project" />
                </div>
                </div>
                {/* Project Description and Disclaimer Row */}
            <div className=" mt-4">
              {/* Project Description */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Project Description</h3>
                  <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                     <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
                <div className="text-gray-600 text-base leading-relaxed p-[25px]">
                  <p className="text-gray-400">Write project description here...</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-white rounded-lg shadow-sm mt-4">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Disclaimer</h3>
                  <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
                <div className="text-gray-600 text-base leading-relaxed space-y-2 p-[25px]">
                  <p>
                    Uncertainty and potential for error can be associated with environmental monitoring data. Data users are cautioned to consider carefully the provisional nature of the information before using it for decisions that concern personal or public safety or the conduct of business that involves substantial monetary or operational consequences. No warranty, express or implied, is given as to the accuracy, reliability, utility or completeness of the data hosted on this datacenter, and this organization shall not be held liable for improper or incorrect use of the data described or information contained on these pages.
                  </p>
                </div>
              </div>
            </div>
              </div>
            </div>

            {/* Template Dashboard Grid */}
            {currentProject?.id && (
              <div className="mt-4">
                <TemplateDashboardGrid projectId={currentProject.id} />
              </div>
            )}
                  </>
                )}

                {/* Parameter Comparison Content — shared DynamicGraphCard */}
                {(templates[currentMenuIndex]?.template_type === 'parameter_comparison' || templates[currentMenuIndex]?.name === 'Parameter Comparison') && (
                  <DynamicGraphCard
                    title="Graph"
                    selectedParameters={selectedParameters}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    activePeriod={activePeriod}
                    onPeriodClick={handlePeriodClick}
                    onDateChange={(field, value) => { setActivePeriod(''); setDateRangeGraph(prev => ({ ...prev, [field]: value })) }}
                    storageKey="wq_param_comparison_graph"
                    colors={PARAM_CHART_COLORS}
                    emptyMessage="Select one or more parameters from the sidebar to compare them here."
                  />
                )}

                {/* Project Map: render whichever widgets the backend has
                    assigned to this template — same pattern as Overview.
                    The template ships with a single GPS widget which, on
                    the React side, renders the full Leaflet map + Edit
                    button + Map Editor popup (see GPSWidget in
                    DashboardWidgets.jsx). If an admin assigns additional
                    widgets to this template later they'll appear here
                    without any frontend change. */}
                {templates[currentMenuIndex]?.name === 'Project Map' && (
                  <>
                    {currentProject?.id ? (
                      <TemplateDashboardGrid
                        ref={gridRef}
                        projectId={currentProject.id}
                        templateId={templates[currentMenuIndex]?.id}
                        isEditing={isCustomizingDashboard}
                        droppingWidget={draggingWidget}
                        onDropComplete={() => { setDraggingWidget(null); setAddWidgetModalHidden(false); setShowAddWidgetsModal(false) }}
                        containerWidth={gridContainerWidth}
                      />
                    ) : (
                      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
                        Select a project to view the dashboard
                      </div>
                    )}
                  </>
                )}

                {/* Current Conditions Content — dynamic (Option A: latest value per parameter) */}
                {(templates[currentMenuIndex]?.template_type === 'current_conditions' || templates[currentMenuIndex]?.name === 'Current Conditions') && (
                  <CurrentConditionsView
                    sites={projectSites}
                    selectedParameters={selectedParameters}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    projectImage={projectImage}
                  />
                )}

                {/* System Performance Content — dynamic */}
                {(templates[currentMenuIndex]?.template_type === 'system_performance' || templates[currentMenuIndex]?.name === 'System Performance') && (
                  <SystemPerformanceView
                    sites={projectSites}
                    selectedParameters={selectedParameters}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    activePeriod={activePeriod}
                    onPeriodClick={handlePeriodClick}
                    onDateChange={(field, value) => { setActivePeriod(''); setDateRangeGraph(prev => ({ ...prev, [field]: value })) }}
                    projectId={currentProject?.id}
                  />
                )}

                {/* All Data — flat grid of cards, one per selectedParameter */}
                {templates[currentMenuIndex]?.name === 'All Data' && (
                  <AllDataView
                    selectedParameters={selectedParameters}
                    projectId={currentProject?.id}
                  />
                )}


            

            

            {/* Nexsens Section — now a template widget, not hardcoded */}

            {/* Footer */}
              </>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        
      </div>

      {/* Customize Data Report Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl mx-4">
            {/* Modal Header with Apply/Cancel */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Customize Data Report</h2>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowCustomizeModal(false)}
                  className="px-5 py-1.5 text-white rounded text-sm font-medium hover:opacity-90 transition"
                  style={{ backgroundColor: '#358ECD' }}
                >
                  Apply
                </button>
                <button 
                  onClick={() => setShowCustomizeModal(false)}
                  className="px-5 py-1.5 text-gray-600 text-sm font-medium hover:text-gray-800 transition"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Modal Tabs */}
            <div className="flex border-b border-gray-200 px-6 overflow-x-auto">
              {['Parameters', 'Data', 'View', 'Filter', 'Header/Footer', 'PDF Format', 'Settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setCustomizeTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    customizeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto" style={{ minHeight: '400px', maxHeight: '60vh' }}>
              {customizeTab === 'Parameters' && (
                <div className="flex gap-8">
                  {/* Left - Select Parameters */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Select Parameters</h3>
                    
                    {/* Land Based Station */}
                    <div className="mb-5">
                      <h4 className="font-medium text-gray-800 mb-2">Land Based Station</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X3-4G-01080 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>Gill MaxiMet GMX200</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>Solinst Levelogger 5</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                      </div>
                    </div>

                    {/* Pond Buoy */}
                    <div className="mb-5">
                      <h4 className="font-medium text-gray-800 mb-2">Pond Buoy</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X3-4G-01078 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>YSI EXO2 Sonde</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>NexSens NX260</span>
                          <ChevronDown className="w-3 h-3 text-gray-400 rotate-180" />
                        </label>
                        {/* Expanded NexSens NX260 sub-items */}
                        <div className="ml-12 space-y-1">
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                            <span>NX Temperature</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                            <span>NX DO</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                            <span>NX Turbidity</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Weather Station */}
                    <div className="mb-5">
                      <h4 className="font-medium text-gray-800 mb-2">Weather Station</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X2-C-VZ4G-01284 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>YSI H-3401</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Right - Selected Parameters */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Selected Parameters</h3>
                    <div className="border border-gray-200 rounded p-4 min-h-[300px]">
                      <p className="text-sm text-gray-700">Pond Buoy: NX Turbidity</p>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Data' && (
                <div>
                  {/* Date Range Section */}
                  <h3 className="font-semibold text-gray-800 mb-4">Date Range</h3>
                  <div className="space-y-4 max-w-lg">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">Range</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>Last 7 Days</option>
                        <option>Last 30 Days</option>
                        <option>This Month</option>
                        <option>Last Month</option>
                        <option>Custom</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">From</label>
                      <input 
                        type="datetime-local" 
                        defaultValue="2026-03-16T00:00" 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" 
                      />
                      <label className="text-sm text-gray-700 w-8 text-center">To</label>
                      <input 
                        type="datetime-local" 
                        defaultValue="2026-03-16T11:59" 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" 
                      />
                    </div>
                  </div>

                  {/* Condense Section */}
                  <h3 className="font-semibold text-gray-800 mt-8 mb-4">Condense</h3>
                  <div className="max-w-lg">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">Mode</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>None</option>
                        <option>Hourly</option>
                        <option>Daily</option>
                        <option>Weekly</option>
                        <option>Monthly</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Filter' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Filter Sites and Parameters</h3>
                  
                  {/* Search Bar */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input 
                        type="text" 
                        placeholder="Search Parameters"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Sites Tree Structure */}
                  <div className="border border-gray-200 rounded p-4 max-h-[400px] overflow-y-auto">
                    {getFilteredSites().length > 0 ? (
                      <div className="space-y-1">
                        {getFilteredSites().map((site) => (
                          <div key={site.id}>
                            {/* Site Header */}
                            <button
                              onClick={() => toggleMenu(site.id)}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                            >
                              {expandedMenus.includes(site.id) ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="text-sm font-medium text-gray-800">{site.name}</span>
                            </button>

                            {/* Devices under Site */}
                            {expandedMenus.includes(site.id) && site.devices && (
                              <div className="ml-4 space-y-1">
                                {site.devices.map((device) => (
                                  <div key={device.id}>
                                    {/* Device Header */}
                                    <button
                                      onClick={() => toggleMenu(`device-${device.id}`)}
                                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                                    >
                                      {expandedMenus.includes(`device-${device.id}`) ? (
                                        <ChevronDown className="w-4 h-4 text-gray-600" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-600" />
                                      )}
                                      <span className="text-sm text-gray-700">{device.name}</span>
                                    </button>

                                    {/* Sensors under Device */}
                                    {expandedMenus.includes(`device-${device.id}`) && device.sensors && (
                                      <div className="ml-4 space-y-1">
                                        {device.sensors.map((sensor) => (
                                          <div key={sensor.id}>
                                            {/* Sensor Header */}
                                            <button
                                              onClick={() => toggleMenu(`sensor-${sensor.id}`)}
                                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                                            >
                                              {expandedMenus.includes(`sensor-${sensor.id}`) ? (
                                                <ChevronDown className="w-4 h-4 text-gray-600" />
                                              ) : (
                                                <ChevronRight className="w-4 h-4 text-gray-600" />
                                              )}
                                              <span className="text-sm text-gray-700">{sensor.name}</span>
                                            </button>

                                            {/* Parameters under Sensor */}
                                            {expandedMenus.includes(`sensor-${sensor.id}`) && sensor.parameters && (
                                              <div className="ml-4 space-y-1">
                                                {sensor.parameters.map((parameter) => (
                                                  <label key={parameter.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition cursor-pointer">
                                                    <input 
                                                      type="checkbox" 
                                                      checked={checkedItems[parameter.id] || false}
                                                      onChange={() => toggleCheckbox(parameter.id)}
                                                      className="w-4 h-4 rounded border-gray-300 accent-blue-600"
                                                    />
                                                    <span className="text-sm text-gray-700">{parameter.name}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">No sites or parameters found</p>
                      </div>
                    )}
                  </div>

                  {/* Filter Parameter Min/Max */}
                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Filter Parameter Range</h4>
                    <div className="space-y-3 max-w-sm">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-700 w-10">Min</label>
                        <input type="number" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-700 w-10">Max</label>
                        <input type="number" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'View' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Include Sections</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Header</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Stat Table</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Plot</span>
                      <select className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Combine Plots</option>
                        <option>Separate Plots</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Data Table</span>
                    </label>
                  </div>
                </div>
              )}
              {customizeTab === 'Header/Footer' && (
                <div className="flex gap-12">
                  {/* Header Section */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Header</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Top Title</label>
                        <input type="text" defaultValue="Environmental Field Station 2025" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Report Title</label>
                        <input type="text" defaultValue="Template First Site" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Subtitle</label>
                        <input type="text" defaultValue="March 16, 2026" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Date Prepared</label>
                        <select className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                          <option>March 16, 2026</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Time Prepared</label>
                        <select className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                          <option>12-hour</option>
                          <option>24-hour</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Footer Section */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Footer</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Page Number</label>
                        <input type="text" defaultValue="Page 1 of 2" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Extra Footer</label>
                        <input type="text" defaultValue="Extra Footer" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-400 focus:outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'PDF Format' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Page Format</h3>
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-24">Size</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Letter (8.5 x 11 in)</option>
                        <option>Legal (8.5 x 14 in)</option>
                        <option>Tabloid (11 x 17 in)</option>
                        <option>A4 (210 x 297 mm)</option>
                        <option>A3 (297 x 420 mm)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-24">Orientation</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Portrait</option>
                        <option>Landscape</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Settings' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Settings</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Report Name</label>
                      <input type="text" defaultValue="Field Station Turbidity" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Description</label>
                      <textarea defaultValue="Report for turbidity at the field station" rows="3" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 resize-none" />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-32">Timezone</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>(UTC-4:00) America/New_York</option>
                        <option>(UTC-5:00) America/Chicago</option>
                        <option>(UTC-6:00) America/Denver</option>
                        <option>(UTC-7:00) America/Los_Angeles</option>
                        <option>(UTC+0:00) UTC</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-32">Show Error Value</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload to Gallery Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 flex flex-col" style={{maxHeight: '85vh'}}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">Upload to Gallery</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleStartUpload}
                  disabled={uploadItems.length === 0 || uploadItems.every(i => i.done)}
                  className="bg-[#358ECD] text-white px-5 py-2 rounded text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Start Upload
                </button>
                <button
                  onClick={() => { setShowUploadModal(false); setUploadItems([]) }}
                  className="text-sm text-gray-600 hover:text-gray-900 transition"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Drop Zone */}
            <div
              className="mx-6 mt-5 mb-3 border-2 border-dashed border-gray-300 rounded p-8 text-center cursor-pointer hover:border-[#358ECD] transition flex-shrink-0"
              onClick={() => document.getElementById('upload-gallery-input').click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleUploadFilesSelect(e.dataTransfer.files) }}
            >
              <input
                id="upload-gallery-input"
                type="file"
                multiple
                accept="image/gif,image/png,image/jpeg,image/jpg,video/mp4,video/quicktime,video/x-flv,video/x-matroska,video/x-msvideo"
                className="hidden"
                onChange={(e) => handleUploadFilesSelect(e.target.files)}
              />
              <p className="text-sm text-gray-400">
                Drag Attachments or{' '}
                <span className="text-[#358ECD] font-semibold cursor-pointer hover:underline">Click Here to Add Files</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">Supports image files: gif, png, jpg, and jpeg</p>
              <p className="text-xs text-gray-400">Supports video files: mp4, mov, flv, mkv, and avi</p>
              <p className="text-xs text-gray-400">Maximum file size of 50.00 MB</p>
            </div>

            {/* Scrollable file list */}
            <div className="flex-1 overflow-y-auto px-6 pb-5 space-y-3 min-h-0">
              {uploadItems.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-4">No files added yet. Use the drop zone above.</p>
              ) : uploadItems.map(item => (
                <div key={item.id} className="flex gap-4 p-4 border border-gray-200 rounded bg-white">

                  {/* Thumbnail + file info */}
                  <div className="flex-shrink-0 w-28">
                    <div className="w-28 h-24 rounded overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
                      {item.previewUrl
                        ? <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                        : <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>
                      }
                    </div>
                    <p className="text-xs text-gray-600 mt-1.5 break-all leading-tight" title={item.file.name}>{item.file.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{(item.file.size / 1024 / 1024).toFixed(1)} MB</p>
                    {item.error && <p className="text-xs text-red-500 mt-1">{item.error}</p>}
                    {item.done && <p className="text-xs text-green-600 mt-1 font-medium">✓ Uploaded</p>}
                  </div>

                  {/* Form fields — 3-col grid with labels */}
                  <div className="flex-1 grid grid-cols-3 gap-x-4 gap-y-3 content-start">
                    {/* Row 1 labels */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Album</label>
                      <select
                        value={item.album_id}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, album_id: e.target.value } : i))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] bg-white"
                      >
                        <option value="">Select Album</option>
                        {projectAlbums.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                      <SecureInput
                        type="text"
                        value={item.title}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, title: e.target.value } : i))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tags</label>
                      <SecureInput
                        type="text"
                        placeholder="Separate by comma"
                        value={item.tags}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, tags: e.target.value } : i))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                    {/* Row 2 labels */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                      <select
                        value={item.location}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, location: e.target.value } : i))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] bg-white"
                      >
                        <option value="">Select Location</option>
                        <option value="Land-based Station">Land-based Station</option>
                        <option value="Site - Pond Buoy">Site - Pond Buoy</option>
                        <option value="Site - Weather Station">Site - Weather Station</option>
                        <option value="Site - North Paterson">Site - North Paterson</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                      <SecureInput
                        type="text"
                        value={item.description}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, description: e.target.value } : i))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 flex-shrink-0 justify-start pt-5">
                    <button
                      onClick={() => handleUploadItem(item.id)}
                      disabled={item.uploading || item.done}
                      title={item.done ? 'Uploaded' : 'Upload this file'}
                      className="w-9 h-9 bg-[#358ECD] text-white rounded flex items-center justify-center hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {item.uploading
                        ? <Loader className="w-4 h-4 animate-spin" />
                        : item.done
                          ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      }
                    </button>
                    <button
                      onClick={() => setUploadItems(prev => prev.filter(i => i.id !== item.id))}
                      title="Remove"
                      className="w-9 h-9 bg-gray-100 text-gray-500 rounded flex items-center justify-center hover:bg-gray-200 transition"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Album Name Modal */}
      {editAlbumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Rename Album</h2>
            <SecureInput
              type="text"
              value={editAlbumName}
              onChange={(e) => setEditAlbumName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEditAlbumSave() }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditAlbumModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition">Cancel</button>
              <button
                onClick={handleEditAlbumSave}
                disabled={!editAlbumName.trim()}
                className="px-4 py-2 text-sm bg-[#358ECD] text-white rounded hover:bg-blue-600 disabled:opacity-50 transition"
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Media Information Modal */}
      {showEditMediaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Edit Media Information</h2>
              <button onClick={() => { setShowEditMediaModal(false); setEditPhotoFile(null); setEditPhotoPreview(null) }} className="text-gray-400 hover:text-gray-600 transition">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

              {/* Image preview + change */}
              <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-24 h-20 rounded overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                  {editPhotoPreview || editingPhoto?.src ? (
                    <img src={editPhotoPreview || editingPhoto?.src} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 truncate mb-1">{editPhotoFile ? editPhotoFile.name : (editingPhoto?.originalFilename || 'No file')}</p>
                  {editPhotoFile && <p className="text-xs text-green-600 mb-2">New file selected</p>}
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium text-gray-600 hover:bg-gray-50 cursor-pointer transition">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    Change Photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) {
                          setEditPhotoFile(f)
                          setEditPhotoPreview(URL.createObjectURL(f))
                        }
                      }}
                    />
                  </label>
                  {editPhotoFile && (
                    <button onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null) }} className="ml-2 text-xs text-red-500 hover:underline">Remove</button>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <SecureInput
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <SecureTextarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description"
                  rows="3"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <SecureInput
                  type="text"
                  value={editFormData.tags}
                  onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Separate By Comma"
                />
              </div>

              {/* Album */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Album</label>
                <select
                  value={editFormData.album}
                  onChange={(e) => setEditFormData({ ...editFormData, album: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an album</option>
                  {projectAlbums.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a Location</option>
                  <option value="Site - Pond Buoy">Site - Pond Buoy</option>
                  <option value="Site - Land Based">Site - Land Based</option>
                  <option value="Site - Weather Station">Site - Weather Station</option>
                  <option value="Site - North Paterson">Site - North Paterson</option>
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setShowEditMediaModal(false); setEditPhotoFile(null); setEditPhotoPreview(null) }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePhotoInfo}
                className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#358ECD] rounded-md hover:bg-blue-600 transition"
              >
                SAVE CHANGES
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure Site Modal */}
      {showAddDeviceModal && (() => {
        const configureSite = modalProjectSites.find(s => s.id === selectedSiteForDevice)
        const siteDevices = configureSite?.devices || []

        const handleAddDevice = async () => {
          if (!newDeviceName.trim()) return
          const _devSecErr = scanInput(newDeviceName)
          if (_devSecErr) { setDeviceError(_devSecErr); return }
          setIsCreatingDevice(true)
          setDeviceError('')
          setSuccessMessage('')
          try {
            const createdDeviceResponse = await projectsApi.createDevice(currentProject.id, { name: newDeviceName.trim() })
            let backendDevice = createdDeviceResponse.device || (createdDeviceResponse.id ? createdDeviceResponse : { id: Math.random(), name: newDeviceName.trim(), ...createdDeviceResponse })
            if (selectedSiteForDevice) {
              const deviceToAdd = { id: backendDevice.id, name: backendDevice.name || newDeviceName.trim(), uid: backendDevice.uid, sensors: backendDevice.sensors || [] }
              setModalProjectSites(prev => prev.map(s => s.id === selectedSiteForDevice ? { ...s, devices: [...(s.devices || []), deviceToAdd] } : s))
              setDeviceAssignments(prev => ({ ...prev, [backendDevice.id]: selectedSiteForDevice }))
              setSuccessMessage(`Device "${deviceToAdd.name}" added!`)
            } else {
              setUnassignedDevices(prev => [...prev, backendDevice])
              saveUnassignedDevicesToStorage(currentProject.id, [...unassignedDevices, backendDevice])
            }
            setNewDeviceName('')
            setTimeout(() => setSuccessMessage(''), 3000)
          } catch (error) {
            setDeviceError(error.response?.data?.message || error.response?.data?.detail || error.message || 'Error creating device')
          } finally {
            setIsCreatingDevice(false)
          }
        }

        const handleSaveChanges = async () => {
          setIsSavingDevices(true)
          setDeviceError('')
          setSuccessMessage('')
          try {
            const assignmentEntries = Object.entries(deviceAssignments)
            if (assignmentEntries.length > 0) {
              await Promise.all(assignmentEntries.map(([deviceId, siteId]) =>
                projectsApi.assignDeviceToSite(parseInt(deviceId), siteId ? parseInt(siteId) : null)
              ))
              const assignedIds = new Set(assignmentEntries.map(([id]) => id))
              const updatedUnassigned = unassignedDevices.filter(d => !assignedIds.has(String(d.id)))
              setUnassignedDevices(updatedUnassigned)
              saveUnassignedDevicesToStorage(currentProject.id, updatedUnassigned)
              setDeviceAssignments({})
              try {
                const refreshedSites = await projectsApi.fetchProjectSites(currentProject.id)
                setProjectSites(refreshedSites || [])
              } catch {}
              setSuccessMessage(`Saved ${assignmentEntries.length} change${assignmentEntries.length !== 1 ? 's' : ''}!`)
              setTimeout(() => { setShowAddDeviceModal(false); setSuccessMessage('') }, 1500)
            } else {
              setShowAddDeviceModal(false)
            }
          } catch (error) {
            setDeviceError('Error saving changes: ' + error.message)
          } finally {
            setIsSavingDevices(false)
          }
        }

        return (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 flex flex-col" style={{ maxHeight: '85vh' }}>
              {/* Header */}
              <div className="px-6 pt-5 pb-4 flex items-center justify-between flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900">Configure {configureSite?.name || 'Site'}</h2>
                <button
                  onClick={() => { setShowAddDeviceModal(false); setDeviceError(''); setSuccessMessage('') }}
                  className="text-gray-400 hover:text-gray-600 transition p-1 rounded hover:bg-gray-100"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 flex gap-0 border-b border-gray-200 flex-shrink-0">
                {['Devices', 'Sensors'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setConfigureModalTab(tab)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                      configureModalTab === tab
                        ? 'border-[#358ECD] text-[#358ECD]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Messages */}
              {successMessage && (
                <div className="mx-6 mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">{successMessage}</div>
              )}
              {deviceError && (
                <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{deviceError}</div>
              )}

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
                {configureModalTab === 'Devices' && (
                  <>
                    {/* Add New Device */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Add New Device</p>
                      <div className="flex gap-2">
                        <SecureInput
                          type="text"
                          value={newDeviceName}
                          onChange={(e) => { setNewDeviceName(e.target.value); setDeviceError('') }}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddDevice()}
                          placeholder="Claim Code"
                          disabled={isCreatingDevice}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent"
                        />
                        <button
                          onClick={handleAddDevice}
                          disabled={!newDeviceName.trim() || isCreatingDevice}
                          className="px-4 py-2 border border-[#358ECD] text-[#358ECD] text-xs font-bold rounded hover:bg-[#EBF5FB] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {isCreatingDevice && <Loader className="w-3.5 h-3.5 animate-spin" />}
                          ADD DEVICE
                        </button>
                      </div>
                    </div>

                    {/* Drag instruction */}
                    <div className="flex items-center gap-1.5 mb-4 text-sm text-gray-500">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Drag Device Names to Site
                    </div>

                    {/* Two columns: Unassigned + Site */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Unassigned Devices */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Unassigned Devices</p>
                        <div
                          className="border border-gray-200 rounded overflow-hidden"
                          style={{ minHeight: '180px', maxHeight: '260px', overflowY: 'auto' }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#EBF5FB' }}
                          onDragLeave={(e) => { e.currentTarget.style.background = '' }}
                          onDrop={(e) => {
                            e.preventDefault()
                            e.currentTarget.style.background = ''
                            if (!draggedDevice || dragSource === 'unassigned') return
                            const deviceToMove = { id: draggedDevice.id, name: draggedDevice.name || `Device ${draggedDevice.id}`, uid: draggedDevice.uid, sensors: draggedDevice.sensors || [] }
                            setModalProjectSites(prev => prev.map(site =>
                              site.devices?.some(d => d.id === draggedDevice.id)
                                ? { ...site, devices: site.devices.filter(d => d.id !== draggedDevice.id) }
                                : site
                            ))
                            setUnassignedDevices(prev => prev.some(d => d.id === deviceToMove.id) ? prev : [...prev, deviceToMove])
                            setDeviceAssignments(prev => ({ ...prev, [draggedDevice.id]: null }))
                            setDraggedDevice(null); setDragSource(null)
                          }}
                        >
                          {unassignedDevices.length === 0 ? (
                            <p className="px-3 py-4 text-xs text-gray-400 text-center">No unassigned devices</p>
                          ) : (
                            unassignedDevices.map(device => (
                              <div
                                key={device.id || `u-${Math.random()}`}
                                draggable
                                onDragStart={() => { setDraggedDevice(device); setDragSource('unassigned') }}
                                onDragEnd={() => { setDraggedDevice(null); setDragSource(null) }}
                                className={`px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 cursor-move select-none ${draggedDevice?.id === device.id ? 'opacity-40 bg-blue-50' : 'hover:bg-gray-50'}`}
                              >
                                <span className="font-medium">{device.name || device.uid || `Device #${device.id}`}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Site Devices */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Site: {configureSite?.name}</p>
                        <div
                          className="border border-gray-200 rounded overflow-hidden"
                          style={{ minHeight: '180px', maxHeight: '260px', overflowY: 'auto' }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#EBF5FB' }}
                          onDragLeave={(e) => { e.currentTarget.style.background = '' }}
                          onDrop={(e) => {
                            e.preventDefault()
                            e.currentTarget.style.background = ''
                            if (!draggedDevice || dragSource === selectedSiteForDevice || !selectedSiteForDevice) return
                            const deviceToMove = { id: draggedDevice.id, name: draggedDevice.name || `Device ${draggedDevice.id}`, uid: draggedDevice.uid, sensors: draggedDevice.sensors || [] }
                            if (dragSource === 'unassigned') {
                              setModalProjectSites(prev => prev.map(s => s.id === selectedSiteForDevice ? { ...s, devices: [...(s.devices || []), deviceToMove] } : s))
                              setUnassignedDevices(prev => prev.filter(d => d.id !== draggedDevice.id))
                            } else {
                              setModalProjectSites(prev => prev.map(site => {
                                if (site.id === dragSource) return { ...site, devices: site.devices.filter(d => d.id !== draggedDevice.id) }
                                if (site.id === selectedSiteForDevice) return { ...site, devices: [...(site.devices || []), deviceToMove] }
                                return site
                              }))
                            }
                            setDeviceAssignments(prev => ({ ...prev, [draggedDevice.id]: selectedSiteForDevice }))
                            setDraggedDevice(null); setDragSource(null)
                          }}
                        >
                          {siteDevices.length === 0 ? (
                            <p className="px-3 py-4 text-xs text-gray-400 text-center">No devices assigned</p>
                          ) : (
                            siteDevices.map(device => (
                              <div
                                key={device.id}
                                draggable
                                onDragStart={() => { setDraggedDevice(device); setDragSource(selectedSiteForDevice) }}
                                onDragEnd={() => { setDraggedDevice(null); setDragSource(null) }}
                                className={`px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 cursor-move select-none ${draggedDevice?.id === device.id ? 'opacity-40 bg-blue-50' : 'hover:bg-gray-50'}`}
                              >
                                {device.name}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {configureModalTab === 'Sensors' && (() => {
                  const siteDeviceList = configureSite?.devices || []
                  const activeSensorDevice = sensorSelectedDevice || siteDeviceList[0]?.name || ''

                  // ── Add Sensor Profile sub-form (XD design) ──
                  if (showSensorProfileForm) {
                    const selSet = new Set(sensorSelectedParams)
                    return (
                      <div className="space-y-4">
                        {/* Title */}
                        <div className="text-sm font-medium text-gray-800">
                          Add SDI-12 Sensor Profile to{' '}
                          <span className="text-[#358ECD] font-semibold">{activeSensorDevice || 'Device'}</span>
                        </div>

                        {/* Form grid */}
                        <div className="flex gap-4">
                          <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-3">
                            {/* Manufacturer */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Manufacturer</label>
                              <div className="relative">
                                <select value={sensorFormManufacturer} onChange={e => setSensorFormManufacturer(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['Sea-Bird','YSI','Solinst','Onset','Campbell','In-Situ','NexSens'].map(m => <option key={m}>{m}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Model */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Model</label>
                              <div className="relative">
                                <select value={sensorFormModel} onChange={e => setSensorFormModel(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['HydroCAT SDI12','SBE 37','SBE 39','EXO2','EXO1'].map(m => <option key={m}>{m}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* SDI-12 Address */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">SDI-12 Address</label>
                              <div className="relative">
                                <select value={sensorFormAddress} onChange={e => setSensorFormAddress(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['0','1','2','3','4','5','6','7'].map(a => <option key={a}>{a}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Meas Cmd */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Meas Cmd</label>
                              <div className="relative">
                                <select value={sensorFormMeasCmd} onChange={e => setSensorFormMeasCmd(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['M','MC','M1','M2','M3','C','CC'].map(c => <option key={c}>{c}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Port */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Port</label>
                              <div className="relative">
                                <select value={sensorFormPort} onChange={e => setSensorFormPort(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['P1','P2','P3','P4'].map(p => <option key={p}>{p}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Power */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Power</label>
                              <div className="relative">
                                <select value={sensorFormPower} onChange={e => setSensorFormPower(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['Switch','Always On','Off'].map(p => <option key={p}>{p}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Warmup */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Warmup (sec)</label>
                              <div className="relative">
                                <select value={sensorFormWarmup} onChange={e => setSensorFormWarmup(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['0','5','10','15','30','60','120'].map(w => <option key={w}>{w}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                          </div>
                          {/* Sensor image placeholder */}
                          <div className="w-28 flex-shrink-0 border border-gray-200 rounded flex items-center justify-center bg-gray-50">
                            <div className="text-center text-gray-300 p-2">
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mx-auto mb-1">
                                <rect x="2" y="2" width="20" height="20" rx="2" stroke="#ccc" strokeWidth="1.5"/>
                                <circle cx="12" cy="10" r="3" stroke="#ccc" strokeWidth="1.5"/>
                                <path d="M6 18c0-3.314 2.686-4 6-4s6 .686 6 4" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              <span className="text-xs">Sensor Image</span>
                            </div>
                          </div>
                        </div>

                        {/* Drag instruction */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M18 11V6.5a2.5 2.5 0 0 0-5 0V12m-5 0V4.5a2.5 2.5 0 0 1 5 0V12m0 0v-2m0 2a2.5 2.5 0 0 1 5 0v1.5" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M3 12.5a2.5 2.5 0 0 1 5 0V17a7 7 0 0 0 14 0v-2.5" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          Drag Parameters to Select, Deselect, and Reorder
                        </div>

                        {/* Available / Selected parameters */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Available */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-600">Available Parameters</span>
                              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                <input type="checkbox" checked={sensorFormShowAll} onChange={e => setSensorFormShowAll(e.target.checked)} className="w-3 h-3" />
                                Show All
                              </label>
                            </div>
                            <div
                              className="border border-gray-300 rounded overflow-hidden"
                              style={{ minHeight: '120px', maxHeight: '160px', overflowY: 'auto' }}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => {
                                e.preventDefault()
                                if (!draggedSensorParam || draggedSensorParamSource !== 'selected') return
                                setSensorSelectedParams(prev => prev.filter(p => p !== draggedSensorParam))
                                setSensorAvailableParams(prev => prev.includes(draggedSensorParam) ? prev : [...prev, draggedSensorParam])
                                setDraggedSensorParam(null); setDraggedSensorParamSource(null)
                              }}
                            >
                              {sensorAvailableParams.map(param => (
                                <div
                                  key={param}
                                  draggable
                                  onDragStart={() => { setDraggedSensorParam(param); setDraggedSensorParamSource('available') }}
                                  onDragEnd={() => { setDraggedSensorParam(null); setDraggedSensorParamSource(null) }}
                                  onClick={() => {
                                    if (selSet.has(param)) return
                                    setSensorSelectedParams(prev => [...prev, param])
                                    setSensorAvailableParams(prev => prev.filter(p => p !== param))
                                  }}
                                  className={`px-3 py-1.5 text-xs cursor-pointer border-b border-gray-100 last:border-b-0 select-none transition ${
                                    draggedSensorParam === param ? 'opacity-40' : 'hover:bg-[#EBF5FB]'
                                  } ${selSet.has(param) ? 'bg-[#358ECD] text-white' : 'text-gray-700'}`}
                                >
                                  {param}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Selected */}
                          <div>
                            <span className="text-xs font-semibold text-gray-600 block mb-1">Selected Parameters</span>
                            <div
                              className="border border-gray-300 rounded overflow-hidden"
                              style={{ minHeight: '120px', maxHeight: '160px', overflowY: 'auto' }}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => {
                                e.preventDefault()
                                if (!draggedSensorParam || draggedSensorParamSource !== 'available') return
                                setSensorAvailableParams(prev => prev.filter(p => p !== draggedSensorParam))
                                setSensorSelectedParams(prev => prev.includes(draggedSensorParam) ? prev : [...prev, draggedSensorParam])
                                setDraggedSensorParam(null); setDraggedSensorParamSource(null)
                              }}
                            >
                              {sensorSelectedParams.length === 0 ? (
                                <p className="px-3 py-4 text-xs text-gray-400 text-center">Drop parameters here</p>
                              ) : (
                                sensorSelectedParams.map(param => (
                                  <div
                                    key={param}
                                    draggable
                                    onDragStart={() => { setDraggedSensorParam(param); setDraggedSensorParamSource('selected') }}
                                    onDragEnd={() => { setDraggedSensorParam(null); setDraggedSensorParamSource(null) }}
                                    onClick={() => {
                                      setSensorSelectedParams(prev => prev.filter(p => p !== param))
                                      setSensorAvailableParams(prev => prev.includes(param) ? prev : [...prev, param])
                                    }}
                                    className={`px-3 py-1.5 text-xs cursor-pointer border-b border-gray-100 last:border-b-0 select-none text-gray-700 transition ${draggedSensorParam === param ? 'opacity-40' : 'hover:bg-red-50'}`}
                                  >
                                    {param}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Temperature unit */}
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                            <input type="radio" name="tempUnit" value="C" checked={sensorFormTempUnit === 'C'} onChange={() => setSensorFormTempUnit('C')} className="w-3.5 h-3.5 accent-[#358ECD]" />
                            C
                          </label>
                          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                            <input type="radio" name="tempUnit" value="F" checked={sensorFormTempUnit === 'F'} onChange={() => setSensorFormTempUnit('F')} className="w-3.5 h-3.5 accent-[#358ECD]" />
                            F
                          </label>
                        </div>

                        {/* Form footer buttons */}
                        <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
                          <button
                            onClick={() => setShowSensorProfileForm(false)}
                            className="px-5 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              const newProfile = {
                                id: Date.now(),
                                name: `${sensorFormManufacturer} ${sensorFormModel}`,
                                manufacturer: sensorFormManufacturer,
                                model: sensorFormModel,
                                address: sensorFormAddress,
                                measCmd: sensorFormMeasCmd,
                                port: sensorFormPort,
                                power: sensorFormPower,
                                warmup: sensorFormWarmup,
                                tempUnit: sensorFormTempUnit,
                                parameters: sensorSelectedParams,
                              }
                              setSensorProfiles(prev => [...prev, newProfile])
                              setSelectedSensorProfile(newProfile)
                              setSensorProfileName(newProfile.name)
                              setShowSensorProfileForm(false)
                            }}
                            className="px-5 py-1.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm font-semibold rounded transition"
                          >
                            Save Sensor Profile
                          </button>
                        </div>
                      </div>
                    )
                  }

                  // ── Default Sensors tab view ──
                  return (
                    <div className="space-y-5">
                      {/* Select Device */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Select Device</p>
                        <div className="relative">
                          <select
                            value={activeSensorDevice}
                            onChange={(e) => setSensorSelectedDevice(e.target.value)}
                            className="w-full appearance-none px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent pr-8"
                          >
                            {siteDeviceList.length === 0 ? (
                              <option value="">No devices assigned</option>
                            ) : (
                              siteDeviceList.map(d => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                              ))
                            )}
                          </select>
                          <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                      </div>

                      {/* Sensor Profile List row */}
                      <div className="border border-gray-200 rounded p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 whitespace-nowrap">Sensor Profile List</span>
                          <input
                            type="text"
                            value={sensorProfileName}
                            onChange={(e) => setSensorProfileName(e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent"
                          />
                          <button
                            onClick={() => {
                              const found = sensorProfiles.find(p => p.name === sensorProfileName)
                              if (found) setSelectedSensorProfile(found)
                            }}
                            className="px-4 py-1.5 border border-[#358ECD] text-[#358ECD] text-sm rounded hover:bg-[#EBF5FB] transition whitespace-nowrap"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => {
                              if (!sensorProfileName.trim()) return
                              const existing = sensorProfiles.find(p => p.name === sensorProfileName.trim())
                              if (!existing) {
                                const newProfile = { id: Date.now(), name: sensorProfileName.trim() }
                                setSensorProfiles(prev => [...prev, newProfile])
                                setSelectedSensorProfile(newProfile)
                              }
                            }}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition whitespace-nowrap"
                          >
                            Save
                          </button>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => {
                              setSensorAvailableParams(['Temperature', 'ODOSat', 'Sample No'])
                              setSensorSelectedParams(['Conductivity (S/m)', 'Pressure (dbar)', 'ODO (mg/L)', 'Salinity (PPT)', 'Sound Speed (m/s)', 'Sp Cond (S/m)'])
                              setSensorFormManufacturer('Sea-Bird')
                              setSensorFormModel('HydroCAT SDI12')
                              setSensorFormAddress('0')
                              setSensorFormMeasCmd('M')
                              setSensorFormPort('P1')
                              setSensorFormPower('Switch')
                              setSensorFormWarmup('30')
                              setSensorFormTempUnit('C')
                              setShowSensorProfileForm(true)
                            }}
                            className="px-4 py-1.5 border border-[#358ECD] text-[#358ECD] text-sm rounded hover:bg-[#EBF5FB] transition"
                          >
                            Add
                          </button>
                          <button
                            disabled={!selectedSensorProfile}
                            onClick={() => selectedSensorProfile && setShowSensorProfileForm(true)}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (!selectedSensorProfile) return
                              setSensorProfiles(prev => prev.filter(p => p.id !== selectedSensorProfile.id))
                              setSelectedSensorProfile(null)
                              setSensorProfileName('Default')
                            }}
                            disabled={!selectedSensorProfile}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                          <div className="flex-1" />
                          <button
                            disabled={!selectedSensorProfile || !activeSensorDevice}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Deploy
                          </button>
                        </div>
                      </div>

                      {/* Profile list */}
                      {sensorProfiles.length > 0 && (
                        <div className="border border-gray-200 rounded overflow-hidden" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                          {sensorProfiles.map(profile => (
                            <div
                              key={profile.id}
                              onClick={() => { setSensorProfileName(profile.name); setSelectedSensorProfile(profile) }}
                              className={`px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 cursor-pointer transition ${
                                selectedSensorProfile?.id === profile.id
                                  ? 'bg-[#EBF5FB] text-[#358ECD] font-medium'
                                  : 'hover:bg-gray-50 text-gray-700'
                              }`}
                            >
                              {profile.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 flex justify-end flex-shrink-0 border-t border-gray-100">
                <button
                  onClick={handleSaveChanges}
                  disabled={isSavingDevices}
                  className="px-6 py-2 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm font-semibold rounded transition disabled:opacity-60 flex items-center gap-2"
                >
                  {isSavingDevices && <Loader className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Create Site Modal */}
      {/* Delete Site Confirmation Modal */}
      {showDeleteSiteConfirm && siteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Delete Site</h2>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Delete "{siteToDelete.name}"?</p>
                  <p className="text-xs text-gray-500 mt-1">This will permanently remove the site and all its associated data. This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteSiteConfirm(false); setSiteToDelete(null) }}
                disabled={isDeletingSite}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSiteDelete}
                disabled={isDeletingSite}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition flex items-center gap-2"
              >
                {isDeletingSite && <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/><path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/></svg>}
                Delete Site
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move/Reorder Site Confirmation Modal */}
      {showMoveSiteConfirm && pendingSiteMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Move Site</h2>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Move "{pendingSiteMove.fromName}"?</p>
                  <p className="text-xs text-gray-500 mt-1">Reposition this site next to "{pendingSiteMove.toName}" in the sidebar.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowMoveSiteConfirm(false); setPendingSiteMove(null) }}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSiteMove}
                className="px-4 py-2 text-sm font-medium text-white bg-[#358ECD] rounded hover:bg-blue-600 transition"
              >
                Confirm Move
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Create Site</h2>
            </div>

            {/* Error/Success Messages */}
            {siteError && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                <p className="text-sm font-medium text-red-700">{siteError}</p>
              </div>
            )}
            {siteSuccess && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200">
                <p className="text-sm font-medium text-green-700">{siteSuccess}</p>
              </div>
            )}

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Site Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newSiteName}
                  onChange={(e) => { setNewSiteName(e.target.value); setSiteError('') }}
                  placeholder="Site Name"
                  disabled={isCreatingSite}
                  className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                    _siteNameFieldError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                    : _siteNameVal && !_siteNameInvalid ? 'border-green-400 focus:ring-green-400'
                    : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {_siteNameFieldError
                  ? <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                      {_siteNameFieldError}
                    </p>
                  : <p className="text-xs text-gray-400 mt-1">Must begin with a letter; may contain letters, digits, spaces, dashes, and underscores.</p>
                }
              </div>

              {/* Site Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={newSiteDescription}
                  onChange={(e) => setNewSiteDescription(e.target.value)}
                  placeholder="Site Description"
                  rows="3"
                  disabled={isCreatingSite}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              {/* Coordinates Section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Coordinates</h3>
                <div className="flex items-start gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="useDeviceGPS"
                    checked={useDeviceGPS}
                    onChange={(e) => setUseDeviceGPS(e.target.checked)}
                    disabled={isCreatingSite}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 cursor-pointer"
                  />
                  <div>
                    <label htmlFor="useDeviceGPS" className="text-sm text-gray-700 cursor-pointer font-medium">Use Device GPS</label>
                    <p className="text-xs text-gray-400 mt-0.5">If the logger has GPS, Latitude and Longitude will automatically update each time data arrives from the logger.</p>
                  </div>
                </div>
                {!useDeviceGPS && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Enter decimal values, e.g. <span className="font-mono text-gray-600">39.782875, -83.993666</span></p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                        <input
                          type="text"
                          value={newSiteLatitude}
                          onChange={(e) => { setNewSiteLatitude(e.target.value); setSiteError('') }}
                          placeholder="e.g. 39.782875"
                          disabled={isCreatingSite}
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            _siteLatError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                            : _siteLatVal && !_siteLatError ? 'border-green-400 focus:ring-green-400'
                            : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        {_siteLatError && <p className="text-xs text-red-500 mt-1">{_siteLatError}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                        <input
                          type="text"
                          value={newSiteLongitude}
                          onChange={(e) => { setNewSiteLongitude(e.target.value); setSiteError('') }}
                          placeholder="e.g. -83.993666"
                          disabled={isCreatingSite}
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            _siteLonError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                            : _siteLonVal && !_siteLonError ? 'border-green-400 focus:ring-green-400'
                            : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        {_siteLonError && <p className="text-xs text-red-500 mt-1">{_siteLonError}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 space-y-3">
              <button
                onClick={async () => {
                  if (!_siteNameVal) { setSiteError('Site name is required.'); return }
                  if (_siteNameInvalid) { setSiteError(_siteNameFieldError); return }
                  if (_siteLatError) { setSiteError(_siteLatError); return }
                  if (_siteLonError) { setSiteError(_siteLonError); return }
                  const _siteSecErr = scanInput(newSiteName) || scanInput(newSiteDescription)
                  if (_siteSecErr) { setSiteError(_siteSecErr); return }
                  setIsCreatingSite(true)
                  setSiteError('')
                  setSiteSuccess('')
                  try {
                    const siteData = { name: _siteNameVal, description: newSiteDescription.trim() }
                    if (_siteLatVal) siteData.latitude = parseFloat(_siteLatVal)
                    if (_siteLonVal) siteData.longitude = parseFloat(_siteLonVal)
                    const response = await projectsApi.createSite(currentProject.id, siteData)
                    const newSite = response.site || response
                    setProjectSites([...projectSites, newSite])
                    setSiteSuccess(`Site "${_siteNameVal}" created successfully!`)
                    setTimeout(() => {
                      setShowCreateSiteModal(false)
                      setNewSiteName('')
                      setNewSiteDescription('')
                      setNewSiteLatitude('')
                      setNewSiteLongitude('')
                      setUseDeviceGPS(false)
                      setSiteSuccess('')
                    }, 1500)
                  } catch (error) {
                    setSiteError(error.response?.data?.message || error.message || 'Error creating site')
                  } finally {
                    setIsCreatingSite(false)
                  }
                }}
                disabled={!_isSiteFormValid || isCreatingSite}
                className="w-full px-4 py-2 text-sm font-bold text-white bg-[#358ECD] hover:bg-blue-600 rounded transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreatingSite
                  ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/><path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/></svg>Creating...</>
                  : 'CREATE SITE'
                }
              </button>
              <button
                onClick={() => { setShowCreateSiteModal(false); setNewSiteName(''); setNewSiteDescription(''); setNewSiteLatitude(''); setNewSiteLongitude(''); setUseDeviceGPS(false); setSiteError(''); setSiteSuccess('') }}
                disabled={isCreatingSite}
                className="w-full px-4 py-2 text-sm font-medium text-[#358ECD] hover:text-blue-700 transition disabled:opacity-50"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Parameter Configuration Modal — ported from the legacy
          Dashboard "Parameter Settings" popup. Conditionally rendered so
          state resets every open. */}
      {showParamConfigModal && (
        <ParameterConfigModal
          open={true}
          onClose={() => setShowParamConfigModal(false)}
          sites={projectSites}
          initialSiteId={projectSites?.[0]?.id || null}
          initialDeviceId={projectSites?.[0]?.devices?.[0]?.id || null}
        />
      )}

      {/* Configure Dashboards Modal — conditional render so it remounts fresh
          each time (guarantees items state re-initialises from current templates) */}
      {showConfigureDashboardsModal && (
        <ConfigDashboardsModal
          open={true}
          projectId={projectId}
          templates={templates}
          onClose={() => setShowConfigureDashboardsModal(false)}
          onSaved={(reordered) => {
            // Update templates state → nav dropdown reflects new order immediately.
            const reorderedSig = `${projectId}:${reordered.map((t) => t.id).join(',')}`
            presOrderSigRef.current = reorderedSig  // prevent useEffect re-fetch loop
            const currentTemplateName = templates[currentMenuIndex]?.name
            setTemplates(reordered)
            const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
            if (newIdx >= 0 && newIdx !== currentMenuIndex) setCurrentMenuIndex(newIdx)
            setShowConfigureDashboardsModal(false)
          }}
          onAddDashboard={() => {
            setShowConfigureDashboardsModal(false)
            setShowAddDashboardModal(true)
            setShowDashboardDetailsModal(false)
            setSelectedTemplateForDetails(null)
            setDashboardName('')
            setDashboardDetails('')
            setSelectedTemplate(null)
            setDashboardError('')
            setDashboardSuccess('')
          }}
          onResetDashboard={async (template) => {
            if (!template?.id || !projectId) return
            if (!window.confirm(`Reset "${template.name}" to its default layout?`)) return
            try {
              await dashboardApi.resetUserLayout(template.id, { project_id: projectId })
              // Force the grid to reload if this is the active template
              if (templates[currentMenuIndex]?.id === template.id) {
                setCurrentMenuIndex(currentMenuIndex)
              }
            } catch (e) {
              console.error('[ConfigureDashboards] reset failed', e)
              window.alert('Failed to reset dashboard. Please try again.')
            }
          }}
          onArchiveChanged={async () => {
            // Refetch the project's templates list so the nav dropdown drops
            // archived dashboards (or picks up restored ones) immediately.
            if (!currentProject?.id) return
            try {
              const res = await dashboardApi.getProjectTemplates(currentProject.id)
              const list = Array.isArray(res.data?.templates || res.data)
                ? (res.data?.templates || res.data)
                : []
              if (list.length || true) {
                setTemplates(list)
                // If the active dashboard was archived, fall back to the first
                // remaining one so the user isn't left on a hidden template.
                const activeId = templates[currentMenuIndex]?.id
                if (activeId && !list.some(t => t.id === activeId)) {
                  setCurrentMenuIndex(0)
                }
              }
            } catch (e) {
              console.warn('[ConfigureDashboards] refresh templates failed', e)
            }
          }}
        />
      )}

      {/* Add Dashboard Modal - Template Selection */}
      {showAddDashboardModal && !showDashboardDetailsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddDashboardModal(false)
            setSelectedTemplate(null)
            setDashboardError('')
            setDashboardSuccess('')
            setAddDashboardSearch('')
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header: title + search + close */}
            <div className="px-6 pt-5 pb-4 flex items-center gap-4 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add Dashboard</h2>
              <div className="flex-1" />
              <div className="relative w-64">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={addDashboardSearch}
                  onChange={(e) => setAddDashboardSearch(e.target.value)}
                  placeholder="Search Dashboards"
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
                />
              </div>
              <button
                onClick={() => {
                  setShowAddDashboardModal(false)
                  setSelectedTemplate(null)
                  setDashboardError('')
                  setDashboardSuccess('')
                  setAddDashboardSearch('')
                }}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Modal Body - Template Selection */}
            <div className="overflow-y-auto flex-1 px-6 pb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Templates</h3>

              {loadingPredefinedTemplates ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-500 mb-3" />
                  <p className="text-sm text-gray-500">Loading templates...</p>
                </div>
              ) : (() => {
                const q = addDashboardSearch.trim().toLowerCase()
                // Only the canonical predefined templates appear here — not
                // the customer's own previously-added dashboards.
                const visibleTemplates = q
                  ? predefinedTemplates.filter(t => t.name.toLowerCase().includes(q))
                  : predefinedTemplates
                const showBlank = !q || 'blank'.includes(q)
                if (visibleTemplates.length === 0 && !showBlank) {
                  return (
                    <p className="py-8 text-center text-gray-400 text-sm">
                      No templates match your search.
                    </p>
                  )
                }
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {visibleTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => {
                          // XD flow: opening a template card moves to the
                          // "Provide name and details" step. Pre-fill the
                          // Details field with "Created from <Name> Template"
                          // matching the design screenshot.
                          setSelectedTemplateForDetails(template)
                          setDashboardName('')
                          setDashboardDetails(`Created from ${template.name} Template`)
                          setDashboardError('')
                          setDashboardSuccess('')
                          setShowDashboardDetailsModal(true)
                        }}
                        disabled={isCreatingDashboard}
                        className="p-0 border border-gray-200 rounded-lg transition overflow-hidden bg-white hover:border-[#358ECD] hover:shadow-md cursor-pointer flex flex-col text-left"
                      >
                        <div className="w-full h-32 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center overflow-hidden border-b border-gray-200">
                          {template.image ? (
                            <img
                              src={template.image}
                              alt={template.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.parentElement.innerHTML = '<div class="text-3xl">📊</div>'
                              }}
                            />
                          ) : (
                            <div className="text-3xl">📊</div>
                          )}
                        </div>
                        <div className="px-3 py-2">
                          <p className="text-sm text-gray-800">{template.name}</p>
                        </div>
                      </button>
                    ))}

                    {showBlank && (
                      <button
                        onClick={() => {
                          // Blank uses the same details step; backend treats
                          // template='blank' as a fresh empty dashboard.
                          setSelectedTemplateForDetails({ id: 'blank', name: 'Blank' })
                          setDashboardName('')
                          setDashboardDetails('')
                          setDashboardError('')
                          setDashboardSuccess('')
                          setShowDashboardDetailsModal(true)
                        }}
                        disabled={isCreatingDashboard}
                        className="p-0 border border-dashed border-gray-300 rounded-lg transition overflow-hidden bg-white hover:border-[#358ECD] hover:bg-blue-50 cursor-pointer flex flex-col text-left"
                      >
                        <div className="w-full h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border-b border-gray-200">
                          <div className="text-4xl">➕</div>
                        </div>
                        <div className="px-3 py-2">
                          <p className="text-sm text-gray-800">Blank</p>
                        </div>
                      </button>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Details Modal - After Template Selection */}
      {showDashboardDetailsModal && selectedTemplateForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Add Dashboard</h2>
            </div>

            {/* Error/Success Messages */}
            {dashboardError && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                <p className="text-sm font-medium text-red-700">{dashboardError}</p>
              </div>
            )}
            {dashboardSuccess && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200">
                <p className="text-sm font-medium text-green-700">{dashboardSuccess}</p>
              </div>
            )}

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Dashboard Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Dashboard Name</label>
                <SecureInput
                  type="text"
                  value={dashboardName}
                  onChange={(e) => {
                    setDashboardName(e.target.value)
                    setDashboardError('')
                  }}
                  placeholder="Enter dashboard name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isCreatingDashboard}
                />
              </div>

              {/* Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Details</label>
                <textarea
                  value={dashboardDetails}
                  onChange={(e) => {
                    setDashboardDetails(e.target.value)
                    setDashboardError('')
                  }}
                  placeholder={selectedTemplateForDetails?.id === 'blank'
                    ? 'Describe your dashboard…'
                    : `Created from ${selectedTemplateForDetails.name} Template`}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] resize-none"
                  disabled={isCreatingDashboard}
                />
              </div>
            </div>

            {/* Modal Footer: full-width primary button + Cancel link below (matches XD) */}
            <div className="px-6 pb-6 pt-2">
              <button
                onClick={async () => {
                  if (!dashboardName.trim()) {
                    setDashboardError('Please enter a dashboard name')
                    return
                  }
                  const _dbSecErr = scanInput(dashboardName) || scanInput(dashboardDetails)
                  if (_dbSecErr) { setDashboardError(_dbSecErr); return }

                  setIsCreatingDashboard(true)
                  setDashboardError('')

                  try {
                    const templateRef = selectedTemplateForDetails.id === 'blank'
                      ? 'blank'
                      : selectedTemplateForDetails.id
                    console.log('[Add Dashboard] Creating dashboard:', {
                      name: dashboardName.trim(),
                      template: templateRef,
                      details: dashboardDetails.trim() ||
                        (selectedTemplateForDetails.id === 'blank'
                          ? 'Custom dashboard'
                          : `Created from ${selectedTemplateForDetails.name} Template`),
                    })

                    const response = await projectsApi.createDashboard(currentProject.id, {
                      name: dashboardName.trim(),
                      template: templateRef,
                      description: dashboardDetails.trim() ||
                        (selectedTemplateForDetails.id === 'blank'
                          ? 'Custom dashboard'
                          : `Created from ${selectedTemplateForDetails.name} Template`),
                    })

                    console.log('[Add Dashboard] Dashboard created:', response)

                    // Refresh the project templates so the new dashboard appears
                    // in the nav dropdown immediately. The dashboard create
                    // endpoint returns { dashboard: { id, name, ... } }.
                    try {
                      const tplRes = await dashboardApi.getProjectTemplates(currentProject.id)
                      const list = Array.isArray(tplRes.data?.templates || tplRes.data)
                        ? (tplRes.data?.templates || tplRes.data)
                        : []
                      if (list.length) {
                        setTemplates(list)
                        const newId = response.dashboard?.id ?? response.id
                        const newIdx = list.findIndex(t => t.id === newId)
                        if (newIdx >= 0) setCurrentMenuIndex(newIdx)
                      }
                    } catch (e) {
                      console.warn('[Add Dashboard] refresh templates failed', e)
                    }

                    setDashboardSuccess(`Dashboard "${dashboardName.trim()}" created.`)

                    setTimeout(() => {
                      setShowAddDashboardModal(false)
                      setShowDashboardDetailsModal(false)
                      setSelectedTemplateForDetails(null)
                      setDashboardName('')
                      setDashboardDetails('')
                      setDashboardSuccess('')
                    }, 900)
                  } catch (error) {
                    console.error('[Add Dashboard] Error:', error)
                    const detail = error.response?.data?.error
                      || error.response?.data?.message
                      || error.message
                      || 'Error creating dashboard'
                    setDashboardError(detail)
                  } finally {
                    setIsCreatingDashboard(false)
                  }
                }}
                disabled={!dashboardName.trim() || isCreatingDashboard}
                className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#358ECD] hover:bg-[#2c7ab2] rounded transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreatingDashboard ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Add Dashboard to Project'
                )}
              </button>
              <button
                onClick={() => {
                  setShowDashboardDetailsModal(false)
                  setSelectedTemplateForDetails(null)
                  setDashboardName('')
                  setDashboardDetails('')
                  setDashboardError('')
                }}
                disabled={isCreatingDashboard}
                className="w-full mt-3 text-sm text-[#358ECD] hover:underline disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Widgets Modal */}
      {showAddWidgetsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={addWidgetModalHidden ? { opacity: 0, pointerEvents: 'none' } : {}}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl max-h-[90vh] overflow-auto w-full mx-4">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Add Widgets</h2>
              <input 
                type="text" 
                placeholder="Search Widgets" 
                value={searchWidgetsQuery}
                onChange={(e) => setSearchWidgetsQuery(e.target.value.toLowerCase())}
                className="px-3 py-1 border border-gray-300 rounded text-sm flex-1 ml-4 mr-4"
              />
              <button 
                onClick={() => setShowAddWidgetsModal(false)}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content - Widget Categories */}
            <div className="px-6 py-4">
              {/* Loading State */}
              {loadingWidgetCategories && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-gray-600">Loading widgets...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {widgetCategoriesError && !loadingWidgetCategories && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <p className="font-semibold">Error loading widgets</p>
                  <p className="text-sm mt-1">{widgetCategoriesError}</p>
                  <button
                    onClick={() => loadWidgetCategories()}
                    className="mt-3 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Widget Categories */}
              {!loadingWidgetCategories && !widgetCategoriesError && (
                <>
                  {dynamicWidgetCategories.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No widget categories available</p>
                    </div>
                  ) : (
                    dynamicWidgetCategories.map((categoryGroup) => {
                      const category = categoryGroup.category
                      const widgets = categoryGroup.widgets || []
                      
                      // Filter widgets by search query
                      const filteredWidgets = widgets.filter(widget =>
                        !searchWidgetsQuery ||
                        widget.name.toLowerCase().includes(searchWidgetsQuery) ||
                        widget.widget_type.toLowerCase().includes(searchWidgetsQuery) ||
                        (widget.description && widget.description.toLowerCase().includes(searchWidgetsQuery))
                      )

                      // Skip empty categories after filtering
                      if (filteredWidgets.length === 0 && searchWidgetsQuery) {
                        return null
                      }

                      return (
                        <div key={category.id || category.name} className="mb-6">
                          <h3 className="text-sm font-bold text-gray-700 mb-3" style={{ color: category.color || '#1F2937' }}>
                            {category.name} ({widgets.length})
                          </h3>
                          <div className="grid grid-cols-5 gap-3">
                            {filteredWidgets.map((widget) => (
                              <div
                                key={widget.id}
                                draggable
                                unselectable="on"
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'copy'
                                  e.dataTransfer.setData('text/plain', String(widget.id))
                                  setDraggingWidget(widget)
                                  // Hide (not unmount) the modal so the grid is exposed as drop target
                                  // while keeping drag source in DOM to prevent premature dragend
                                  setAddWidgetModalHidden(true)
                                }}
                                onDragEnd={() => {
                                  // If drop didn't fire (drag cancelled), clean up
                                  setDraggingWidget(null)
                                  setAddWidgetModalHidden(false)
                                  setShowAddWidgetsModal(false)
                                }}
                                onClick={() => {
                                  gridRef.current?.addWidget(widget)
                                  setShowAddWidgetsModal(false)
                                }}
                                className="flex flex-col items-center justify-center p-3 rounded border border-gray-300 hover:border-[#358ECD] hover:bg-blue-50 transition cursor-grab active:cursor-grabbing h-32 select-none"
                                title={`Click to add · Drag to place: ${widget.name}`}
                              >
                                {widget.preview_image ? (
                                  <img
                                    src={widget.preview_image}
                                    alt={widget.name}
                                    className="w-16 h-16 object-contain mb-1 rounded pointer-events-none"
                                  />
                                ) : (
                                  <div className="text-2xl mb-1 pointer-events-none">{widget.icon || '📦'}</div>
                                )}
                                <div className="text-xs text-center text-gray-700 font-medium line-clamp-2 pointer-events-none">{widget.name}</div>
                                <div className="text-[10px] text-gray-400 mt-0.5 pointer-events-none">drag or click</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset Dashboard Modal */}
      {showResetDashboardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Reset Dashboard to Default</h2>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6">
              <p className="text-gray-700 text-center mb-2">
                Are you sure you want to reset the dashboard to default settings?
              </p>
              <p className="text-red-600 font-semibold text-center">
                This action CANNOT be undone once completed.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-center">
              <button
                onClick={() => setShowResetDashboardModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded hover:bg-blue-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await gridRef.current?.reset()
                  setShowResetDashboardModal(false)
                  setIsCustomizingDashboard(false)
                }}
                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-red-600 rounded hover:bg-red-700 transition"
              >
                Reset to Default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Presentation Settings modal (Symfony parity). On save we refresh
          the local config + rotation so Play picks up the new values without
          a page reload. */}
      <PresentationSettingsModal
        open={showPresentationSettings}
        projectId={projectId}
        onClose={() => setShowPresentationSettings(false)}
        onSaved={(cfg) => {
          setPresentationConfig({
            pauseSeconds: Math.max(30, cfg.pause_seconds || 30),
            transition: cfg.transition || 'fade',
            randomOrder: !!cfg.random_order,
          })

          // Reorder templates to match the saved presentation order so the
          // nav dropdown immediately reflects the new order.
          const orderedDashboards = cfg.dashboards || []
          const reordered = []
          orderedDashboards.forEach((d) => {
            const t = templates.find((t) => t.name === d.name || t.id === d.id)
            if (t) reordered.push(t)
          })
          templates.forEach((t) => {
            if (!reordered.find((r) => r.id === t.id)) reordered.push(t)
          })

          const reorderedSig = `${projectId}:${reordered.map((t) => t.id).join(',')}`
          presOrderSigRef.current = reorderedSig // prevent useEffect from re-fetching

          // Build rotation using the NEW template order
          const included = orderedDashboards.filter((d) => d.included)
          const rotation = included
            .map((d) => {
              let idx = reordered.findIndex((t) => t.name === d.name)
              if (idx < 0) idx = reordered.findIndex((t) => t.id === d.id)
              return idx
            })
            .filter((idx) => idx >= 0)
          setPresentationRotation(rotation)

          // Apply the new template order, preserving the currently viewed tab
          const currentTemplateName = templates[currentMenuIndex]?.name
          setTemplates(reordered)
          const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
          if (newIdx >= 0 && newIdx !== currentMenuIndex) {
            setCurrentMenuIndex(newIdx)
          }
        }}
      />

    </div>
    </DashboardContext.Provider>
  )
}